import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import IOSToggle from '@/components/IOSToggle';
import FunctiesPanel from '@/components/FunctiesPanel';
import EmailTemplatesPanel from '@/components/EmailTemplatesPanel';
import GebruikersPage from '@/pages/GebruikersPage';
import KassaDisplayPanel from '@/components/KassaDisplayPanel';
import KassaTicketPanel from '@/components/KassaTicketPanel';
import KassaPrijsgroepenPanel from '@/components/KassaPrijsgroepenPanel';
import LeverbonPanel from '@/components/LeverbonPanel';
import ChauffeursPanel from '@/components/ChauffeursPanel';
import EenhedenPanel from '@/components/EenhedenPanel';
import VerkopersPanel from '@/components/VerkopersPanel';
import CommissieMatrix from '@/components/CommissieMatrix';
import PeppolLogViewer from '@/components/PeppolLogViewer';
import PrullenbakPanel from '@/components/PrullenbakPanel';
import KlantenViesPanel from '@/components/KlantenViesPanel';
import PackingConfigPanel from '@/components/PackingConfigPanel';
import PdfLayoutEditor from '@/components/PdfLayoutEditor';
import { EpcQrValidation } from '@/components/EpcQrValidation';
import NummeringPanel from '@/components/NummeringPanel';
import FactoringPanel from '@/components/FactoringPanel';
import PrintAgentPanel from '@/components/PrintAgentPanel';
import PrintNodePanel from '@/components/PrintNodePanel';
import BillitPanel from '@/components/BillitPanel';
import { FloppyDisk, ArrowCounterClockwise, Buildings, Hash, Plugs, ArrowsClockwise, Database, Trash, UsersThree, Article, FileText, Bank as BankIcon, CashRegister, Warning, Gear, Bell, Paperclip, Info, FolderOpen, Lightning, Handshake, DownloadSimple, Link as LinkIcon, CheckCircle, Globe, Envelope, ListChecks, Copy, PencilSimple, Plus, CaretDown, CaretRight, PaintBrush, Truck, Package, Users, Receipt, Calculator, ChatCircle, Tag, Storefront, Tray, Palette, TextAa } from '@phosphor-icons/react';
import { loadUiPrefs, saveUiPrefs, FONT_FAMILY_PRESETS, FONT_SIZE_PRESETS } from '@/lib/uiPrefs';

/* Helper: header-knop voor uitklapbare sectie (gebruikt in tab Bedrijf > Algemeen).
   Klikt op de header om open/dicht te schakelen. Velden in `right` slot bubblen niet door.
   ───────────────────────────────────────────────────────────────────────────────────── */
function CollapsibleHeader({ title, open, onToggle, right = null, testid = '' }) {
  return (
    <div
      className="section-header cursor-pointer hover:bg-gray-50 transition-colors select-none"
      onClick={onToggle}
      role="button"
      data-testid={testid || `collapsible-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="section-title flex items-center gap-2">
        {open ? <CaretDown size={12} weight="bold" className="text-gray-400" /> : <CaretRight size={12} weight="bold" className="text-gray-400" />}
        {title}
      </span>
      {right && <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-3">{right}</div>}
    </div>
  );
}

const DOC_TYPES = [
  { code: 'FAC', label: 'Factuur', color: '#002FA7' },
  { code: 'OFF', label: 'Offerte', color: '#7C3AED' },
  { code: 'BB', label: 'Bestelbon', color: '#2563EB' },
  { code: 'WB', label: 'Webshop-bestelling', color: '#7C3AED' },
  { code: 'LB', label: 'Leveringsbon', color: '#059669' },
  { code: 'PRO', label: 'Proforma', color: '#D97706' },
  { code: 'CN', label: 'Creditnota', color: '#DC2626' },
  { code: 'KL', label: 'Klant (lifetime)', color: '#0891B2' },
];

const MAAND_NAMEN = ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'];
const SEPARATORS = [
  { value: '-', label: 'Streepje  FAC-2026-0001' },
  { value: '/', label: 'Slash  FAC/2026/0001' },
  { value: '.', label: 'Punt  FAC.2026.0001' },
];
const PADDINGS = [
  { value: '3', label: '3 cijfers (001)' },
  { value: '4', label: '4 cijfers (0001)' },
  { value: '5', label: '5 cijfers (00001)' },
];

const TABS = [
  { key: 'bedrijf', label: 'Bedrijf', icon: Buildings },
  { key: 'documenten', label: 'Documenten', icon: FileText },
  { key: 'facturen', label: 'Facturen & CN', icon: CashRegister },
  { key: 'herinneringen', label: 'Herinneringen', icon: Bell },
  { key: 'nummering', label: 'Boekjaar & Nummering', icon: Hash },
  { key: 'boekingsregels', label: 'Boekingsregels', icon: ListChecks },
  { key: 'categorien', label: 'Categorieën → Boekhouding', icon: FolderOpen },
  { key: 'inbox', label: 'E-mail Inbox', icon: Envelope },
  { key: 'weergave', label: 'Weergave', icon: Palette },
  { key: 'integraties', label: 'Integraties', icon: Plugs },
  { key: 'databeheer', label: 'Databeheer', icon: Database },
];

const VERZEND_LANDEN = [
  { code: 'BE', naam: 'België' }, { code: 'NL', naam: 'Nederland' }, { code: 'DE', naam: 'Duitsland' },
  { code: 'FR', naam: 'Frankrijk' }, { code: 'LU', naam: 'Luxemburg' }, { code: 'ES', naam: 'Spanje' },
  { code: 'IT', naam: 'Italië' }, { code: 'PT', naam: 'Portugal' }, { code: 'AT', naam: 'Oostenrijk' },
  { code: 'PL', naam: 'Polen' }, { code: 'CZ', naam: 'Tsjechië' }, { code: 'DK', naam: 'Denemarken' },
  { code: 'SE', naam: 'Zweden' }, { code: 'IE', naam: 'Ierland' }, { code: 'CH', naam: 'Zwitserland' },
  { code: 'GB', naam: 'Verenigd Koninkrijk' }, { code: 'NO', naam: 'Noorwegen' }, { code: 'US', naam: 'Verenigde Staten' },
];

const BEDRIJF_SUBTABS = [
  { key: 'bedrijf', label: 'Bedrijf' },
  { key: 'branding', label: 'Branding (White-label)' },
  { key: 'bank_tab', label: 'Bank' },
  { key: 'bijlagen_tab', label: 'Bijlagen' },
  { key: 'informatie', label: 'Informatie' },
  { key: 'bedrijfsdocs', label: 'Bedrijfsdocumenten' },
  { key: 'einvoicing', label: 'E-Invoicing' },
];

// ── Nav-tree (Linear/Notion-stijl) ──
const SETTINGS_TREE = [
  {
    id: 'bedrijf', label: 'Bedrijf', icon: Buildings, color: '#002FA7',
    kids: [
      { id: 'bedrijf|bedrijf', label: 'Algemeen', tab: 'bedrijf', subTab: 'bedrijf', icon: Buildings },
      { id: 'bedrijf|branding', label: 'Branding (White-label)', tab: 'bedrijf', subTab: 'branding', icon: PaintBrush },
      { id: 'bedrijf|bank_tab', label: 'Bank', tab: 'bedrijf', subTab: 'bank_tab', icon: BankIcon },
      { id: 'bedrijf|bijlagen_tab', label: 'Bijlagen', tab: 'bedrijf', subTab: 'bijlagen_tab', icon: Paperclip },
      { id: 'bedrijf|informatie', label: 'Informatie', tab: 'bedrijf', subTab: 'informatie', icon: Info },
      { id: 'bedrijf|bedrijfsdocs', label: 'Bedrijfsdocumenten', tab: 'bedrijf', subTab: 'bedrijfsdocs', icon: FolderOpen },
      { id: 'bedrijf|einvoicing', label: 'E-Invoicing', tab: 'bedrijf', subTab: 'einvoicing', icon: Lightning },
    ],
  },
  {
    id: 'boekhouding', label: 'Boekhouding', icon: FileText, color: '#D97706',
    kids: [
      { id: 'documenten', label: 'Documenten', tab: 'documenten', icon: FileText },
      { id: 'pdf_layouts', label: 'PDF-layouts per type', tab: 'pdf_layouts', icon: Article },
      { id: 'leverbon', label: 'Leverbon & handtekening', tab: 'leverbon', icon: Truck },
      { id: 'verzendkosten', label: 'Verzendkosten', tab: 'verzendkosten', icon: Truck },
      { id: 'chauffeurs', label: 'Chauffeurs', tab: 'chauffeurs', icon: Truck },
      { id: 'verkopers', label: 'Verkopers', tab: 'verkopers', icon: UsersThree },
      { id: 'commissie-matrix', label: 'Commissie-matrix', tab: 'commissie-matrix', permKey: 'commissies_beheren', icon: Calculator },
      { id: 'eenheden', label: 'Eenheden', tab: 'eenheden', icon: ListChecks },
      { id: 'packing', label: 'Packing-configuratie', tab: 'packing', icon: Package },
      { id: 'klanten-vies', label: 'Klanten & VIES controle', tab: 'klanten-vies', icon: Users },
      { id: 'facturen', label: 'Facturen & Creditnota\'s', tab: 'facturen', icon: Receipt },
      { id: 'nummering', label: 'Boekjaar & Nummering', tab: 'nummering', icon: Hash },
      { id: 'boekingsregels', label: 'Boekingsregels', tab: 'boekingsregels', icon: ListChecks },
      { id: 'categorien', label: 'Categorieën → Grootboek', tab: 'categorien', icon: FolderOpen },
      { id: 'prullenbak', label: 'Prullenbak', tab: 'prullenbak', icon: Trash },
    ],
  },
  {
    id: 'communicatie', label: 'Communicatie', icon: Envelope, color: '#7C3AED',
    kids: [
      { id: 'herinneringen', label: 'Herinneringen', tab: 'herinneringen', icon: Bell },
      { id: 'email_templates', label: 'E-mail templates', tab: 'email_templates', icon: Envelope },
      { id: 'inbox', label: 'E-mail Inbox', tab: 'inbox', icon: Tray },
      { id: 'whatsapp', label: 'WhatsApp meldingen', tab: 'whatsapp', icon: ChatCircle },
    ],
  },
  {
    id: 'systeem', label: 'Systeem', icon: Gear, color: '#374151',
    kids: [
      { id: 'functies', label: 'Functies', tab: 'functies', icon: Lightning },
      { id: 'functie-details', label: 'Aanvullende functies', tab: 'functie-details', icon: ListChecks },
      { id: 'gebruikers', label: 'Gebruikers & Rechten', tab: 'gebruikers', adminOnly: true, icon: UsersThree },
      { id: 'weergave', label: 'Weergave (lettertype/grootte)', tab: 'weergave', icon: Palette },
      { id: 'integraties', label: 'Integraties', tab: 'integraties', icon: Plugs },
      { id: 'databeheer', label: 'Databeheer', tab: 'databeheer', icon: Database },
    ],
  },
  {
    id: 'kassa_grp', label: 'Kassa', icon: CashRegister, color: '#059669',
    kids: [
      { id: 'kassa_ticket', label: 'Kassaticket', tab: 'kassa_ticket', icon: Receipt },
      { id: 'kassa_display', label: 'Klant-display', tab: 'kassa_display', icon: Storefront },
      { id: 'kassa_prijsgroepen', label: 'Prijsgroepen', tab: 'kassa_prijsgroepen', icon: Tag },
    ],
  },
];

const SETTINGS_HINTS = {
  'bedrijf|bedrijf': 'Basisgegevens van je onderneming — verschijnen op facturen, offertes en in de BTW-aangifte.',
  'bedrijf|bank_tab': 'Bankrekeningen die gebruikt worden voor facturatie en automatische bankafletter.',
  'bedrijf|bijlagen_tab': 'Standaard-bijlagen (logo, algemene voorwaarden) die meegestuurd worden met documenten.',
  'bedrijf|informatie': 'Extra bedrijfsinformatie voor klantenportaal en e-mails.',
  'bedrijf|bedrijfsdocs': 'Interne documenten zoals statuten, UBO-register, verzekeringen.',
  'bedrijf|einvoicing': 'Peppol/UBL e-invoicing configuratie voor automatische B2B-uitwisseling.',
  'chauffeurs': 'Beheer chauffeurs voor leveringen. Bij het verzenden van een leverbon kan je een chauffeur kiezen — datum, uur en chauffeurnaam worden automatisch vastgelegd.',
  'verkopers': 'Beheer uw verkopers voor Verkoop onderweg. Ken elke verkoper een pincode en machtigingen toe — hij kan enkel de Verkoop-pagina openen met zijn eigen pincode, en u ziet op elke bestelbon wie hem gemaakt heeft.',
  'commissie-matrix': 'Commissie-matrix per verkoper × categorie. Stel in één overzichtelijk grid de commissie (% en/of €/st, op winst of omzet) in voor elke combinatie. Bulk-vul per verkoper om snel alle categorieën gelijk in te stellen.',
  'prullenbak': 'Verwijderde artikels, klanten en documenten blijven hier 30 dagen bewaarbaar. Klik op "Herstel" om een item terug te plaatsen, of "definitief verwijderen" voor een onherstelbare wissing. Na 30 dagen worden items automatisch permanent verwijderd.',
  'eenheden': 'Eenheden die beschikbaar zijn in de artikel-dropdown (bv. stuk, meter, kg, liter, dag, set, rol, ...). De standaard-eenheden zijn ingebouwd en niet wijzigbaar; custom eenheden kan je zelf toevoegen/bewerken/verwijderen.',
  'packing': 'Beheer bundel-/doos-regels en verpakkings-overhead per sector. Gebruikt bij automatische Packing List generatie vanuit facturen/leverbonnen.',
  'verzendkosten': 'Automatische verzendkosten per land: bij documenten en webshop-bestellingen onder het drempelbedrag wordt automatisch een lijn "Verzendkosten" toegevoegd. Boven de drempel is levering gratis.',
  'documenten': 'Standaardteksten, voorwaarden, betalingstermijn en PDF-layout per documenttype.',
  'pdf_layouts': 'Configureer per documenttype (factuur/offerte/bestelbon/leverbon/...) de accent-kleur, tabel-kolommen en optionele blokken voor de PDF.',
  'facturen': 'Facturatie-specifieke instellingen: BTW, kortingen, voorschotten en creditnota\'s.',
  'nummering': 'Boekjaar-start en nummeringsreeksen per documenttype.',
  'boekingsregels': 'Automatische labels en mappings voor inkomende facturen en banktransacties.',
  'categorien': 'Link tussen 98 voorgedefinieerde kostencategorieën, grootboekrekeningen, BTW% en aftrekbaarheid.',
  'herinneringen': 'Automatische herinneringen voor openstaande facturen (cron) en BTW-kwartaalmails.',
  'inbox': 'SendGrid Inbound Parse webhook voor automatisch verwerken van binnenkomende PDF-facturen.',
  'integraties': 'SendGrid, Stripe, Mollie, CodaBox, Ponto, WooCommerce en AI OCR.',
  'functies': 'Schakel nieuwe functies aan/uit en stel per functie in welke gebruikers-rol toegang heeft. Handig overzicht van wat deze app allemaal kan.',
  'gebruikers': 'Beheer alle login-gebruikers (admins, gebruikers, alleen-lezen). Onderaan vindt u de volledige machtigingen-matrix: wie mag wat op basis van rol × feature-configuratie.',
  'databeheer': 'Backup, import, export, bulk-wissen en archivering van financiële gegevens.',
  'kassa_display': 'Personaliseer het 2e scherm aan de kassa: welkomstekst, bedanktekst, kleuren en roterende reclamebanners in idle-modus.',
  'kassa_ticket': 'Stel de ticket-nummering (prefix, reset per dag, cijfers), de header-regel en de verkoopvoorwaarden onderaan de kassabon in.',
  'kassa_prijsgroepen': 'Beheer de prijsgroepen (Basic/Standaard/...) en kies welke de Kassa standaard gebruikt. Wijzigingen werken onmiddellijk door op het kassa-toestel — u hoeft daar niets aan te passen.',
  'leverbon': 'Leverbon-opties: digitale handtekening van de klant (tablet of smartphone-link), verplicht voor status &laquo;Geleverd&raquo;, prijzen tonen/verbergen op de PDF.',
};

// Integration logos as inline SVGs for clean rendering
const LogoSendGrid = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#1A82E2"/><path d="M12 12h8v8h-8zm8 8h8v8h-8zm-8 0h8v8h-8z" fill="#fff" opacity=".3"/><path d="M20 12h8v8h-8z" fill="#fff"/><path d="M12 20h8v8h-8z" fill="#fff"/></svg>
);
const LogoSMTP = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#059669"/><rect x="9" y="12" width="22" height="16" rx="2" fill="#fff"/><path d="M9 14l11 8 11-8" stroke="#059669" strokeWidth="2" fill="none" strokeLinejoin="round"/></svg>
);
const LogoStripe = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#635BFF"/><path d="M18.5 16.5c0-.9.75-1.25 2-1.25 1.8 0 4 .55 5.8 1.5v-5.5c-1.95-.77-3.87-1.08-5.8-1.08-4.75 0-7.9 2.48-7.9 6.63 0 6.47 8.9 5.43 8.9 8.22 0 1.07-.93 1.42-2.23 1.42-1.93 0-4.4-.8-6.35-1.87v5.57c2.16.93 4.35 1.33 6.35 1.33 4.87 0 8.2-2.4 8.2-6.62-.02-6.98-8.97-5.73-8.97-8.35z" fill="#fff"/></svg>
);
const LogoCodaBox = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#00A651"/><rect x="10" y="12" width="20" height="16" rx="2" fill="#fff"/><path d="M14 18h12M14 22h8" stroke="#00A651" strokeWidth="1.5" strokeLinecap="round"/></svg>
);
const LogoMollie = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#0A0B09"/><circle cx="20" cy="20" r="8" fill="#fff"/><circle cx="20" cy="20" r="4" fill="#0A0B09"/></svg>
);
const LogoWoo = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#7B2D8E"/><path d="M10 14c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H22l-4 4v-4h-6c-1.1 0-2-.9-2-2V14z" fill="#fff"/><circle cx="16" cy="19" r="1.5" fill="#7B2D8E"/><circle cx="20" cy="19" r="1.5" fill="#7B2D8E"/><circle cx="24" cy="19" r="1.5" fill="#7B2D8E"/></svg>
);
const LogoPonto = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#1E3A5F"/><path d="M12 26l4-12 4 8 4-5 4 9" stroke="#4FD1C5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
);
const LogoTwilio = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#F22F46"/><circle cx="15" cy="15" r="3" fill="#fff"/><circle cx="25" cy="15" r="3" fill="#fff"/><circle cx="15" cy="25" r="3" fill="#fff"/><circle cx="25" cy="25" r="3" fill="#fff"/></svg>
);
const LogoMessageBird = () => (
  <svg viewBox="0 0 40 40" width="36" height="36" fill="none"><rect width="40" height="40" rx="8" fill="#2481D7"/><path d="M28 14c0 5-3 10-8 10-2.5 0-4-1-5-2v6h-3V14h3v2c1-1.5 3-3 5.5-3 4.5 0 7.5 2 7.5 5z" fill="#fff"/></svg>
);

// Module-scoped IntegrationCard — voorkomt dat React de component bij elke parent re-render
// opnieuw aanmaakt (wat focus-loss in inputs veroorzaakt).
const IntegrationCard = ({ Logo, title, status, children }) => (
  <div className="section-card">
    <div className="section-header">
      <span className="flex items-center gap-3">
        <Logo />
        <span className="section-title">{title}</span>
      </span>
      {status ? (
        <span className="text-[10px] font-bold px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">Actief</span>
      ) : (
        <span className="text-[10px] font-bold px-2 py-0.5 bg-gray-50 text-gray-400 border border-gray-200">Niet geconfigureerd</span>
      )}
    </div>
    <div className="p-5 space-y-3">{children}</div>
  </div>
);

export default function InstellingenPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'bedrijf');
  const [subTab, setSubTab] = useState('bedrijf');
  const isBedrijfGroup = tab === 'bedrijf';
  // Tabs die hun EIGEN Opslaan-knop hebben — de globale "Opslaan" boven rechts is daar
  // misleidend (slaat enkel /instellingen-form op, niet de panel-data). Voorkomt dat de
  // gebruiker bv. op de Commissie-matrix-pagina de bovenste knop klikt en denkt dat
  // zijn cellen bewaard zijn (zoals gerapporteerd: "boven rechts gaat niet, onder rechts wel").
  const TABS_MET_EIGEN_OPSLAAN = new Set([
    'functie-details',
    'verkopers',           // VerkopersPanel — eigen "Verkoper aanmaken/Wijzigingen opslaan"
    'commissie-matrix',    // CommissieMatrix — eigen "Opslaan (X cellen)"
    'chauffeurs',          // eigen CRUD per regel
    'email_templates',     // eigen save per template
    'gebruikers',          // eigen form/save per gebruiker
    'whatsapp',            // eigen save flow
    'integraties',         // eigen save per integratie
    'databeheer',          // actie-pagina, geen form-save
    'prullenbak',          // alleen lijst, geen save
    'inbox',               // alleen viewer
    'kassa_prijsgroepen',  // eigen CRUD
  ]);
  const [form, setForm] = useState({
    naam: '', btw: '', email: '', tel: '', adres: '', iban: '', bic: '', iban_2: '', bic_2: '', iban_2_label: '',
    // Uitgebreide bedrijfsgegevens
    ondernemingsnummer: '', btw_plichtig: true, kleine_onderneming: false,
    maatschappelijke_naam: '', commerciele_naam: '',
    domiciliering_id: '', munteenheid: 'EUR', standaard_btw: '21', grootboekrekening: '',
    // Bestuurder / contact
    aanspreking: '', voornaam_bestuurder: '', achternaam_bestuurder: '',
    rpr: '', website: '', mobiel: '', fax: '', bedrijf_taal: 'nl',
    public_app_url: '', public_backend_url: '',
    // Facturatieadres
    fac_tav: '', fac_naam: '', fac_land: 'België', fac_straat: '', fac_nr: '', fac_bus: '',
    fac_postcode: '', fac_gemeente: '', fac_tel: '',
    // Afleveradres
    lev_zelfde: true, lev_tav: '', lev_naam: '', lev_land: 'België', lev_straat: '', lev_nr: '', lev_bus: '',
    lev_postcode: '', lev_gemeente: '', lev_tel: '',
    // Bedrijfsinstellingen
    logo_url: '', logo_max_height: 60, logo_max_width: 220, pdf_meta_gap: 14, pdf_title_size: 20, pdf_toon_artikel_fotos: false, voorwaarden_url: '', voorwaarden_naam: '', voorwaarden_actief: true, commentaar: '',
    boekjaar_start: '01-01', nummering: {}, num_format: { separator: '-', padding: '4' },
    sendgrid_key: '', sender_email: '', stripe_key: '',
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', smtp_from: '', smtp_from_name: '', smtp_tls: true, smtp_ssl: false,
    whatsapp_actief: false, whatsapp_provider: 'callmebot', whatsapp_callmebot_apikey: '', whatsapp_twilio_sid: '', whatsapp_twilio_token: '', whatsapp_twilio_sender: '', whatsapp_recipients: '',
    whatsapp_brons: ['verkoop_onderweg'], whatsapp_types: ['bestelbon', 'factuur'],
    telegram_actief: false, telegram_bot_token: '', telegram_chat_ids: '',
    // Varianten-module (kleur/maat/breedte) — opt-in
    varianten_module_actief: false,
    codabox_key: '', mollie_key: '', woocommerce_url: '', woocommerce_key: '', woocommerce_secret: '', ponto_key: '',
    // White-label / Branding
    app_naam: '', primary_kleur: '', accent_kleur: '', login_achtergrond: '',
    pdf_gebruik_logo_kleur: true,
    // IBAN Factoring (cessie van factuur)
    factoring_actief: false, factoring_email: '',
    factoring_ondertek_voornaam: '', factoring_ondertek_naam: '', factoring_ondertek_functie: '',
    factoring_tekst_nl: '', factoring_tekst_fr: '', factoring_tekst_en: '', factoring_tekst_de: '',
    factoring_introbrief_nl: '', factoring_introbrief_fr: '', factoring_introbrief_en: '', factoring_introbrief_de: '',
  });
  const [numStatus, setNumStatus] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetVals, setResetVals] = useState({});
  const [saved, setSaved] = useState(false);
  const [voorwHealth, setVoorwHealth] = useState(null);

  const load = () => {
    API.get('/instellingen').then(r => {
      const d = r.data;
      setForm({
        naam: d.naam || '', btw: d.btw || '', email: d.email || '', tel: d.tel || '',
        adres: d.adres || '', iban: d.iban || '', bic: d.bic || '', iban_2: d.iban_2 || '', bic_2: d.bic_2 || '', iban_2_label: d.iban_2_label || '',
        // Uitgebreid
        ondernemingsnummer: d.ondernemingsnummer || '', btw_plichtig: d.btw_plichtig ?? true, kleine_onderneming: d.kleine_onderneming ?? false,
        maatschappelijke_naam: d.maatschappelijke_naam || '', commerciele_naam: d.commerciele_naam || '',
        domiciliering_id: d.domiciliering_id || '', munteenheid: d.munteenheid || 'EUR', standaard_btw: d.standaard_btw || '21', grootboekrekening: d.grootboekrekening || '',
        aanspreking: d.aanspreking || '', voornaam_bestuurder: d.voornaam_bestuurder || '', achternaam_bestuurder: d.achternaam_bestuurder || '',
        rpr: d.rpr || '', website: d.website || '', mobiel: d.mobiel || '', fax: d.fax || '', bedrijf_taal: d.bedrijf_taal || 'nl',
        public_app_url: d.public_app_url || '', public_backend_url: d.public_backend_url || '',
        fac_tav: d.fac_tav || '', fac_naam: d.fac_naam || '', fac_land: d.fac_land || 'België', fac_straat: d.fac_straat || '', fac_nr: d.fac_nr || '', fac_bus: d.fac_bus || '',
        fac_postcode: d.fac_postcode || '', fac_gemeente: d.fac_gemeente || '', fac_tel: d.fac_tel || '',
        lev_zelfde: d.lev_zelfde ?? true, lev_tav: d.lev_tav || '', lev_naam: d.lev_naam || '', lev_land: d.lev_land || 'België', lev_straat: d.lev_straat || '', lev_nr: d.lev_nr || '', lev_bus: d.lev_bus || '',
        lev_postcode: d.lev_postcode || '', lev_gemeente: d.lev_gemeente || '', lev_tel: d.lev_tel || '',
        logo_url: d.logo_url || '', logo_max_height: d.logo_max_height || 60, logo_max_width: d.logo_max_width || 220, pdf_meta_gap: d.pdf_meta_gap ?? 14, pdf_title_size: d.pdf_title_size ?? 20, pdf_toon_artikel_fotos: !!d.pdf_toon_artikel_fotos, pdf_fotos_types: Array.isArray(d.pdf_fotos_types) ? d.pdf_fotos_types : ['factuur', 'creditnota', 'offerte', 'bestelbon', 'leverbon', 'proforma'], voorwaarden_url: d.voorwaarden_url || '', voorwaarden_naam: d.voorwaarden_naam || '', voorwaarden_url_nl: d.voorwaarden_url_nl || '', voorwaarden_naam_nl: d.voorwaarden_naam_nl || '', voorwaarden_url_fr: d.voorwaarden_url_fr || '', voorwaarden_naam_fr: d.voorwaarden_naam_fr || '', voorwaarden_url_en: d.voorwaarden_url_en || '', voorwaarden_naam_en: d.voorwaarden_naam_en || '', voorwaarden_url_de: d.voorwaarden_url_de || '', voorwaarden_naam_de: d.voorwaarden_naam_de || '', voorwaarden_actief: d.voorwaarden_actief !== false, voorwaarden_per_type: d.voorwaarden_per_type || { factuur: true, creditnota: true, offerte: true, bestelbon: true, leverbon: true, proforma: true }, commentaar: d.commentaar || '',
        boekjaar_start: d.boekjaar_start || '01-01',
        webshop_actief: !!d.webshop_actief,
        nummering: d.nummering || {},
        num_format: d.num_format || { separator: '-', padding: '4' },
        sendgrid_key: d.sendgrid_key || '', sender_email: d.sender_email || '',
        smtp_host: d.smtp_host || '', smtp_port: d.smtp_port || 587, smtp_user: d.smtp_user || '', smtp_password: d.smtp_password || '', smtp_from: d.smtp_from || '', smtp_from_name: d.smtp_from_name || '', smtp_tls: d.smtp_tls !== false, smtp_ssl: !!d.smtp_ssl,
        whatsapp_actief: !!d.whatsapp_actief, whatsapp_provider: d.whatsapp_provider || 'callmebot',
        whatsapp_callmebot_apikey: d.whatsapp_callmebot_apikey || '', whatsapp_twilio_sid: d.whatsapp_twilio_sid || '',
        whatsapp_twilio_token: d.whatsapp_twilio_token || '', whatsapp_twilio_sender: d.whatsapp_twilio_sender || '',
        whatsapp_recipients: Array.isArray(d.whatsapp_recipients) ? d.whatsapp_recipients.join(', ') : (d.whatsapp_recipients || ''),
        whatsapp_brons: d.whatsapp_brons || ['verkoop_onderweg'],
        whatsapp_types: d.whatsapp_types || ['bestelbon', 'factuur'],
        telegram_actief: !!d.telegram_actief,
        telegram_bot_token: d.telegram_bot_token || '',
        telegram_chat_ids: Array.isArray(d.telegram_chat_ids) ? d.telegram_chat_ids.join(', ') : (d.telegram_chat_ids || ''),
        varianten_module_actief: !!d.varianten_module_actief,
        app_naam: d.app_naam || '', primary_kleur: d.primary_kleur || '', accent_kleur: d.accent_kleur || '', login_achtergrond: d.login_achtergrond || '',
        pdf_gebruik_logo_kleur: d.pdf_gebruik_logo_kleur !== false,
        stripe_key: d.stripe_key || '',
        codabox_key: d.codabox_key || '', mollie_key: d.mollie_key || '',
        woocommerce_url: d.woocommerce_url || '', woocommerce_key: d.woocommerce_key || '', woocommerce_secret: d.woocommerce_secret || '',
        ponto_key: d.ponto_key || '',
        // SMS-integraties
        twilio_sid: d.twilio_sid || '', twilio_token: d.twilio_token || '', twilio_from: d.twilio_from || '', twilio_actief: !!d.twilio_actief,
        twilio_whatsapp_from: d.twilio_whatsapp_from || '', twilio_whatsapp_actief: !!d.twilio_whatsapp_actief,
        whatsapp_voorkeur: !!d.whatsapp_voorkeur,
        msgbird_key: d.msgbird_key || '', msgbird_from: d.msgbird_from || '', msgbird_actief: !!d.msgbird_actief,
        sms_vervallen_factuur: !!d.sms_vervallen_factuur, sms_herinnering: !!d.sms_herinnering,
        sms_aanmaning: !!d.sms_aanmaning, sms_betaling_ontvangen: !!d.sms_betaling_ontvangen,
        sms_budget_max: d.sms_budget_max ?? '',
        // Documenten
        aankopen_actief: d.aankopen_actief ?? true, po_nummer: d.po_nummer ?? true,
        taal_per_doc: d.taal_per_doc ?? true, doc_notities: d.doc_notities ?? true,
        titels_op_doc: d.titels_op_doc ?? true, doc_historiek: d.doc_historiek ?? true,
        korting_display: d.korting_display || 'indien_gebruikt',
        prijzen_modus: d.prijzen_modus || 'excl',
        decimalen: d.decimalen || '2',
        nota_per_taal: d.nota_per_taal ?? true,
        factuur_nota_nl: d.factuur_nota_nl || 'Gelieve het totaalbedrag voor {vervaldag} over te schrijven met gestructureerde mededeling {ogm}.',
        factuur_nota_fr: d.factuur_nota_fr || 'Veuillez transférer le montant total avant le {vervaldag} avec une communication structurée {ogm}.',
        factuur_nota_en: d.factuur_nota_en || 'Please transfer the total amount before {vervaldag} with structured communication {ogm}.',
        proforma_nota_nl: d.proforma_nota_nl || '', proforma_nota_fr: d.proforma_nota_fr || '', proforma_nota_en: d.proforma_nota_en || '',
        cn_nota_nl: d.cn_nota_nl || 'U mag het bedrag van deze creditnota in mindering brengen van de originele factuur.',
        cn_nota_fr: d.cn_nota_fr || 'Vous pouvez déduire le montant de cette note de crédit de la facture originale.',
        cn_nota_en: d.cn_nota_en || 'You may deduct the amount of this credit note from the original invoice.',
        // Voettekst per documenttype (aanpasbaar)
        factuur_voettekst: d.factuur_voettekst || 'Gelieve het totaalbedrag van {totaal} over te schrijven vóór {vervaldag} met gestructureerde mededeling {ogm}.',
        offerte_voettekst: d.offerte_voettekst || 'Deze offerte is 30 dagen geldig vanaf de uitgiftedatum. Voor akkoord gelieve ondertekend terug te sturen.',
        cn_voettekst: d.cn_voettekst || 'Dit bedrag van {totaal} zal binnen de 14 dagen teruggestort worden op uw rekening.',
        bestelbon_voettekst: d.bestelbon_voettekst || '',
        leverbon_voettekst: d.leverbon_voettekst || '',
        // Facturen & CN
        betalingstermijn_std: d.betalingstermijn_std || '14',
        termijn_einde_maand: d.termijn_einde_maand ?? true,
        betalingskorting_dagen: d.betalingskorting_dagen || '',
        betalingskorting_pct: d.betalingskorting_pct || '',
        epc_qr_actief: !!d.epc_qr_actief,
        epc_qr_per_type: d.epc_qr_per_type || { factuur: true, creditnota: true, proforma: true, offerte: true },
        ogm_actief: d.ogm_actief ?? true,
        concept_facturen: d.concept_facturen ?? true,
        proforma_facturen: d.proforma_facturen ?? true,
        leverdatum: d.leverdatum ?? true,
        duplicaat_verloren: d.duplicaat_verloren ?? true,
        betalingen_afdrukken: d.betalingen_afdrukken || 'altijd',
        voldaan_vermelden: d.voldaan_vermelden || 'altijd',
        // Herinneringen
        herinnering_1_dagen: d.herinnering_1_dagen || '1',
        herinnering_2_dagen: d.herinnering_2_dagen || '7',
        aanmaning_dagen: d.aanmaning_dagen || '7',
        auto_herinnering_email: d.auto_herinnering_email ?? true,
        // Automatische Betalingsherinnering (PDF, samengevoegd per klant)
        bh_auto_actief: d.bh_auto_actief ?? false,
        bh_auto_dagen_1: d.bh_auto_dagen_1 || 14,
        bh_auto_dagen_2: d.bh_auto_dagen_2 || 21,
        bh_auto_dagen_3: d.bh_auto_dagen_3 || 30,
        auto_btw_reminder: d.auto_btw_reminder ?? false,
        btw_reminder_email: d.btw_reminder_email || '',
        cron_schemas_auto: d.cron_schemas_auto ?? false,
        daily_summary_email: d.daily_summary_email ?? false,
        daily_summary_to: d.daily_summary_to || '',
        weekly_report_email: d.weekly_report_email ?? false,
        weekly_report_to: d.weekly_report_to || '',
        auto_goedkeuren_enabled: d.auto_goedkeuren_enabled ?? true,
        auto_goedkeuren_drempel: d.auto_goedkeuren_drempel || 5,
        interest_actief: d.interest_actief ?? false,
        interest_pct: d.interest_pct || '5',
        interest_berekening: d.interest_berekening || 'maandelijks',
        interest_min: d.interest_min || '100',
        aanmaningskosten_actief: d.aanmaningskosten_actief ?? false,
        aanmaningskost_bedrag: d.aanmaningskost_bedrag || '50',
        aanmaningskosten_vanaf: d.aanmaningskosten_vanaf || 'aanmaning',
        herinnering_1_tekst: d.herinnering_1_tekst || 'Geachte klant,\n\nVolgens onze administratie hebben wij nog geen betaling van factuurnummer {document_nr} mogen ontvangen. Wij verzoeken u het openstaande factuurbedrag van {outstanding} binnen 5 werkdagen over te maken op onze bankrekening met gestructureerde mededeling {ogm}.\n\nHoogachtend,\n{bedrijfsnaam}',
        herinnering_2_tekst: d.herinnering_2_tekst || 'Geachte klant,\n\nWij attenderen u er nogmaals op dat de betalingstermijn van factuur {document_nr} verstreken is. Wij hebben u reeds een herinnering gestuurd, maar het verschuldigde bedrag is nog niet voldaan. Wij verzoeken u dringend het openstaande factuurbedrag van {outstanding} zo spoedig mogelijk over te maken.\n\nHoogachtend,\n{bedrijfsnaam}',
        aanmaning_tekst: d.aanmaning_tekst || 'Geachte klant,\n\nOndanks twee eerdere herinneringen hebben wij nog geen betaling van de factuur met nummer {document_nr} mogen ontvangen. Wij verzoeken u dringend het openstaande factuurbedrag van {outstanding} binnen 7 dagen over te maken. Als deze laatste aanmaning u niet aanzet tot betaling, zijn wij genoodzaakt de vordering definitief uit handen te geven.\n\nHoogachtend,\n{bedrijfsnaam}',
        // IBAN Factoring
        factoring_actief: !!d.factoring_actief,
        factoring_email: d.factoring_email || '',
        factoring_ondertek_voornaam: d.factoring_ondertek_voornaam || '',
        factoring_ondertek_naam: d.factoring_ondertek_naam || '',
        factoring_ondertek_functie: d.factoring_ondertek_functie || '',
        factoring_tekst_nl: d.factoring_tekst_nl || '',
        factoring_tekst_fr: d.factoring_tekst_fr || '',
        factoring_tekst_en: d.factoring_tekst_en || '',
        factoring_tekst_de: d.factoring_tekst_de || '',
        factoring_introbrief_nl: d.factoring_introbrief_nl || '',
        factoring_introbrief_fr: d.factoring_introbrief_fr || '',
        factoring_introbrief_en: d.factoring_introbrief_en || '',
        factoring_introbrief_de: d.factoring_introbrief_de || '',
        verzendkosten_actief: !!d.verzendkosten_actief,
        verzendkosten_regels: Array.isArray(d.verzendkosten_regels) ? d.verzendkosten_regels : [],
      });
    }).catch(() => {});
    API.get('/nummering').then(r => setNumStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Health-check voor voorwaarden PDFs — draait bij page-load en signaleert ontbrekende bestanden
  useEffect(() => {
    API.get('/instellingen/voorwaarden/health')
      .then(r => setVoorwHealth(r.data))
      .catch(() => setVoorwHealth(null));
  }, []);

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const uBool = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.checked }));
  const uFmt = (k) => (e) => setForm(p => ({ ...p, num_format: { ...p.num_format, [k]: e.target.value } }));
  const [syncMsg, setSyncMsg] = useState('');
  const [smsStats, setSmsStats] = useState(null);

  const loadSmsStats = () => API.get('/sms/stats').then(r => setSmsStats(r.data)).catch(() => {});
  useEffect(() => { if (tab === 'integraties') loadSmsStats(); }, [tab]);

  const doSync = async (endpoint, label, options = {}) => {
    let body = {};
    // Voor SMS/WhatsApp-test: vraag expliciet een ander nummer dan de Twilio-afzender
    if (options.askTestNumber) {
      const suggest = options.suggest || '';
      const to = window.prompt(
        `${label}\n\nNaar welk nummer sturen?\n(mag NIET hetzelfde zijn als je Twilio-afzender — Twilio blokkeert self-send)\n\nFormaat: +32XXXXXXXXX`,
        suggest
      );
      if (!to || !to.trim()) { setSyncMsg(''); return; }
      body.to = to.trim();
    }
    setSyncMsg(`${label} bezig...`);
    try {
      const r = await API.post(endpoint, body);
      const d = r.data;
      const sim = d.simulated ? ' (simulatie)' : '';
      setSyncMsg(`${label}: ${d.message || `${d.imported || d.synced_orders || 0} items${sim}`}`);
    } catch (e) {
      setSyncMsg(`${label} mislukt: ${e.response?.data?.detail || 'Fout'}`);
    }
    setTimeout(() => setSyncMsg(''), 10000);
  };

  // Databeheer state
  const [dbStats, setDbStats] = useState(null);
  const [dbPw, setDbPw] = useState('');
  const [dbVerified, setDbVerified] = useState(false);
  const [dbMsg, setDbMsg] = useState('');
  const [dbLoading, setDbLoading] = useState('');

  const loadStats = () => API.get('/databeheer/stats').then(r => setDbStats(r.data)).catch(() => {});
  const verifyPw = async () => {
    try {
      await API.post('/databeheer/verify', { password: dbPw });
      setDbVerified(true); setDbMsg('');
    } catch { setDbMsg('Onjuist wachtwoord'); }
  };
  const doActie = async (actie, label) => {
    if (!window.confirm(`Weet je zeker dat je "${label}" wilt uitvoeren? Dit is niet omkeerbaar.`)) return;
    setDbLoading(actie); setDbMsg('');
    try {
      const r = await API.post('/databeheer/actie', { actie, password: dbPw });
      setDbMsg(`${label}: ${r.data.verwijderd || 0} verwijderd, ${r.data.samengevoegd || 0} samengevoegd`);
      loadStats();
    } catch (e) { setDbMsg(e.response?.data?.detail || 'Fout bij uitvoeren'); }
    setDbLoading('');
  };

  const setPrefix = (code, val) => {
    setForm(p => ({ ...p, nummering: { ...p.nummering, [code]: { ...(p.nummering[code] || {}), prefix: val } } }));
  };

  // ─── Boekingsregels state ──────────────────────────────────
  const [boekingsregels, setBoekingsregels] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [brForm, setBrForm] = useState(null); // null = closed, {} = new, {id:...} = edit
  const [brSaving, setBrSaving] = useState(false);

  const loadBoekingsregels = () => {
    API.get('/boekingsregels').then(r => setBoekingsregels(r.data)).catch(() => {});
    API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
  };
  const saveBr = async () => {
    if (!brForm?.leverancier_id) { alert('Selecteer een leverancier'); return; }
    setBrSaving(true);
    try {
      if (brForm.id) {
        await API.put(`/boekingsregels/${brForm.id}`, brForm);
      } else {
        await API.post('/boekingsregels', brForm);
      }
      setBrForm(null);
      loadBoekingsregels();
    } catch (e) { alert(e.response?.data?.detail || 'Fout bij opslaan'); }
    setBrSaving(false);
  };
  const delBr = async (id) => {
    if (!window.confirm('Deze boekingsregel verwijderen?')) return;
    await API.delete(`/boekingsregels/${id}`).catch(() => {});
    loadBoekingsregels();
  };

  // ─── Categorieën → Grootboekrekening + BTW + Aftrekbaarheid mapping ──
  const [catMappings, setCatMappings] = useState([]);
  const [catFilter, setCatFilter] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const loadCatMappings = () => API.get('/categorie-mappings').then(r => setCatMappings(r.data)).catch(() => {});

  // Eigen (custom) categorieën
  const [customCats, setCustomCats] = useState([]);
  const [customCatModal, setCustomCatModal] = useState({ open: false, edit: null });
  const [customCatForm, setCustomCatForm] = useState({ naam: '', groep: 'Eigen categorieën', grootboekrekening: '', btw_pct: 21, aftrekbaarheid: 100 });
  const loadCustomCats = () => API.get('/categorie-custom').then(r => setCustomCats(r.data || [])).catch(() => {});

  // Leverancier-regels (leer van correcties)
  const [supplierRules, setSupplierRules] = useState([]);
  const loadSupplierRules = () => API.get('/leverancier-regels').then(r => setSupplierRules(r.data || [])).catch(() => {});
  useEffect(() => { loadSupplierRules(); }, []);
  useEffect(() => {
    if (customCatModal.open) {
      if (customCatModal.edit) {
        setCustomCatForm({
          naam: customCatModal.edit.naam,
          groep: customCatModal.edit.groep || 'Eigen categorieën',
          grootboekrekening: customCatModal.edit.grootboekrekening || '',
          btw_pct: customCatModal.edit.btw_pct ?? 21,
          aftrekbaarheid: customCatModal.edit.aftrekbaarheid ?? 100,
        });
      } else {
        setCustomCatForm({ naam: '', groep: 'Eigen categorieën', grootboekrekening: '', btw_pct: 21, aftrekbaarheid: 100 });
      }
    }
  }, [customCatModal.open, customCatModal.edit]);
  useEffect(() => { loadCustomCats(); }, []);
  const saveCatField = async (categorie, field, value) => {
    setCatSaving(true);
    await API.put('/categorie-mappings', { categorie, [field]: value }).catch(() => {});
    await loadCatMappings();
    setCatSaving(false);
  };
  const resetCatMappings = async () => {
    if (!window.confirm('Alle overrides verwijderen en terug naar Belgische standaardwaarden?')) return;
    await API.post('/categorie-mappings/reset').catch(() => {});
    await loadCatMappings();
  };

  // ─── E-mail Inbox state ────────────────────────────────────
  const [inbox, setInbox] = useState(null);
  const [inboxCopied, setInboxCopied] = useState(false);
  const loadInbox = () => API.get('/email-inbox').then(r => setInbox(r.data)).catch(() => {});
  const regenerateInbox = async () => {
    if (!window.confirm('Nieuw inbox-adres genereren? Het oude adres werkt daarna niet meer.')) return;
    await API.post('/email-inbox/regenerate').catch(() => {});
    loadInbox();
  };
  const copyInbox = () => {
    if (inbox?.full_address) {
      navigator.clipboard.writeText(inbox.full_address);
      setInboxCopied(true);
      setTimeout(() => setInboxCopied(false), 2000);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip UI-only state keys before posting
      const { __preview_open, ...clean } = form;
      void __preview_open;
      // WhatsApp recipients altijd als array opslaan
      if (typeof clean.whatsapp_recipients === 'string') {
        clean.whatsapp_recipients = clean.whatsapp_recipients
          .split(/[,;]/)
          .map(s => s.trim())
          .filter(Boolean);
      }
      // Telegram chat_ids ook als array
      if (typeof clean.telegram_chat_ids === 'string') {
        clean.telegram_chat_ids = clean.telegram_chat_ids
          .split(/[,;]/)
          .map(s => s.trim())
          .filter(Boolean);
      }
      await API.put('/instellingen', clean);
      load();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { alert('Fout bij opslaan'); }
    setSaving(false);
  };

  const resetCounter = async (code) => {
    const val = resetVals[code];
    if (!val || isNaN(val)) { alert('Voer een geldig nummer in'); return; }
    try {
      await API.put(`/nummering/${code}/reset`, { volgend: parseInt(val) });
      setResetVals(p => ({ ...p, [code]: '' }));
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Fout bij reset'); }
  };

  const bjMaand = form.boekjaar_start.split('-')[0] || '01';
  const bjDag = form.boekjaar_start.split('-')[1] || '01';
  const sep = form.num_format?.separator || '-';
  const pad = parseInt(form.num_format?.padding || '4');
  const curYear = new Date().getFullYear();

  const navSearch = (typeof window !== 'undefined' ? window.__insSearch || '' : '');
  const [nav, setNav] = useState(navSearch);
  const [openGroups, setOpenGroups] = useState({ bedrijf: true, boekhouding: true, communicatie: true, systeem: true, kassa_grp: true });
  // Accordion-modus: één groep tegelijk open. localStorage onthoudt voorkeur.
  const [openGroupId, setOpenGroupId] = useState(() => {
    try { return localStorage.getItem('inst_sidebar_open_group') || 'bedrijf'; }
    catch { return 'bedrijf'; }
  });
  const toggleNavGroup = (gid) => {
    setOpenGroupId(prev => {
      const next = prev === gid ? '' : gid;
      try { localStorage.setItem('inst_sidebar_open_group', next); } catch {}
      return next;
    });
  };
  // Uitklapbare secties binnen tab Bedrijf → Algemeen
  // Standaard: belangrijkste info open, minder kritieke dicht.
  const [openBedrijfSecties, setOpenBedrijfSecties] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bedrijf_secties_open') || 'null') || { gegevens: true, contact: true, fact: true, aflev: false, factoring: false, instellingen: false }; }
    catch { return { gegevens: true, contact: true, fact: true, aflev: false, factoring: false, instellingen: false }; }
  });
  const toggleBedrijfSectie = (k) => {
    setOpenBedrijfSecties(prev => {
      const next = { ...prev, [k]: !prev[k] };
      try { localStorage.setItem('bedrijf_secties_open', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const currentNavId = tab === 'bedrijf' ? `bedrijf|${subTab}` : tab;
  const currentGroup = SETTINGS_TREE.find(g => g.kids.some(k => k.id === currentNavId));
  const currentItem = currentGroup?.kids.find(k => k.id === currentNavId);
  const currentHint = SETTINGS_HINTS[currentNavId];

  // Auto-open de groep waarin het actieve item zit (accordion-gedrag)
  useEffect(() => {
    if (currentGroup && openGroupId !== currentGroup.id) {
      setOpenGroupId(currentGroup.id);
      try { localStorage.setItem('inst_sidebar_open_group', currentGroup.id); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup?.id]);

  const goTo = (k) => {
    if (k.tab === 'functies') { navigate('/instellingen/functies'); return; }
    setTab(k.tab);
    if (k.subTab) setSubTab(k.subTab);
    else if (k.tab === 'bedrijf') setSubTab('bedrijf');
    if (k.tab === 'databeheer') loadStats();
    if (k.tab === 'boekingsregels') loadBoekingsregels();
    if (k.tab === 'categorien') loadCatMappings();
    if (k.tab === 'inbox') loadInbox();
  };

  const matchesNav = (label) => !nav || label.toLowerCase().includes(nav.toLowerCase());

  // Toegangscontrole: admin altijd, anders checken user.machtigingen
  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin';
  const hasPerm = (key) => !!(isAdminUser || (user?.machtigingen || {})[key]);
  const kidVisible = (k) => {
    if (k.adminOnly && !isAdminUser) return false;
    if (k.permKey && !hasPerm(k.permKey)) return false;
    return true;
  };

  return (
    <div data-testid="instellingen-page">
      {/* Breadcrumb + Save bar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="font-semibold text-gray-700">Instellingen</span>
          {currentGroup && <><span className="text-gray-300">›</span><span>{currentGroup.label}</span></>}
          {currentItem && <><span className="text-gray-300">›</span><span className="text-gray-900 font-semibold">{currentItem.label}</span></>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/instellingen/functies" className="btn btn-secondary btn-sm inline-flex items-center gap-1.5" data-testid="settings-open-functions"><Lightning size={14} /> Functies</Link>
          {saved && <span className="text-xs text-green-600 font-semibold flex items-center gap-1"><CheckCircle size={12} weight="bold" /> Opgeslagen</span>}
          <span className="text-[11px] text-gray-400 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>Gesynchroniseerd
          </span>
          {/* Globale "Opslaan" verbergen op tabs die hun eigen save-knop hebben
              (anders denkt de gebruiker dat hij hier matrix-cellen bewaart) */}
          {!TABS_MET_EIGEN_OPSLAAN.has(tab) && (
            <button
              className="px-4 py-1.5 bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 inline-flex items-center gap-1.5 transition-colors"
              onClick={save} disabled={saving} data-testid="save-instellingen-btn">
              <FloppyDisk size={13} weight="bold" /> {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          )}
          {/* Subtiele hint op tabs met eigen save — wijst de gebruiker op de juiste knop */}
          {TABS_MET_EIGEN_OPSLAAN.has(tab) && (
            <span
              className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 inline-flex items-center gap-1"
              data-testid="eigen-opslaan-hint"
              title="Deze pagina heeft een eigen Opslaan-knop in de panel hieronder"
            >
              ⬇ Gebruik de Opslaan-knop in de panel hieronder
            </span>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-[640px]">
          {/* Sidebar — Linear/Notion stijl */}
          <aside className="border-b lg:border-b-0 lg:border-r border-gray-200 bg-[#FAFAFA]">
            <div className="px-3 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-white border border-gray-200">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                <input
                  value={nav} onChange={e => setNav(e.target.value)}
                  className="text-xs bg-transparent outline-none flex-1 placeholder:text-gray-400"
                  placeholder="Zoeken in instellingen..."
                  data-testid="instellingen-nav-search"
                />
              </div>
            </div>
            <nav className="py-2">
              {SETTINGS_TREE.map(group => {
                const kids = group.kids.filter(k => kidVisible(k) && (matchesNav(k.label) || matchesNav(group.label)));
                if (kids.length === 0) return null;
                // Tijdens zoeken: alle groepen open. Anders accordion (één tegelijk).
                const isOpen = !!nav || openGroupId === group.id;
                const GIcon = group.icon;
                return (
                  <div key={group.id} className="mb-0.5">
                    <button
                      onClick={() => toggleNavGroup(group.id)}
                      className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-gray-50 transition-colors"
                      data-testid={`nav-group-${group.id}`}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="3" className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}><polyline points="9 18 15 12 9 6"/></svg>
                      <GIcon size={14} weight={isOpen ? 'fill' : 'regular'} style={{ color: isOpen ? (group.color || '#002FA7') : '#9CA3AF' }} />
                      <span className={`text-[11px] font-bold uppercase tracking-wider ${isOpen ? 'text-gray-900' : 'text-gray-500'}`}>{group.label}</span>
                      <span className="ml-auto text-[10px] text-gray-400 tabular-nums">{group.kids.length}</span>
                    </button>
                    {isOpen && kids.map(k => {
                      const a = currentNavId === k.id;
                      const ItemIcon = k.icon;
                      const groupColor = group.color || '#6B7280';
                      return (
                        <button key={k.id}
                          onClick={() => goTo(k)}
                          className={`w-full pl-6 pr-3 py-1.5 text-left text-xs flex items-center gap-2 transition-all border-l-2 ${
                            a
                              ? 'bg-[#EEF3FF] text-[#002FA7] font-semibold border-l-[#002FA7]'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-transparent'
                          }`}
                          data-testid={`nav-${k.id}`}
                        >
                          {ItemIcon && (
                            <ItemIcon
                              size={13}
                              weight={a ? 'fill' : 'regular'}
                              style={{ color: a ? '#002FA7' : groupColor, opacity: a ? 1 : 0.85 }}
                              className="flex-shrink-0"
                            />
                          )}
                          <span className="truncate">{k.label}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </nav>
          </aside>

          {/* Main content */}
          <section className="min-w-0">
            {/* Page header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 tracking-tight">{currentItem?.label || 'Instellingen'}</h2>
                  {currentHint && <p className="text-xs text-gray-500 mt-1 max-w-2xl leading-relaxed">{currentHint}</p>}
                </div>
                {/* Inline Bedrijf-subtabs wanneer we in Bedrijf zitten → ook benaderbaar via sidebar, maar handig als snelkoppeling */}
              </div>
            </div>

            <div className="p-6 space-y-5">

      {/* ── Bedrijfsnaam-header + horizontale tabs (per groep) ── */}
      {currentGroup && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-5" data-testid="group-header-card">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: (currentGroup.color || '#002FA7') + '15' }}>
              {React.createElement(currentGroup.icon || Buildings, { size: 22, weight: 'fill', style: { color: currentGroup.color || '#002FA7' } })}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900" data-testid="group-header-naam">{form.naam || form.maatschappelijke_naam || form.commerciele_naam || 'Bedrijf'}</h2>
              <div className="text-[10px] uppercase tracking-wider font-bold mt-0.5" style={{ color: currentGroup.color || '#6B7280' }}>
                {currentGroup.label}
              </div>
            </div>
          </div>
          <div className="px-5 overflow-x-auto">
            <div className="flex items-center gap-1">
              {currentGroup.kids.filter(k => kidVisible(k)).map(k => {
                const KIcon = k.icon;
                const isActive = currentNavId === k.id;
                const groupColor = currentGroup.color || '#6B7280';
                return (
                  <button
                    key={k.id}
                    onClick={() => goTo(k)}
                    className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${
                      isActive
                        ? 'border-current'
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                    style={{
                      color: isActive ? groupColor : '#6B7280',
                      borderColor: isActive ? groupColor : 'transparent',
                    }}
                    data-testid={`group-tab-${k.id}`}
                  >
                    {KIcon && <KIcon size={18} weight={isActive ? 'fill' : 'regular'} style={{ color: groupColor }} />}
                    {k.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Bedrijf ── */}
      {isBedrijfGroup && subTab === 'bedrijf' && (
        <div className="space-y-5">
          {/* Identiteit + Contact in 2 kolommen */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="section-card">
              <CollapsibleHeader title="Bedrijfsgegevens" open={openBedrijfSecties.gegevens} onToggle={() => toggleBedrijfSectie('gegevens')} testid="sec-bedrijfsgegevens" />
              {openBedrijfSecties.gegevens && (
              <div className="p-5 space-y-3">
                <div><label className="form-label">Ondernemingsnummer</label><input className="field-input font-mono" value={form.ondernemingsnummer} onChange={u('ondernemingsnummer')} placeholder="BE 0803.088.437" data-testid="inst-ondnr" /></div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <IOSToggle checked={!!form.btw_plichtig} onChange={v => setForm(p => ({ ...p, btw_plichtig: v }))} size="sm" showLabel={false} />
                    <span className="text-xs">BTW-plichtig</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <IOSToggle checked={!!form.kleine_onderneming} onChange={v => setForm(p => ({ ...p, kleine_onderneming: v }))} size="sm" showLabel={false} />
                    <span className="text-xs">Kleine onderneming</span>
                  </div>
                </div>
                <div><label className="form-label">Maatschappelijke naam *</label><input className="field-input" value={form.maatschappelijke_naam} onChange={u('maatschappelijke_naam')} placeholder="Uw bedrijf NV" data-testid="inst-maat-naam" /></div>
                <div><label className="form-label">Commerciële naam</label><input className="field-input" value={form.commerciele_naam || form.naam} onChange={u('commerciele_naam')} placeholder="Handelsnaam" data-testid="inst-comm-naam" /></div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div><label className="form-label">IBAN (standaard)</label><input className="field-input font-mono text-xs" value={form.iban} onChange={u('iban')} placeholder="BE68 5390 0754 7034" data-testid="inst-iban" /></div>
                  <div><label className="form-label">BIC (standaard)</label><input className="field-input font-mono text-xs" value={form.bic} onChange={u('bic')} placeholder="TRIOBEBB" data-testid="inst-bic" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-1 items-end">
                  <div><label className="form-label">IBAN 2 <span className="text-[10px] font-normal text-gray-400">(optioneel — per klant selecteerbaar)</span></label><input className="field-input font-mono text-xs" value={form.iban_2 || ''} onChange={u('iban_2')} placeholder="BE.. .... .... ...." data-testid="inst-iban-2" /></div>
                  <div><label className="form-label">BIC 2</label><input className="field-input font-mono text-xs" value={form.bic_2 || ''} onChange={u('bic_2')} placeholder="GEBABEBB" data-testid="inst-bic-2" /></div>
                </div>
                <div><label className="form-label">Label IBAN 2 <span className="text-[10px] text-gray-400">(bv. "ING-account", "Buitenlandse klanten")</span></label><input className="field-input text-xs" value={form.iban_2_label || ''} onChange={u('iban_2_label')} placeholder="Alternatieve rekening" data-testid="inst-iban-2-label" /></div>
                <EpcQrValidation form={form} testId="bank-epc-validation" />
                <div><label className="form-label">Domiciliëringsidentificatie</label><input className="field-input font-mono text-xs" value={form.domiciliering_id} onChange={u('domiciliering_id')} data-testid="inst-domid" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="form-label">Standaard munteenheid</label>
                    <select className="field-input" value={form.munteenheid} onChange={u('munteenheid')} data-testid="inst-munt">
                      <option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option>
                    </select></div>
                  <div><label className="form-label">Standaard BTW</label>
                    <select className="field-input" value={form.standaard_btw} onChange={u('standaard_btw')} data-testid="inst-std-btw">
                      <option value="21">21%</option><option value="6">6%</option><option value="12">12%</option><option value="0">0%</option>
                    </select></div>
                </div>
                <div><label className="form-label">Grootboekrekening</label><input className="field-input" value={form.grootboekrekening} onChange={u('grootboekrekening')} data-testid="inst-gbrk" /></div>
              </div>
              )}
            </div>

            <div className="section-card">
              <CollapsibleHeader title="IBAN Factoring (cessie van factuur)" open={openBedrijfSecties.factoring} onToggle={() => toggleBedrijfSectie('factoring')} testid="sec-factoring" />
              {openBedrijfSecties.factoring && (
                <FactoringPanel form={form} setForm={setForm} IOSToggle={IOSToggle} />
              )}
            </div>

            <div className="section-card">
              <CollapsibleHeader title="Contact & Bestuurder" open={openBedrijfSecties.contact} onToggle={() => toggleBedrijfSectie('contact')} testid="sec-contact" />
              {openBedrijfSecties.contact && (
              <div className="p-5 space-y-3">
                <div><label className="form-label">Aanspreking</label><input className="field-input" value={form.aanspreking} onChange={u('aanspreking')} placeholder="Dhr. / Mevr." data-testid="inst-aanspreking" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="form-label">Voornaam bestuurder</label><input className="field-input" value={form.voornaam_bestuurder} onChange={u('voornaam_bestuurder')} data-testid="inst-voornaam-best" /></div>
                  <div><label className="form-label">Achternaam bestuurder</label><input className="field-input" value={form.achternaam_bestuurder} onChange={u('achternaam_bestuurder')} data-testid="inst-achternaam-best" /></div>
                </div>
                <div><label className="form-label">Rechtspersonenregister (RPR)</label><input className="field-input" value={form.rpr} onChange={u('rpr')} data-testid="inst-rpr" /></div>
                <div><label className="form-label">E-mail *</label><input type="email" className="field-input" value={form.email} onChange={u('email')} placeholder="info@uwbedrijf.be" data-testid="inst-email" /></div>
                <div><label className="form-label">Website</label><input className="field-input" value={form.website} onChange={u('website')} placeholder="www.uwbedrijf.be" data-testid="inst-website" /></div>
                <div><label className="form-label">Telefoon *</label><input className="field-input" value={form.tel} onChange={u('tel')} placeholder="+32 16 123 45 67" data-testid="inst-tel" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="form-label">Mobiel</label><input className="field-input" value={form.mobiel} onChange={u('mobiel')} placeholder="+32 475 ..." data-testid="inst-mobiel" /></div>
                  <div><label className="form-label">Fax</label><input className="field-input" value={form.fax} onChange={u('fax')} data-testid="inst-fax" /></div>
                </div>
                <div><label className="form-label">Taal</label>
                  <select className="field-input" value={form.bedrijf_taal} onChange={u('bedrijf_taal')} data-testid="inst-taal">
                    <option value="nl">Nederlands (NL)</option><option value="fr">Frans (FR)</option><option value="en">Engels (EN)</option><option value="de">Duits (DE)</option>
                  </select></div>
              </div>
              )}
            </div>
          </div>

          {/* Facturatieadres + Afleveradres */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="section-card">
              <CollapsibleHeader
                title="Facturatieadres"
                open={openBedrijfSecties.fact}
                onToggle={() => toggleBedrijfSectie('fact')}
                testid="sec-fact"
                right={<button className="text-[10px] text-[#D97706] font-bold hover:underline" onClick={() => setForm(p => ({ ...p, fac_naam: p.commerciele_naam || p.naam, fac_tel: p.tel }))} data-testid="fac-kopieer">Kopieer</button>}
              />
              {openBedrijfSecties.fact && (
              <div className="p-5 space-y-3">
                <div><label className="form-label">T.a.v.</label><input className="field-input" value={form.fac_tav} onChange={u('fac_tav')} data-testid="inst-fac-tav" /></div>
                <div><label className="form-label">Commerciële naam</label><input className="field-input" value={form.fac_naam} onChange={u('fac_naam')} placeholder="Bedrijf BV" data-testid="inst-fac-naam" /></div>
                <div><label className="form-label">Land</label>
                  <select className="field-input" value={form.fac_land} onChange={u('fac_land')} data-testid="inst-fac-land">
                    <option value="België">België</option><option value="Nederland">Nederland</option><option value="Luxemburg">Luxemburg</option><option value="Frankrijk">Frankrijk</option><option value="Duitsland">Duitsland</option>
                  </select></div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-2"><label className="form-label">Straat</label><input className="field-input" value={form.fac_straat} onChange={u('fac_straat')} data-testid="inst-fac-straat" /></div>
                  <div><label className="form-label">Nr</label><input className="field-input" value={form.fac_nr} onChange={u('fac_nr')} data-testid="inst-fac-nr" /></div>
                  <div><label className="form-label">Bus</label><input className="field-input" value={form.fac_bus} onChange={u('fac_bus')} data-testid="inst-fac-bus" /></div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className="form-label">Postcode</label><input className="field-input" value={form.fac_postcode} onChange={u('fac_postcode')} data-testid="inst-fac-postcode" /></div>
                  <div className="col-span-2"><label className="form-label">Gemeente</label><input className="field-input" value={form.fac_gemeente} onChange={u('fac_gemeente')} data-testid="inst-fac-gemeente" /></div>
                </div>
                <div><label className="form-label">Telefoon</label><input className="field-input" value={form.fac_tel} onChange={u('fac_tel')} data-testid="inst-fac-tel" /></div>
              </div>
              )}
            </div>

            <div className="section-card">
              <CollapsibleHeader
                title="Afleveradres"
                open={openBedrijfSecties.aflev}
                onToggle={() => toggleBedrijfSectie('aflev')}
                testid="sec-aflev"
                right={
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <span className="text-gray-400">Zelfde als facturatieadres</span>
                    <div className={`w-9 h-[18px] rounded-full relative transition-colors cursor-pointer ${form.lev_zelfde ? 'bg-[#D97706]' : 'bg-gray-300'}`}
                      onClick={() => setForm(p => ({ ...p, lev_zelfde: !p.lev_zelfde }))} data-testid="inst-lev-zelfde">
                      <div className={`w-4 h-4 bg-white rounded-full absolute top-[1px] transition-all shadow-sm ${form.lev_zelfde ? 'left-[19px]' : 'left-[1px]'}`} />
                    </div>
                  </label>
                }
              />
              {openBedrijfSecties.aflev && !form.lev_zelfde && (
                <div className="p-5 space-y-3">
                  <div><label className="form-label">T.a.v.</label><input className="field-input" value={form.lev_tav} onChange={u('lev_tav')} /></div>
                  <div><label className="form-label">Commerciële naam</label><input className="field-input" value={form.lev_naam} onChange={u('lev_naam')} /></div>
                  <div><label className="form-label">Land</label>
                    <select className="field-input" value={form.lev_land} onChange={u('lev_land')}>
                      <option value="België">België</option><option value="Nederland">Nederland</option><option value="Luxemburg">Luxemburg</option><option value="Frankrijk">Frankrijk</option><option value="Duitsland">Duitsland</option>
                    </select></div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2"><label className="form-label">Straat</label><input className="field-input" value={form.lev_straat} onChange={u('lev_straat')} /></div>
                    <div><label className="form-label">Nr</label><input className="field-input" value={form.lev_nr} onChange={u('lev_nr')} /></div>
                    <div><label className="form-label">Bus</label><input className="field-input" value={form.lev_bus} onChange={u('lev_bus')} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><label className="form-label">Postcode</label><input className="field-input" value={form.lev_postcode} onChange={u('lev_postcode')} /></div>
                    <div className="col-span-2"><label className="form-label">Gemeente</label><input className="field-input" value={form.lev_gemeente} onChange={u('lev_gemeente')} /></div>
                  </div>
                  <div><label className="form-label">Telefoon</label><input className="field-input" value={form.lev_tel} onChange={u('lev_tel')} /></div>
                </div>
              )}
              {form.lev_zelfde && openBedrijfSecties.aflev && (
                <div className="p-5 text-sm text-gray-400">Het afleveradres is gelijk aan het facturatieadres.</div>
              )}
            </div>
          </div>

          {/* Bedrijfsinstellingen: logo + voorwaarden + bedrijfsnaam */}
          <div className="section-card">
            <CollapsibleHeader title="Bedrijfsinstellingen" open={openBedrijfSecties.instellingen} onToggle={() => toggleBedrijfSectie('instellingen')} testid="sec-bedrijfsinst" />
            {openBedrijfSecties.instellingen && (
            <div className="p-5 space-y-4">
              <div>
                <label className="form-label">Bedrijfsnaam *</label>
                <p className="text-[10px] text-gray-400 mb-1">Deze naam verschijnt op al uw documenten (facturen, offertes, creditnota's).</p>
                <input className="field-input" value={form.naam} onChange={u('naam')} placeholder="Emergent BV" data-testid="inst-naam" />
              </div>
              <div>
                <label className="form-label">BTW-nummer</label>
                <input className="field-input font-mono" value={form.btw} onChange={u('btw')} placeholder="BE 0123.456.789" data-testid="inst-btw" />
              </div>
              <div>
                <label className="form-label">Commentaar</label>
                <textarea className="field-input text-xs" rows={3} value={form.commentaar} onChange={u('commentaar')} placeholder="Interne opmerkingen..." data-testid="inst-commentaar" />
              </div>
            </div>
            )}
          </div>
        </div>
      )}


      {/* ── TAB: Branding (White-label) ── */}
      {isBedrijfGroup && subTab === 'branding' && (
        <div className="space-y-5" data-testid="branding-panel">
          <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-4 text-xs text-gray-700" style={{ border: '1px solid rgba(124,58,237,0.2)' }}>
            <div className="font-bold text-purple-700 mb-1 text-[13px]">🎨 White-label — verkoop deze app onder uw eigen merk</div>
            <div>Stel hier de app-naam, logo en hoofdkleur in. Deze verschijnen op het login-scherm, in de zijbalk en op PDF-documenten. Ideaal voor resellers die de app aan hun eigen klanten verkopen.</div>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
            <div className="px-5 py-3 border-b border-gray-100" style={{ background: '#F5F5F7' }}>
              <span className="text-[13px] font-bold text-gray-900">App-identiteit</span>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">App-naam (titel)</label>
                <input
                  className="field-input"
                  value={form.app_naam}
                  onChange={e => setForm(p => ({ ...p, app_naam: e.target.value }))}
                  placeholder="bv. Alphaprofiles billing"
                  data-testid="app-naam-input"
                />
                <div className="text-[10px] text-gray-500 mt-1">Verschijnt op login-scherm + browser-tabblad. Laat leeg voor "BillFac".</div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Primaire kleur</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.primary_kleur || '#002FA7'}
                    onChange={e => setForm(p => ({ ...p, primary_kleur: e.target.value }))}
                    style={{ width: 44, height: 44, padding: 2, border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer' }}
                    data-testid="primary-kleur-input"
                  />
                  <input
                    className="field-input flex-1 font-mono"
                    value={form.primary_kleur}
                    onChange={e => setForm(p => ({ ...p, primary_kleur: e.target.value }))}
                    placeholder="#002FA7"
                  />
                </div>
                <div className="text-[10px] text-gray-500 mt-1">Gebruikt voor knoppen, headers, links. Default: BillFac blauw.</div>
                {/* 🎨 AUTO-DETECT uit logo */}
                <button
                  type="button"
                  onClick={async () => {
                    if (!form.logo_url) {
                      toast.warning('Upload eerst je bedrijfslogo onder Bedrijf → Algemeen');
                      return;
                    }
                    try {
                      const r = await API.post('/instellingen/auto-kleur-uit-logo');
                      setForm(p => ({
                        ...p,
                        primary_kleur: r.data.primary_kleur,
                        accent_kleur: r.data.accent_kleur,
                      }));
                      toast.success(`✨ Kleuren gedetecteerd: ${r.data.primary_kleur} + ${r.data.accent_kleur}`);
                    } catch (e) {
                      toast.error('Fout: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                  className="mt-2 w-full px-3 py-2 text-xs rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold inline-flex items-center justify-center gap-2 shadow-sm transition-all"
                  data-testid="auto-kleur-uit-logo-btn"
                >
                  ✨ Detecteer kleuren automatisch uit logo
                </button>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Accent-kleur (optioneel)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.accent_kleur || '#22C55E'}
                    onChange={e => setForm(p => ({ ...p, accent_kleur: e.target.value }))}
                    style={{ width: 44, height: 44, padding: 2, border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer' }}
                    data-testid="accent-kleur-input"
                  />
                  <input
                    className="field-input flex-1 font-mono"
                    value={form.accent_kleur}
                    onChange={e => setForm(p => ({ ...p, accent_kleur: e.target.value }))}
                    placeholder="#22C55E"
                  />
                </div>
                <div className="text-[10px] text-gray-500 mt-1">Tweede kleur voor highlights, success-states.</div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">App-logo</label>
                <div className="flex items-center gap-4">
                  {form.logo_url ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-center" style={{ width: 120, height: 80 }}>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`}
                        alt="Logo"
                        style={{ maxHeight: 60, maxWidth: 100, objectFit: 'contain' }}
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 border-dashed rounded-lg p-3 text-[11px] text-gray-400 text-center" style={{ width: 120, height: 80, lineHeight: '60px' }}>Geen logo</div>
                  )}
                  <div className="flex-1">
                    <div className="text-[11px] text-gray-600 mb-1">Upload via <strong>Bedrijf → Algemeen</strong> (uw huidige logo wordt hergebruikt).</div>
                    <div className="text-[10px] text-gray-500">Aanbevolen: PNG met transparante achtergrond, max 300×100 px.</div>
                  </div>
                </div>
              </div>
              {/* ── Toggle: Logo-kleur ook toepassen op PDF documenten (Factuur, Offerte, Proforma, Creditnota) ── */}
              <div className="md:col-span-2 mt-2">
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg hover:border-blue-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.pdf_gebruik_logo_kleur !== false}
                    onChange={e => setForm(p => ({ ...p, pdf_gebruik_logo_kleur: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 accent-[#002FA7]"
                    data-testid="toggle-pdf-logo-kleur"
                  />
                  <div className="flex-1">
                    <div className="text-[12px] font-bold text-gray-900 flex items-center gap-2">
                      <span>🎨 Logo-kleur ook in PDF documenten</span>
                      {form.primary_kleur && (
                        <span
                          className="inline-block w-4 h-4 rounded-full border-2 border-white shadow-sm"
                          style={{ background: form.primary_kleur }}
                          title={form.primary_kleur}
                        />
                      )}
                    </div>
                    <div className="text-[11px] text-gray-600 mt-0.5">
                      Past uw logo-kleur toe op <strong>factuur</strong>, <strong>creditnota</strong>, <strong>offerte</strong> en <strong>proforma</strong> (tabel-header, totaal-lijn, accenten). Bestelbon (oranje) en leverbon (groen) houden hun operationele kleurcode voor herkenbaarheid.
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1 italic">
                      Tip: detecteer uw kleur automatisch via de "Auto-detecteer" knop bij Primaire kleur.
                    </div>
                  </div>
                </label>
              </div>
              {/* ── Foto-migratie: verplaats schijf-foto's naar DB voor productie-veiligheid ── */}
              <div className="md:col-span-2 mt-2">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="text-[12px] font-bold text-amber-900 mb-1 flex items-center gap-2">
                    <span>🛡️ Foto-veiligheid (productie)</span>
                  </div>
                  <div className="text-[11px] text-gray-700 mb-2">
                    Controleer of alle artikel-foto's in de database staan (i.p.v. enkel op schijf). Productie-servers zijn ephemeral — foto's op schijf kunnen verdwijnen bij redeploy.
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    data-testid="btn-migrate-photos"
                    onClick={async () => {
                      try {
                        const dry = await API.post('/admin/migrate-photos-to-db?dry_run=true');
                        const r = dry.data || {};
                        if ((r.on_disk_only || 0) === 0) {
                          toast.success(`✅ Alle ${r.in_db}/${r.scanned} foto's staan al veilig in de DB. Geen migratie nodig.`);
                          return;
                        }
                        const ok = window.confirm(
                          `${r.on_disk_only} foto's staan enkel op schijf. Naar database verplaatsen?\n\n` +
                          `Totaal artikels: ${r.scanned}\nAl in DB: ${r.in_db}\nKlaar om te migreren: ${r.on_disk_only}`
                        );
                        if (!ok) return;
                        const exec = await API.post('/admin/migrate-photos-to-db?dry_run=false');
                        const e = exec.data || {};
                        toast.success(`✅ ${e.migrated} foto's gemigreerd naar DB.${e.errors?.length ? ` (${e.errors.length} fouten)` : ''}`);
                      } catch (err) {
                        toast.error('Migratie mislukt: ' + (err.response?.data?.detail || err.message));
                      }
                    }}
                  >
                    Controleer & migreer foto's
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
            <div className="px-5 py-3 border-b border-gray-100" style={{ background: '#F5F5F7' }}>
              <span className="text-[13px] font-bold text-gray-900">Voorbeeld login-scherm</span>
            </div>
            <div className="p-8 bg-[#FAFAFA]">
              <div className="max-w-sm mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-center mb-6">
                  {form.logo_url && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`}
                      alt={form.app_naam}
                      style={{ maxHeight: 48, margin: '0 auto 10px', objectFit: 'contain' }}
                    />
                  )}
                  <h1 className="text-2xl font-extrabold tracking-tight">
                    <span style={{ color: form.primary_kleur || '#002FA7' }}>{(form.app_naam || 'BillFac').split(' ')[0]}</span>
                    {(form.app_naam || 'BillFac').split(' ').slice(1).join(' ') && <> {(form.app_naam || 'BillFac').split(' ').slice(1).join(' ')}</>}
                  </h1>
                  <p className="text-gray-500 text-xs mt-1">Inloggen op uw account</p>
                </div>
                <div className="space-y-3">
                  <div className="h-9 bg-gray-100 rounded border border-gray-200" />
                  <div className="h-9 bg-gray-100 rounded border border-gray-200" />
                  <button
                    className="w-full text-white font-bold py-2 rounded text-sm"
                    style={{ background: form.primary_kleur || '#002FA7' }}
                  >Inloggen</button>
                </div>
              </div>
              <div className="text-center mt-3 text-[10px] text-gray-400">© {new Date().getFullYear()} {form.naam || form.app_naam || 'BillFac'}</div>
            </div>
          </div>
        </div>
      )}


      {/* ── TAB: Bank ── */}
      {isBedrijfGroup && subTab === 'bank_tab' && (
        <div className="space-y-5">
          <div className="section-card">
            <div className="section-header"><span className="section-title">Bankrekeningen</span></div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Hieronder zie je een overzicht van de beschikbare bankrekeningen in je dossier. Deze bankrekeningen komen op je dashboard en kan je selecteren als je betaalopdrachten wil maken.</p>
              <p className="text-xs text-gray-500">Er zijn twee manieren om een bankrekening te koppelen met je account:</p>
              <ul className="text-xs text-gray-500 list-disc pl-5 space-y-1">
                <li><strong>DIGITAAL:</strong> koppel je bankrekening digitaal om je financiële transacties te importeren in het menu bank en ze automatisch te laten koppelen met een inkomende of uitgaande factuur.</li>
                <li><strong>CODA:</strong> koppel je bankrekening via CODA indien je boekhouder CODA-bestanden wenst te ontvangen bij het importeren van je documenten.</li>
              </ul>
              <div className="p-4 bg-gray-50 border border-gray-200 flex items-center gap-3">
                <BankIcon size={24} className="text-gray-400" />
                <span className="text-xs text-gray-500">Nog geen professionele bankrekening? <span className="text-[#D97706] font-bold cursor-pointer hover:underline">Open nu een bankrekening via dit platform.</span></span>
              </div>
            </div>
          </div>
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Gekoppelde rekeningen</span>
            </div>
            <div className="section-body">
              <table className="data-table">
                <thead><tr><th></th><th>Provider</th><th>IBAN</th><th>Naam</th><th>Munt</th><th>Digitaal</th><th>CODA</th><th>Geverifieerd</th><th>Actief</th></tr></thead>
                <tbody>
                  {form.iban ? (
                    <tr>
                      <td><span className="w-3 h-3 bg-green-500 rounded-full inline-block" /></td>
                      <td className="text-sm font-medium">{form.iban?.startsWith('BE') ? 'Belgische bank' : 'Bank'}</td>
                      <td className="font-mono text-xs">{form.iban}</td>
                      <td className="text-sm">{form.commerciele_naam || form.naam || '-'}</td>
                      <td>{form.munteenheid || 'EUR'}</td>
                      <td><CheckCircle size={16} className="text-green-500" /></td>
                      <td><CheckCircle size={16} className="text-green-500" /></td>
                      <td><CheckCircle size={16} className="text-green-500" /></td>
                      <td><span className="text-[10px] font-bold px-2 py-0.5 bg-green-100 text-green-700">Actief</span></td>
                    </tr>
                  ) : (
                    <tr><td colSpan="9" className="text-center text-gray-400 py-6">Geen bankrekeningen gekoppeld. Vul IBAN in bij Bedrijf tab.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Bijlagen ── */}
      {isBedrijfGroup && subTab === 'bijlagen_tab' && (
        <div className="space-y-5">
          <div className="section-card">
            <div className="section-header"><span className="section-title">Bedrijfsbijlagen</span></div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Upload hier uw bedrijfslogo en algemene voorwaarden. Deze worden automatisch toegevoegd aan uw offertes, facturen en creditnota's.</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="border border-dashed border-gray-300 p-5 text-center">
                  <FolderOpen size={32} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-medium mb-1">Bedrijfslogo</p>
                  <p className="text-[10px] text-gray-400 mb-3">Aanbevolen: PNG of JPG, transparante achtergrond, breedte ≥ 300 px.</p>
                  <label className="btn btn-secondary btn-sm cursor-pointer inline-flex items-center gap-1">
                    <Paperclip size={13} /> Bestand uploaden
                    <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.svg" onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('file', file);
                      try { const r = await API.post('/bijlagen/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setForm(p => ({ ...p, logo_url: `/api/uploads/${r.data.stored}` })); } catch { alert('Upload mislukt'); }
                    }} />
                  </label>
                  {form.logo_url && (
                    <>
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 inline-block">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`}
                          alt="Logo preview"
                          style={{ maxHeight: (form.logo_max_height || 60) + 'px', maxWidth: (form.logo_max_width || 220) + 'px' }}
                          data-testid="logo-preview"
                        />
                        <div className="text-[9px] text-gray-400 mt-1 uppercase tracking-wider">Exact zoals op PDF</div>
                      </div>
                      <button className="block mx-auto text-[#D97706] text-xs mt-2 hover:underline" onClick={() => setForm(p => ({ ...p, logo_url: '' }))}>Wissen</button>

                      <div className="mt-4 pt-3 border-t border-gray-200 text-left space-y-3">
                        <div>
                          <label className="form-label inline-flex items-center justify-between w-full">
                            <span>Hoogte op PDF <span className="text-gray-400 text-[10px]">(20–200 px)</span></span>
                            <span className="text-xs font-mono font-bold text-[#002FA7]">{form.logo_max_height || 60} px</span>
                          </label>
                          <input
                            type="range"
                            min="20"
                            max="200"
                            step="2"
                            className="w-full"
                            value={form.logo_max_height || 60}
                            onChange={e => setForm(p => ({ ...p, logo_max_height: parseInt(e.target.value) }))}
                            data-testid="logo-height"
                          />
                        </div>
                        <div>
                          <label className="form-label inline-flex items-center justify-between w-full">
                            <span>Max. breedte op PDF <span className="text-gray-400 text-[10px]">(60–500 px)</span></span>
                            <span className="text-xs font-mono font-bold text-[#002FA7]">{form.logo_max_width || 220} px</span>
                          </label>
                          <input
                            type="range"
                            min="60"
                            max="500"
                            step="10"
                            className="w-full"
                            value={form.logo_max_width || 220}
                            onChange={e => setForm(p => ({ ...p, logo_max_width: parseInt(e.target.value) }))}
                            data-testid="logo-width"
                          />
                        </div>
                        <button
                          type="button"
                          className="text-[10px] text-[#002FA7] hover:underline"
                          onClick={() => setForm(p => ({ ...p, logo_max_height: 60, logo_max_width: 220 }))}
                          data-testid="logo-reset"
                        >
                          ↺ Standaardgrootte herstellen (60 × 220)
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="border border-dashed border-gray-300 p-5 text-center">
                  <FileText size={32} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-medium mb-1">Algemene voorwaarden (per taal)</p>
                  <p className="text-[10px] text-gray-400 mb-3">Upload tot 4 taalversies. Bij PDF-generatie wordt automatisch de juiste versie als <strong>extra pagina('s)</strong> toegevoegd, afhankelijk van <code>klant.taal</code> (de "Standaard" wordt gebruikt als er voor die taal nog geen versie geüpload is). <strong>Alleen PDF-bestanden</strong> worden meegedrukt.</p>

                  {/* Health-check banner: waarschuwing bij ontbrekende bestanden (na deploy verlies) */}
                  {voorwHealth && !voorwHealth.ok && (
                    <div className="mb-3 p-4 rounded-lg bg-red-50 border-l-4 border-red-500" data-testid="voorwaarden-health-broken">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🚨</div>
                        <div className="flex-1">
                          <div className="font-bold text-red-900 text-sm mb-1">Voorwaarden-bestanden ontbreken op de server!</div>
                          <div className="text-red-800 text-[12px] mb-2">
                            <strong>{voorwHealth.missing_count}</strong> van uw geconfigureerde voorwaarden-bestand(en) zijn niet meer aanwezig (verloren gegaan door een productie-deploy). De disclaimer op documenten zegt "(bijgevoegd)" maar het PDF-bestand wordt in werkelijkheid <strong>niet meegedrukt</strong>.
                          </div>
                          <ul className="text-[11px] text-red-900 mb-2 ml-4">
                            {voorwHealth.items.filter(x => x.url && !x.present).map(x => (
                              <li key={x.key} className="mb-0.5">
                                • <strong>{x.label}</strong>: <code className="text-[10px] bg-red-100 px-1 rounded">{x.naam || x.stored}</code> — ontbreekt
                              </li>
                            ))}
                          </ul>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm text-[11px]"
                              data-testid="voorwaarden-repair-btn"
                              onClick={async () => {
                                if (!window.confirm(
                                  'Kapotte URL\'s wissen?\n\n' +
                                  'Dit wist de kapotte referenties zodat u opnieuw kunt uploaden.\n' +
                                  'Uw huidige (werkende) URL\'s blijven behouden.'
                                )) return;
                                try {
                                  const r = await API.post('/instellingen/voorwaarden/repair');
                                  toast.success(r.data?.message || 'Repair uitgevoerd');
                                  // Herlaad form + health
                                  const inst = await API.get('/instellingen');
                                  setForm(p => ({
                                    ...p,
                                    voorwaarden_url: inst.data.voorwaarden_url || '',
                                    voorwaarden_naam: inst.data.voorwaarden_naam || '',
                                    voorwaarden_url_nl: inst.data.voorwaarden_url_nl || '',
                                    voorwaarden_naam_nl: inst.data.voorwaarden_naam_nl || '',
                                    voorwaarden_url_fr: inst.data.voorwaarden_url_fr || '',
                                    voorwaarden_naam_fr: inst.data.voorwaarden_naam_fr || '',
                                    voorwaarden_url_en: inst.data.voorwaarden_url_en || '',
                                    voorwaarden_naam_en: inst.data.voorwaarden_naam_en || '',
                                    voorwaarden_url_de: inst.data.voorwaarden_url_de || '',
                                    voorwaarden_naam_de: inst.data.voorwaarden_naam_de || '',
                                  }));
                                  const h = await API.get('/instellingen/voorwaarden/health');
                                  setVoorwHealth(h.data);
                                } catch (e) {
                                  toast.error('Repair mislukt: ' + (e?.response?.data?.detail || e.message));
                                }
                              }}
                            >
                              🧹 Wis kapotte URL's
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm text-[11px]"
                              data-testid="voorwaarden-health-refresh"
                              onClick={async () => {
                                try {
                                  const h = await API.get('/instellingen/voorwaarden/health');
                                  setVoorwHealth(h.data);
                                  toast.info(h.data.message);
                                } catch (e) {
                                  toast.error('Check mislukt: ' + e.message);
                                }
                              }}
                            >
                              🔄 Opnieuw controleren
                            </button>
                          </div>
                          <div className="text-[10px] text-red-700 italic mt-2">
                            Na "Wis kapotte URL's" → upload het PDF-bestand opnieuw via de "Upload"-knop hieronder. Vanaf nu wordt het bestand ook in de database bewaard en overleeft het toekomstige deploys.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {voorwHealth && voorwHealth.ok && voorwHealth.items.some(x => x.url) && (
                    <div className="mb-3 p-2 rounded bg-green-50 border-l-4 border-green-500 text-[11px]" data-testid="voorwaarden-health-ok">
                      <span className="font-medium text-green-800">✅ Alle voorwaarden-bestanden zijn aanwezig op de server</span>
                      {' · '}
                      <span className="text-green-700">
                        {voorwHealth.items.filter(x => x.present).length} bestand(en) — bronnen: {[...new Set(voorwHealth.items.filter(x => x.present).map(x => x.source))].join(', ')}
                      </span>
                    </div>
                  )}

                  {/* Duplicate-detectie: zelfde URL bij meerdere talen waarschuwen */}
                  {(() => {
                    const urls = [
                      { key: 'NL', url: form.voorwaarden_url_nl || '' },
                      { key: 'FR', url: form.voorwaarden_url_fr || '' },
                      { key: 'EN', url: form.voorwaarden_url_en || '' },
                      { key: 'DE', url: form.voorwaarden_url_de || '' },
                    ].filter((x) => x.url);
                    const byUrl = {};
                    urls.forEach((u) => { (byUrl[u.url] = byUrl[u.url] || []).push(u.key); });
                    const dups = Object.entries(byUrl).filter(([, ks]) => ks.length > 1);
                    if (dups.length === 0) return null;
                    return (
                      <div className="mb-3 p-3 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-[11px]" data-testid="voorwaarden-duplicate-warn">
                        <strong className="text-amber-900">⚠ Let op:</strong>{' '}
                        <span className="text-amber-800">
                          Meerdere talen wijzen naar hetzelfde bestand. Dit betekent dat klanten met deze taal-instelling dezelfde versie van de algemene voorwaarden ontvangen:
                        </span>
                        <ul className="mt-1 text-amber-900 font-medium">
                          {dups.map(([url, keys]) => (
                            <li key={url}>• Talen <strong>{keys.join(', ')}</strong> delen: <code className="text-[10px]">{(url.split('/').pop() || '').slice(0, 40)}</code></li>
                          ))}
                        </ul>
                        <div className="mt-1 text-amber-700 italic">→ Upload een ander bestand voor elke taal, of laat een taal leeg om de "Standaard"-versie te gebruiken.</div>
                      </div>
                    );
                  })()}

                  {/* Upload-rijen per taal */}
                  <div className="space-y-2 text-left mb-4">
                    {[
                      { code: '', flag: '⭐', label: 'Standaard (fallback)', urlKey: 'voorwaarden_url', naamKey: 'voorwaarden_naam' },
                      { code: 'nl', flag: '🇳🇱', label: 'Nederlands', urlKey: 'voorwaarden_url_nl', naamKey: 'voorwaarden_naam_nl' },
                      { code: 'fr', flag: '🇫🇷', label: 'Français', urlKey: 'voorwaarden_url_fr', naamKey: 'voorwaarden_naam_fr' },
                      { code: 'en', flag: '🇬🇧', label: 'English', urlKey: 'voorwaarden_url_en', naamKey: 'voorwaarden_naam_en' },
                      { code: 'de', flag: '🇩🇪', label: 'Deutsch', urlKey: 'voorwaarden_url_de', naamKey: 'voorwaarden_naam_de' },
                    ].map(t => {
                      const url = form[t.urlKey] || '';
                      const naam = form[t.naamKey] || '';
                      const hasFile = !!url;
                      return (
                        <div key={t.code || 'default'} className={`flex items-center gap-2 p-2 border rounded ${hasFile ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`} data-testid={`voorwaarden-lang-${t.code || 'default'}`}>
                          <span className="text-lg shrink-0" title={t.label}>{t.flag}</span>
                          <span className="text-xs font-bold w-24 shrink-0">{t.label}</span>
                          {hasFile ? (
                            <>
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] text-xs hover:underline flex-1 truncate">{naam}</a>
                              <button
                                className="text-[#D97706] text-[11px] hover:underline shrink-0"
                                onClick={() => setForm(p => ({ ...p, [t.urlKey]: '', [t.naamKey]: '' }))}
                              >Wissen</button>
                            </>
                          ) : (
                            <>
                              <span className="text-[11px] text-gray-400 flex-1">Geen bestand</span>
                              <label className="btn btn-secondary btn-sm cursor-pointer inline-flex items-center gap-1 shrink-0 text-[11px]">
                                <Paperclip size={11} /> Upload
                                <input type="file" className="hidden" accept=".pdf" onChange={async (e) => {
                                  const file = e.target.files?.[0]; if (!file) return;
                                  if (!file.name.toLowerCase().endsWith('.pdf')) { alert('Alleen PDF toegestaan'); return; }
                                  const fd = new FormData(); fd.append('file', file);
                                  try {
                                    const r = await API.post('/bijlagen/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    setForm(p => ({ ...p, [t.urlKey]: `/api/uploads/${r.data.stored}`, [t.naamKey]: file.name, voorwaarden_actief: true }));
                                  } catch { alert('Upload mislukt'); }
                                }} />
                              </label>
                              {t.code && t.code !== 'nl' && (form.voorwaarden_url_nl || form.voorwaarden_url) && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!window.confirm(
                                      `Vertaal de Standaard/NL voorwaarden automatisch naar ${t.label} via AI?\n\n` +
                                      `Dit duurt ~20-60 seconden. Klik daarna 'Opslaan' om te bewaren.`
                                    )) return;
                                    try {
                                      const r = await API.post('/instellingen/voorwaarden/translate', {
                                        source_lang: form.voorwaarden_url_nl ? 'nl' : 'nl',
                                        targets: [t.code],
                                      });
                                      const res = (r.data?.results || {})[t.code];
                                      if (res && res.url) {
                                        setForm(p => ({ ...p, [t.urlKey]: res.url, [t.naamKey]: res.naam, voorwaarden_actief: true }));
                                        toast.success(`AI-vertaling klaar: ${res.naam}`);
                                      } else {
                                        toast.error('Vertaling mislukt: ' + (res?.error || 'onbekend'));
                                      }
                                    } catch (e) {
                                      toast.error('Vertaling mislukt: ' + (e?.response?.data?.detail || e.message));
                                    }
                                  }}
                                  className="text-[10px] px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center gap-1 shrink-0 font-semibold"
                                  title={`AI-vertaal Standaard/NL voorwaarden naar ${t.label}`}
                                  data-testid={`voorwaarden-translate-${t.code}`}
                                >
                                  🌐 AI-vertaal
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {(form.voorwaarden_naam || form.voorwaarden_naam_nl || form.voorwaarden_naam_fr || form.voorwaarden_naam_en || form.voorwaarden_naam_de) && (
                    <>
                      <label className="inline-flex items-center gap-2 text-[11px] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={form.voorwaarden_actief !== false}
                          onChange={e => setForm(p => ({ ...p, voorwaarden_actief: e.target.checked }))}
                          data-testid="voorwaarden-actief"
                        />
                        <span className="font-bold text-gray-700">Altijd mee afdrukken bij alle documenten</span>
                      </label>
                      {form.voorwaarden_actief !== false && (
                        <div className="mt-3 pt-3 border-t border-gray-100" data-testid="voorwaarden-per-type">
                          <div className="text-xs font-semibold text-gray-700 mb-2">Voorwaarden per documenttype</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {[
                              { k: 'factuur',    l: 'Factuur',    icon: '📄' },
                              { k: 'creditnota', l: 'Creditnota', icon: '↩️' },
                              { k: 'offerte',    l: 'Offerte',    icon: '✉️' },
                              { k: 'bestelbon',  l: 'Bestelbon',  icon: '🛒' },
                              { k: 'leverbon',   l: 'Leverbon',   icon: '🚚' },
                              { k: 'proforma',   l: 'Proforma',   icon: '📑' },
                            ].map(t => {
                              const checked = form.voorwaarden_per_type?.[t.k] !== false;
                              return (
                                <label
                                  key={t.k}
                                  className={`flex items-center gap-2 cursor-pointer p-2 border rounded transition-colors ${checked ? 'border-[#002FA7] bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                                  data-testid={`voorwaarden-type-${t.k}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4"
                                    checked={checked}
                                    onChange={e => setForm(p => ({
                                      ...p,
                                      voorwaarden_per_type: { ...(p.voorwaarden_per_type || {}), [t.k]: e.target.checked },
                                    }))}
                                  />
                                  <span className="text-xs font-medium">{t.icon} {t.l}</span>
                                </label>
                              );
                            })}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-2">
                            Vink uit per type om de voorwaarden daar weg te laten. Werkt enkel als de schakelaar hierboven actief is.
                          </div>
                        </div>
                      )}

                      {/* Persistent-storage migratie: vermijdt verlies bij deploy (productie ephemeral pods) */}
                      <div className="mt-4 pt-3 border-t border-gray-100" data-testid="voorwaarden-migrate-section">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <div className="text-xs font-semibold text-gray-700">🛡️ Deploy-veilig maken</div>
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              Kopieer geüploade voorwaarden-PDF's naar de database zodat ze niet verloren gaan bij een productie-deploy. Één klik, idempotent.
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm text-[11px] shrink-0"
                            data-testid="voorwaarden-migrate-btn"
                            onClick={async () => {
                              try {
                                const r = await API.post('/instellingen/voorwaarden/migrate-to-db');
                                const d = r.data || {};
                                const msg = `✅ Migratie voltooid\n\n` +
                                  `Gemigreerd naar DB: ${d.gemigreerd}\n` +
                                  `Reeds aanwezig: ${d.al_ok}\n` +
                                  `Ontbreekt op disk: ${d.ontbreekt_op_disk}\n` +
                                  `Mislukt: ${d.mislukt}\n\n` +
                                  `Voorwaarden blijven nu bewaard bij toekomstige deploys.`;
                                if (d.mislukt > 0) toast.error(`Migratie deels mislukt (${d.mislukt}). Zie console voor details.`);
                                else toast.success(`Voorwaarden persistent opgeslagen (${d.gemigreerd + d.al_ok} bestanden)`);
                                console.log('[voorwaarden-migrate]', d);
                                if (d.ontbreekt_op_disk > 0) {
                                  alert(msg + `\n\n⚠️ ${d.ontbreekt_op_disk} bestand(en) niet meer op disk gevonden. Upload deze opnieuw.`);
                                }
                              } catch (e) {
                                toast.error('Migratie mislukt: ' + (e?.response?.data?.detail || e.message));
                              }
                            }}
                          >
                            Migreer naar DB
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* EPC-QR + Artikel-foto's verhuisd naar Instellingen → PDF-layouts per type */}
          <div className="section-card" data-testid="bijlagen-spacing-card">
            <div className="section-header"><span className="section-title inline-flex items-center gap-2">📐 PDF-layout fijn afstellen</span></div>
            <div className="p-5 space-y-4">
              <div>
                <label className="form-label inline-flex items-center justify-between w-full">
                  <span>Grootte factuur-titel <span className="text-gray-400 text-[10px]">(12–36 px, bv. "Factuur FAC-2026-0001")</span></span>
                  <span className="text-xs font-mono font-bold text-[#002FA7]">{form.pdf_title_size ?? 20} px</span>
                </label>
                <input
                  type="range"
                  min="12"
                  max="36"
                  step="1"
                  className="w-full"
                  value={form.pdf_title_size ?? 20}
                  onChange={e => setForm(p => ({ ...p, pdf_title_size: parseInt(e.target.value) }))}
                  data-testid="pdf-title-size"
                />
                <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                  <span>klein</span><span>standaard (20)</span><span>groot</span>
                </div>
              </div>
              <div>
                <label className="form-label inline-flex items-center justify-between w-full">
                  <span>Ruimte boven tabel <span className="text-gray-400 text-[10px]">(0–40 px)</span></span>
                  <span className="text-xs font-mono font-bold text-[#002FA7]">{form.pdf_meta_gap ?? 14} px</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="40"
                  step="2"
                  className="w-full"
                  value={form.pdf_meta_gap ?? 14}
                  onChange={e => setForm(p => ({ ...p, pdf_meta_gap: parseInt(e.target.value) }))}
                  data-testid="pdf-meta-gap"
                />
                <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                  <span>compact</span><span>standaard (14)</span><span>ruim</span>
                </div>
              </div>
              <button
                type="button"
                className="text-[10px] text-[#002FA7] hover:underline"
                onClick={() => setForm(p => ({ ...p, pdf_meta_gap: 14, pdf_title_size: 20 }))}
                data-testid="pdf-spacing-reset"
              >
                ↺ Alle standaardwaarden herstellen
              </button>
            </div>
          </div>

          <div className="section-card" data-testid="bijlagen-proefpagina-card2">
            <div className="section-header"><span className="section-title inline-flex items-center gap-2">📄 Proefpagina genereren</span></div>
            <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <p className="text-xs text-gray-500">
                  Bekijk meteen hoe uw documenten er live uit gaan zien met de <strong>huidige instellingen</strong>: logo-grootte, titel-grootte, kleuren, QR-code, PDF-spacing en algemene voorwaarden. De voorbeeld-PDF opent in een nieuw venster zonder dat u iets hoeft op te slaan.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-primary inline-flex items-center gap-2"
                onClick={async () => {
                  try {
                    const res = await API.post('/pdf-layouts/preview/factuur', {
                      inst_overrides: {
                        logo_url: form.logo_url,
                        logo_max_height: form.logo_max_height,
                        logo_max_width: form.logo_max_width,
                        epc_qr_actief: form.epc_qr_actief,
                        voorwaarden_url: form.voorwaarden_url,
                        voorwaarden_naam: form.voorwaarden_naam,
                        voorwaarden_actief: form.voorwaarden_actief,
                        pdf_meta_gap: form.pdf_meta_gap,
                        pdf_title_size: form.pdf_title_size,
                      },
                    }, { responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                  } catch (e) {
                    alert('Voorbeeld genereren mislukt: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="bijlagen-proefpagina-btn"
              >
                📄 Voorbeeld-PDF openen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Informatie ── */}
      {isBedrijfGroup && subTab === 'informatie' && (
        <div className="space-y-5">
          <div className="section-card">
            <div className="section-header"><span className="section-title">Externe informatie</span></div>
            <div className="p-5 space-y-5">
              <p className="text-xs text-gray-500">Snelle links naar externe bronnen over uw onderneming.</p>
              {[
                { label: 'Jaarrekeningen', desc: 'Bekijk de jaarrekeningen van uw onderneming op de Nationale Bank.', url: 'https://consult.cbso.nbb.be/' },
                { label: 'Publicaties staatsblad', desc: 'Raadpleeg de publicaties in het Belgisch Staatsblad.', url: 'https://www.ejustice.just.fgov.be/cgi/welcome.pl' },
                { label: 'KBO', desc: 'Kruispuntbank van Ondernemingen — officiële bedrijfsregistratie.', url: `https://kbopub.economie.fgov.be/kbopub/zoeknummerform.html?nummer=${(form.ondernemingsnummer || form.btw || '').replace(/[^0-9]/g, '')}` },
                { label: 'Inhoudingsplicht', desc: 'Controleer de inhoudingsplicht van uw onderneming (RSZ/fiscaal).', url: 'https://www.checkinhoudingsplicht.be/' },
              ].map(link => (
                <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 border border-gray-200 hover:border-[#D97706] hover:bg-[#FFFBEB] transition-colors group cursor-pointer" data-testid={`info-${link.label.toLowerCase()}`}>
                  <div>
                    <div className="text-sm font-bold text-[#D97706] group-hover:underline">{link.label}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">{link.desc}</div>
                  </div>
                  <LinkIcon size={16} className="text-gray-300 group-hover:text-[#D97706]" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Bedrijfsdocumenten ── */}
      {isBedrijfGroup && subTab === 'bedrijfsdocs' && (
        <div className="space-y-5">
          <div className="section-card">
            <div className="section-header"><span className="section-title">Bedrijfsgegevens</span></div>
            <div className="p-5 space-y-3">
              <p className="text-[10px] text-gray-400">Licentieperiode: {form.boekjaar_start ? `${new Date().getFullYear()}` : '-'}</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 border border-gray-200"><div className="text-[10px] text-gray-400 uppercase tracking-wider">Inkomsten</div><div className="text-2xl font-bold text-[#002FA7]" data-testid="bdocs-inkomsten">-</div></div>
                <div className="text-center p-3 border border-gray-200"><div className="text-[10px] text-gray-400 uppercase tracking-wider">Uitgaven</div><div className="text-2xl font-bold text-[#D97706]" data-testid="bdocs-uitgaven">-</div></div>
                <div className="text-center p-3 border border-gray-200"><div className="text-[10px] text-gray-400 uppercase tracking-wider">Documenten</div><div className="text-2xl font-bold text-gray-600" data-testid="bdocs-documenten">-</div></div>
              </div>
            </div>
          </div>
          <div className="section-card">
            <div className="section-header"><span className="section-title">Download bedrijfsdocumenten</span></div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Geef de begin- en einddatum in van de periode waarvoor je de aangemaakte documenten wil downloaden.</p>
              <div className="grid grid-cols-2 gap-4" style={{ maxWidth: 400 }}>
                <div><label className="form-label">Van</label><input type="date" className="field-input" defaultValue={`${new Date().getFullYear()}-01-01`} data-testid="bdocs-van" /></div>
                <div><label className="form-label">Tot en met</label><input type="date" className="field-input" defaultValue={new Date().toISOString().split('T')[0]} data-testid="bdocs-tot" /></div>
              </div>
              <p className="text-xs text-gray-400">Selecteer het bestandstype van de bestanden die u wenst te downloaden</p>
              <div className="space-y-3">
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Inkomsten</div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" defaultChecked className="accent-[#D97706]" /> Verkoopboek als UBL</label>
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" defaultChecked className="accent-[#D97706]" /> Verkoopboek als PDF</label>
                  </div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Uitgaven</div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" defaultChecked className="accent-[#D97706]" /> Aankoopboek als UBL</label>
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" defaultChecked className="accent-[#D97706]" /> Aankoopboek als PDF</label>
                  </div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Documenten</div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" defaultChecked className="accent-[#D97706]" /> Alle documenten</label>
                </div>
              </div>
              <button className="btn btn-primary" data-testid="bdocs-download"><DownloadSimple size={14} weight="bold" /> Download</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: E-Invoicing ── */}
      {isBedrijfGroup && subTab === 'einvoicing' && (
        <div className="space-y-5" data-testid="einvoicing-tab">
          <div className="section-card">
            <div className="p-8 text-center" style={{ background: '#E8F5E9' }}>
              <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-sm">
                <Globe size={32} className="text-[#D97706]" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Peppol / UBL — klaar voor 2026</h3>
              <p className="text-sm text-gray-700 max-w-lg mx-auto mb-4">
                Belgische B2B-facturatie via Peppol wordt verplicht vanaf 1 januari 2026. Dit platform ondersteunt volledige UBL 2.1 / Peppol BIS Billing 3.0 <strong>import én export</strong> — upload een XML, laat uw leverancier het naar uw inbox-adres sturen of download/verzend uw eigen facturen als Peppol-XML.
              </p>
              <div className="flex justify-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold px-3 py-1 bg-green-100 text-green-700"><CheckCircle size={11} weight="bold" className="inline mr-1" /> UBL Import actief</span>
                <span className="text-[10px] font-bold px-3 py-1 bg-green-100 text-green-700"><CheckCircle size={11} weight="bold" className="inline mr-1" /> UBL Export (BIS 3.0) actief</span>
                <span className="text-[10px] font-bold px-3 py-1 bg-blue-100 text-blue-700">Access Point: manueel of via provider</span>
              </div>
            </div>
          </div>

          {/* Peppol Access Point config — voor automatische verzending via provider */}
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Peppol Access Point (optioneel)</span>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-600 leading-relaxed">
                Vul de endpoint van uw Peppol Access Point in (Codabox, Ibanity, Unifiedpost, …) om facturen <strong>automatisch te verzenden</strong> bij het klikken op "Verzenden via Peppol". Als je dit leeg laat, kan je altijd de XML zelf downloaden en handmatig uploaden bij je provider.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1 block">Access Point URL</label>
                  <input
                    className="field-input text-sm"
                    placeholder="https://api.ibanity.com/einvoicing/peppol/outbound-documents"
                    value={form.peppol_access_point_url || ''}
                    onChange={e => setForm(p => ({ ...p, peppol_access_point_url: e.target.value }))}
                    data-testid="peppol-ap-url"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1 block">API Key / Bearer Token</label>
                  <input
                    type="password"
                    className="field-input text-sm"
                    placeholder="Bearer-token van uw provider"
                    value={form.peppol_access_point_apikey || ''}
                    onChange={e => setForm(p => ({ ...p, peppol_access_point_apikey: e.target.value }))}
                    data-testid="peppol-ap-apikey"
                  />
                </div>
              </div>
              <div className="text-[10px] text-gray-500 bg-amber-50 border-l-3 border-amber-400 p-2.5">
                <strong>⚠ Belangrijk:</strong> De XML-generatie is 100% conform EN 16931 / Peppol BIS 3.0. Voor <strong>effectieve verzending via het Peppol-netwerk</strong> heeft u een registratie bij een Access Point nodig — dit is een ander dienstverlener dan deze app. Populaire BE-providers: <a href="https://www.codabox.com" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Codabox</a>, <a href="https://ibanity.com" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Ibanity</a>, <a href="https://www.unifiedpost.com" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Unifiedpost</a>.
              </div>
            </div>
          </div>

          {/* Peppol verzend-log */}
          <PeppolLogViewer />

          <div className="section-card">
            <div className="section-header"><span className="section-title">Hoe UBL-import werkt</span></div>
            <div className="p-5 space-y-3 text-xs text-gray-600">
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-[#002FA7] text-white text-[11px] font-bold flex items-center justify-center shrink-0">1</div>
                <div><strong className="text-gray-800">Ontvangen</strong> — Upload de UBL XML via Inkomende Facturen, of laat uw leverancier de XML versturen naar uw e-mail inbox (zie tab E-mail Inbox).</div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-[#002FA7] text-white text-[11px] font-bold flex items-center justify-center shrink-0">2</div>
                <div><strong className="text-gray-800">Automatisch parsen</strong> — Alle velden (factuurnr, leverancier, BTW-nummer, IBAN, vervaldag, lijnen, totalen) worden 100% accuraat uit de XML gehaald. Geen AI nodig, volledig deterministisch.</div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-[#002FA7] text-white text-[11px] font-bold flex items-center justify-center shrink-0">3</div>
                <div><strong className="text-gray-800">Koppelen</strong> — Leverancier wordt gematched op BTW-nummer en eventuele Boekingsregels (zie tab Boekingsregels) worden automatisch toegepast.</div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-[#002FA7] text-white text-[11px] font-bold flex items-center justify-center shrink-0">4</div>
                <div><strong className="text-gray-800">Goedkeuren & boeken</strong> — Document verschijnt in de pipeline (Verwerkt → Goedgekeurd → Geboekt). UBL-facturen krijgen een <span className="text-[9px] font-bold px-1.5 py-0.5 bg-cyan-100 text-cyan-700">PEPPOL</span> badge.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Documenten ── */}
      {tab === 'documenten' && (
        <div className="space-y-5">
          {/* Toggles */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Document opties</span></div>
            <div className="divide-y divide-gray-100">
              {[
                { key: 'aankopen_actief', label: 'Aankopen', desc: 'Verwerk aankopen via scan & herken' },
                { key: 'po_nummer', label: 'PO-nummer / referentie', desc: 'Vermeld een PO-nummer of referentie van uw klant in documenten' },
                { key: 'taal_per_doc', label: 'Taal kiezen per document', desc: 'Stel de taal in per document' },
                { key: 'doc_notities', label: 'Document notities', desc: 'Toon notities kolom op documenten' },
                { key: 'titels_op_doc', label: 'Titels op documenten', desc: 'Toon titels en koppen op documenten' },
                { key: 'doc_historiek', label: 'Document historiek', desc: 'Toon tijdlijn van acties per document' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50">
                  <div><div className="text-sm font-medium">{opt.label}</div><div className="text-[10px] text-gray-400">{opt.desc}</div></div>
                  <input type="checkbox" checked={form[opt.key] ?? (opt.defaultVal ?? true)} onChange={e => setForm(p => ({ ...p, [opt.key]: e.target.checked }))} className="w-5 h-5 accent-[#059669]" data-testid={`doc-${opt.key}`} />
                </label>
              ))}
            </div>
          </div>
          {/* Radio groups */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="section-card">
              <div className="section-header"><span className="section-title">Korting per regel</span></div>
              <div className="p-4 space-y-2">
                {[{ v: 'indien_gebruikt', l: 'Ja, kolom enkel tonen indien gebruikt' }, { v: 'altijd', l: 'Ja, kolom altijd tonen' }, { v: 'nee', l: 'Nee' }].map(o => (
                  <label key={o.v} className="flex items-center gap-2 text-xs cursor-pointer"><input type="radio" name="korting_display" checked={form.korting_display === o.v} onChange={() => setForm(p => ({ ...p, korting_display: o.v }))} className="accent-[#D97706]" />{o.l}</label>
                ))}
              </div>
            </div>
            <div className="section-card">
              <div className="section-header"><span className="section-title">Prijzen ingeven</span></div>
              <div className="p-4 space-y-2">
                {[{ v: 'excl', l: 'Altijd exclusief BTW' }, { v: 'incl', l: 'Altijd inclusief BTW' }, { v: 'std_excl', l: 'Standaard exclusief BTW' }, { v: 'std_incl', l: 'Standaard inclusief BTW' }].map(o => (
                  <label key={o.v} className="flex items-center gap-2 text-xs cursor-pointer"><input type="radio" name="prijzen_modus" checked={form.prijzen_modus === o.v} onChange={() => setForm(p => ({ ...p, prijzen_modus: o.v }))} className="accent-[#D97706]" />{o.l}</label>
                ))}
              </div>
            </div>
            <div className="section-card">
              <div className="section-header"><span className="section-title">Decimalen</span></div>
              <div className="p-4 space-y-2">
                {[{ v: '2', l: '2 cijfers na de komma' }, { v: '3', l: '3 cijfers na de komma' }, { v: '4', l: '4 cijfers na de komma' }].map(o => (
                  <label key={o.v} className="flex items-center gap-2 text-xs cursor-pointer"><input type="radio" name="decimalen" checked={form.decimalen === o.v} onChange={() => setForm(p => ({ ...p, decimalen: o.v }))} className="accent-[#D97706]" />{o.l}</label>
                ))}
              </div>
            </div>
          </div>
          {/* Standaard nota's */}
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Standaard nota's onderaan documenten</span>
              <div className="flex items-center gap-1.5 text-xs">
                <IOSToggle checked={!!form.nota_per_taal} onChange={v => setForm(p => ({ ...p, nota_per_taal: v }))} size="sm" showLabel={false} />
                <span>Per taal</span>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {[
                { type: 'Factuur', keys: ['factuur_nota_nl', 'factuur_nota_fr', 'factuur_nota_en'] },
                { type: 'Proforma', keys: ['proforma_nota_nl', 'proforma_nota_fr', 'proforma_nota_en'] },
                { type: 'Creditnota', keys: ['cn_nota_nl', 'cn_nota_fr', 'cn_nota_en'] },
              ].map(g => (
                <div key={g.type}>
                  <div className="text-xs font-bold text-gray-500 mb-2">{g.type} betalingsnota</div>
                  <div className={`grid gap-3 ${form.nota_per_taal ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}>
                    {(form.nota_per_taal ? g.keys : [g.keys[0]]).map(k => {
                      const lang = k.endsWith('_nl') ? 'NL' : k.endsWith('_fr') ? 'FR' : 'EN';
                      return (
                        <div key={k}>
                          {form.nota_per_taal && <label className="text-[10px] text-gray-400">{lang}</label>}
                          <textarea className="field-input text-xs" rows={3} value={form[k] || ''} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))} data-testid={`nota-${k}`} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-gray-400">Gebruik {'{vervaldag}'} voor vervaldatum, {'{ogm}'} voor gestructureerde mededeling, {'{document_nr}'} voor documentnummer.</p>
            </div>
          </div>
          {/* Voettekst per documenttype */}
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Voettekst betalingsinstructies (PDF)</span>
              <span className="text-[10px] text-gray-400">Verschijnt onderaan elk document</span>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Pas de tekst aan die onderaan elke PDF verschijnt in de gekleurde informatiebox. Laat leeg om het blok te verbergen.</p>
              {[
                { key: 'factuur_voettekst', label: 'Factuur', kleur: '#002FA7', bg: '#F0F4FF', textCol: '#002FA7', title: 'Betalingsinstructies' },
                { key: 'offerte_voettekst', label: 'Offerte', kleur: '#D97706', bg: '#FFFBEB', textCol: '#92400E', title: 'Geldigheid' },
                { key: 'cn_voettekst', label: 'Creditnota', kleur: '#DC2626', bg: '#FEF2F2', textCol: '#B91C1C', title: 'Terugbetaling' },
                { key: 'bestelbon_voettekst', label: 'Bestelbon', kleur: '#059669', bg: '#ECFDF5', textCol: '#065F46', title: 'Bestelvoorwaarden' },
                { key: 'leverbon_voettekst', label: 'Leveringsbon', kleur: '#059669', bg: '#ECFDF5', textCol: '#065F46', title: 'Leveringsvoorwaarden' },
              ].map(g => {
                const raw = form[g.key] || '';
                const previewOpen = (form.__preview_open || {})[g.key];
                const sample = {
                  totaal: '€ 1.250,00', vervaldag: '15/05/2026', ogm: '+++002/0260/04745+++',
                  iban: form.iban || 'BE68 5390 0754 7034', bic: form.bic || 'BBRUBEBB',
                  bedrijf: form.naam || 'Uw bedrijf BV', document_nr: 'FAC-2026-0054', datum: '30/04/2026',
                };
                const rendered = Object.keys(sample).reduce((s, k) => s.replaceAll(`{${k}}`, sample[k]), raw);
                const decoratedOgm = g.key === 'factuur_voettekst' && sample.ogm
                  ? rendered.replace(sample.ogm, `<span style="font-family:monospace;font-weight:700;color:#002FA7;letter-spacing:.8px;background:#fff;padding:1px 6px;border:1px solid #C7D2FE">${sample.ogm}</span>`)
                  : rendered;
                const togglePreview = () => setForm(p => ({ ...p, __preview_open: { ...(p.__preview_open || {}), [g.key]: !previewOpen } }));
                return (
                  <div key={g.key}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-1 h-4" style={{ background: g.kleur }} />
                      <label className="text-xs font-bold text-gray-700">{g.label}</label>
                      <button type="button" className="ml-auto text-[10px] font-bold px-2 py-0.5 border border-gray-200 hover:border-[#002FA7] hover:text-[#002FA7] text-gray-500" onClick={togglePreview} data-testid={`preview-btn-${g.key}`}>
                        {previewOpen ? 'Verberg voorbeeld' : 'Voorbeeld ▸'}
                      </button>
                    </div>
                    <textarea className="field-input text-xs w-full" style={{ background: g.bg + '80', minHeight: 60 }} rows={2}
                      value={raw} onChange={e => setForm(p => ({ ...p, [g.key]: e.target.value }))}
                      placeholder={`Bv. Bij wanbetaling is een verwijlintrest van 10% verschuldigd...`}
                      data-testid={`voettekst-${g.key}`} />
                    {previewOpen && raw && (
                      <div className="mt-2" data-testid={`preview-box-${g.key}`}>
                        <div className="text-[10px] text-gray-400 mb-1 uppercase tracking-wider font-bold">Voorbeeld in PDF:</div>
                        <div style={{ padding: '14px 18px', background: g.bg, borderLeft: `3px solid ${g.kleur}` }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: g.textCol, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{g.title}</div>
                          <div style={{ fontSize: 12, color: '#1a1a1a', lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: decoratedOgm }} />
                          {g.key === 'factuur_voettekst' && (
                            <div style={{ fontSize: 11, color: '#4B5563', marginTop: 8, lineHeight: 1.5 }}>
                              Rekening: <strong style={{ fontFamily: 'monospace' }}>{sample.iban}</strong> &middot; BIC: <span style={{ fontFamily: 'monospace' }}>{sample.bic}</span> &middot; Begunstigde: {sample.bedrijf}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {previewOpen && !raw && (
                      <div className="mt-2 text-[11px] text-gray-400 italic" data-testid={`preview-empty-${g.key}`}>Voettekst is leeg — dit blok wordt niet in de PDF getoond.</div>
                    )}
                  </div>
                );
              })}
              <div className="p-3 bg-gray-50 border border-gray-200 text-[11px] text-gray-600">
                <div className="font-bold mb-1">Beschikbare placeholders:</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 font-mono text-[10px]">
                  <span><code className="text-[#002FA7]">{'{totaal}'}</code> — eindbedrag</span>
                  <span><code className="text-[#002FA7]">{'{vervaldag}'}</code> — vervaldatum</span>
                  <span><code className="text-[#002FA7]">{'{ogm}'}</code> — gestructureerde mededeling</span>
                  <span><code className="text-[#002FA7]">{'{iban}'}</code> — uw IBAN</span>
                  <span><code className="text-[#002FA7]">{'{bic}'}</code> — uw BIC</span>
                  <span><code className="text-[#002FA7]">{'{bedrijf}'}</code> — uw bedrijfsnaam</span>
                  <span><code className="text-[#002FA7]">{'{document_nr}'}</code> — nummer</span>
                  <span><code className="text-[#002FA7]">{'{datum}'}</code> — uitgiftedatum</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Facturen & Creditnota's ── */}
      {tab === 'verzendkosten' && (
        <div className="space-y-5">
          <div className="section-card" data-testid="verzendkosten-card">
            <div className="section-header"><span className="section-title">🚚 Automatische verzendkosten</span></div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">
                Voeg automatisch een lijn "Verzendkosten" toe aan documenten (offerte, bestelbon, leverbon, factuur, proforma) én webshop-bestellingen
                wanneer het goederen-subtotaal (excl. BTW) onder het drempelbedrag van het land van de klant ligt. Boven de drempel is levering gratis.
                De BTW op de verzendkosten volgt automatisch het document (21% normaal, 0% bij intracommunautair/export).
              </p>
              <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 hover:bg-gray-50 transition-colors">
                <IOSToggle checked={!!form.verzendkosten_actief} onChange={v => setForm(p => ({ ...p, verzendkosten_actief: v }))} size="sm" showLabel={false} data-testid="verzendkosten-toggle" />
                <span className="text-sm font-semibold">Automatische verzendkosten actief</span>
              </label>
              <div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-gray-400 border-b">
                      <th className="py-1.5">Land</th>
                      <th className="py-1.5">Drempel (€ excl. BTW)</th>
                      <th className="py-1.5">Verzendkost (€ excl. BTW)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.verzendkosten_regels || []).map((r, i) => (
                      <tr key={i} className="border-b border-gray-100" data-testid={`vz-regel-${i}`}>
                        <td className="py-1.5 pr-2">
                          <select className="field-input" value={r.land || 'BE'} onChange={e => setForm(p => { const rg = [...p.verzendkosten_regels]; rg[i] = { ...rg[i], land: e.target.value }; return { ...p, verzendkosten_regels: rg }; })} data-testid={`vz-land-${i}`}>
                            {VERZEND_LANDEN.map(l => <option key={l.code} value={l.code}>{l.naam}</option>)}
                          </select>
                        </td>
                        <td className="py-1.5 pr-2"><input type="number" step="0.01" className="field-input" value={r.drempel ?? ''} onChange={e => setForm(p => { const rg = [...p.verzendkosten_regels]; rg[i] = { ...rg[i], drempel: e.target.value }; return { ...p, verzendkosten_regels: rg }; })} placeholder="bv. 1500" data-testid={`vz-drempel-${i}`} /></td>
                        <td className="py-1.5 pr-2"><input type="number" step="0.01" className="field-input" value={r.kost ?? ''} onChange={e => setForm(p => { const rg = [...p.verzendkosten_regels]; rg[i] = { ...rg[i], kost: e.target.value }; return { ...p, verzendkosten_regels: rg }; })} placeholder="bv. 50" data-testid={`vz-kost-${i}`} /></td>
                        <td className="py-1.5 text-right"><button className="text-red-400 hover:text-red-600" onClick={() => setForm(p => ({ ...p, verzendkosten_regels: p.verzendkosten_regels.filter((_, j) => j !== i) }))} data-testid={`vz-del-${i}`}><Trash size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="mt-2 text-[#002FA7] text-xs font-semibold hover:underline" onClick={() => setForm(p => ({ ...p, verzendkosten_regels: [...(p.verzendkosten_regels || []), { land: 'BE', drempel: '1500', kost: '' }] }))} data-testid="vz-regel-toevoegen">+ Land toevoegen</button>
              </div>
              {!!form.verzendkosten_actief && (form.verzendkosten_regels || []).length === 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-900">Voeg minstens één land-regel toe, anders worden nergens verzendkosten toegevoegd.</div>
              )}
              <p className="text-[11px] text-gray-400">Voorbeeld: België drempel €1500 / kost €50 → een bestelling van €1200 aan een Belgische klant krijgt automatisch €50 verzendkosten; vanaf €1500 is de levering gratis. De lijn kan op elk document handmatig aangepast of verwijderd worden.</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'facturen' && (
        <div className="space-y-5">
          <div className="section-card">
            <div className="section-header"><span className="section-title">Factuurinstellingen</span></div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div><label className="form-label">Standaard betalingstermijn (dagen)</label><input type="number" className="field-input" value={form.betalingstermijn_std} onChange={u('betalingstermijn_std')} data-testid="fac-termijn" /></div>
                <div className="flex items-center gap-2 self-end pb-2">
                  <IOSToggle checked={!!form.termijn_einde_maand} onChange={v => setForm(p => ({ ...p, termijn_einde_maand: v }))} size="sm" showLabel={false} />
                  <span className="text-xs">Verleng betalingstermijn tot einde maand</span>
                </div>
              </div>
            </div>
          </div>
          <div className="section-card">
            <div className="section-header"><span className="section-title">Betalingskorting (kassavermindering)</span></div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-400">Geef uw klant een korting als ze de factuur vroegtijdig betalen. Na het verstrijken van de kortingstermijn betaalt de klant het volledige bedrag.</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div><label className="form-label">Kortingstermijn (dagen)</label><input type="number" className="field-input" value={form.betalingskorting_dagen} onChange={u('betalingskorting_dagen')} placeholder="bv. 8 of 10" data-testid="fac-korting-dagen" /></div>
                <div><label className="form-label">Kortingspercentage (%)</label><input type="number" step="0.1" className="field-input" value={form.betalingskorting_pct} onChange={u('betalingskorting_pct')} placeholder="bv. 2" data-testid="fac-korting-pct" /></div>
              </div>
              {form.betalingskorting_dagen && form.betalingskorting_pct && (
                <div className="p-3 bg-[#F0F4FF] border border-[#002FA7]/10 text-xs text-[#002FA7]" data-testid="korting-preview">
                  Klanten die binnen <strong>{form.betalingskorting_dagen} dagen</strong> betalen krijgen <strong>{form.betalingskorting_pct}% korting</strong>. Na {form.betalingskorting_dagen} dagen betalen zij het volledige bedrag.
                </div>
              )}
            </div>
          </div>
          <div className="section-card" data-testid="epc-qr-card">
            <div className="section-header"><span className="section-title inline-flex items-center gap-2">📱 EPC-QR (SEPA betaal-QR)</span></div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">
                SEPA-betaal-QR voor bankapps met een overschrijvingsscanner. Ondersteuning verschilt per bank en scanner; dit is geen Bancontact-QR.
              </p>
              <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={!!form.epc_qr_actief}
                  onChange={uBool('epc_qr_actief')}
                  data-testid="epc-qr-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold" data-testid="epc-qr-status">{form.epc_qr_actief ? 'EPC-QR is ingeschakeld' : 'EPC-QR activeren'}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5" data-testid="epc-qr-availability">
                    {form.epc_qr_actief ? 'Alleen bij geldige betaalgegevens en een positief te betalen bedrag.' : 'QR-code voor overschrijvingen in EUR.'}
                  </div>
                </div>
              </label>
              <EpcQrValidation form={form} testId="invoice-epc-validation" />
            </div>
          </div>
          <div className="section-card">
            <div className="section-header"><span className="section-title">Opties</span></div>
            <div className="divide-y divide-gray-100">
              {[
                { key: 'ogm_actief', label: 'Gestructureerde mededeling (OGM)', desc: 'Genereer automatisch een Belgische gestructureerde mededeling (+++XXX/XXXX/XXXXX+++) op facturen en creditnota\'s' },
                { key: 'concept_facturen', label: 'Concept facturen', desc: 'Maak eerst een concept aan voordat de factuur definitief wordt' },
                { key: 'proforma_facturen', label: 'Proforma facturen', desc: 'Gebruik proforma facturen als voorlopige documenten' },
                { key: 'leverdatum', label: 'Leverdatum / prestatie tonen', desc: 'Toon een extra veld voor leveringsdatum op documenten' },
                { key: 'duplicaat_verloren', label: 'Duplicaat bij verloren', desc: 'Markeer opnieuw afgedrukte documenten als DUPLICAAT' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50">
                  <div><div className="text-sm font-medium">{opt.label}</div><div className="text-[10px] text-gray-400">{opt.desc}</div></div>
                  <input type="checkbox" checked={form[opt.key] ?? true} onChange={e => setForm(p => ({ ...p, [opt.key]: e.target.checked }))} className="w-5 h-5 accent-[#059669]" data-testid={`fac-${opt.key}`} />
                </label>
              ))}
            </div>
          </div>
          <div className="section-card">
            <div className="section-header"><span className="section-title">Afdrukopties</span></div>
            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Betalingen afdrukken op factuur</label>
                <div className="space-y-1.5">
                  {[{ v: 'altijd', l: 'Altijd' }, { v: 'indien_betaald', l: 'Alleen indien (deels) betaald' }, { v: 'nooit', l: 'Nooit' }].map(o => (
                    <label key={o.v} className="flex items-center gap-2 text-xs cursor-pointer"><input type="radio" name="betalingen_afdrukken" checked={form.betalingen_afdrukken === o.v} onChange={() => setForm(p => ({ ...p, betalingen_afdrukken: o.v }))} className="accent-[#D97706]" />{o.l}</label>
                  ))}
                </div>
              </div>
              <div>
                <label className="form-label">"Voldaan" vermelden</label>
                <div className="space-y-1.5">
                  {[{ v: 'altijd', l: 'Altijd bij betaalde facturen' }, { v: 'nooit', l: 'Nooit vermelden' }].map(o => (
                    <label key={o.v} className="flex items-center gap-2 text-xs cursor-pointer"><input type="radio" name="voldaan_vermelden" checked={form.voldaan_vermelden === o.v} onChange={() => setForm(p => ({ ...p, voldaan_vermelden: o.v }))} className="accent-[#D97706]" />{o.l}</label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Betalingsherinneringen ── */}
      {tab === 'herinneringen' && (
        <div className="space-y-5">
          <div className="section-card">
            <div className="section-header"><span className="section-title">Timing</span></div>
            <div className="p-5">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div><label className="form-label">1e herinnering na (dagen)</label><input type="number" className="field-input" value={form.herinnering_1_dagen} onChange={u('herinnering_1_dagen')} data-testid="her-1-dagen" /></div>
                <div><label className="form-label">2e herinnering na (dagen)</label><input type="number" className="field-input" value={form.herinnering_2_dagen} onChange={u('herinnering_2_dagen')} data-testid="her-2-dagen" /></div>
                <div><label className="form-label">Aanmaning na (dagen)</label><input type="number" className="field-input" value={form.aanmaning_dagen} onChange={u('aanmaning_dagen')} data-testid="her-aanm-dagen" /></div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <IOSToggle checked={!!form.auto_herinnering_email} onChange={v => setForm(p => ({ ...p, auto_herinnering_email: v }))} size="md" showLabel={false} />
                <span className="text-xs">Automatisch herinneringen versturen per e-mail</span>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-gray-500">
                  <Lightning size={13} weight="bold" className="inline mr-1 text-[#D97706]" />
                  Automatische cron draait <strong>dagelijks om 08:00</strong> (Brussel). U kunt het nu handmatig testen:
                </div>
                <button className="btn btn-secondary btn-sm" onClick={async () => {
                  setSyncMsg('Reminder cron bezig...');
                  try {
                    await API.post('/cron/run-reminders');
                    setSyncMsg('Reminder cron uitgevoerd');
                  } catch (e) {
                    setSyncMsg(`Mislukt: ${e.response?.data?.detail || 'Fout'}`);
                  }
                  setTimeout(() => setSyncMsg(''), 5000);
                }} data-testid="cron-run-btn">
                  <ArrowsClockwise size={13} weight="bold" /> Nu uitvoeren
                </button>
              </div>
              {syncMsg && <div className="mt-2 p-2 bg-blue-50 text-blue-700 text-xs" data-testid="cron-msg">{syncMsg}</div>}
            </div>
          </div>

          <div className="section-card" data-testid="bh-auto-section">
            <div className="section-header"><span className="section-title">⚙ Automatische Betalingsherinnering (PDF, samengevoegd per klant)</span></div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Wanneer geactiveerd, controleert de server dagelijks welke klanten vervallen facturen hebben. Zijn er meerdere openstaande facturen bij dezelfde klant, dan worden deze <strong>samengevoegd in 1 PDF-brief</strong> (net als bij de handmatige knop) met alle facturen in detail + het totaal te betalen. Het niveau escaleert automatisch (1e → 2e → aanmaning) en wordt per klant maximaal 1x per niveau verstuurd.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <IOSToggle checked={!!form.bh_auto_actief} onChange={v => setForm(p => ({ ...p, bh_auto_actief: v }))} size="md" showLabel={false} testid="bh-auto-actief-cb" />
                <span className="text-xs font-medium">Automatische betalingsherinneringen (PDF) activeren</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div><label className="form-label">1e herinnering na (dagen te laat)</label><input type="number" className="field-input" value={form.bh_auto_dagen_1} onChange={u('bh_auto_dagen_1')} data-testid="bh-auto-dagen-1" /></div>
                <div><label className="form-label">2e herinnering na (dagen te laat)</label><input type="number" className="field-input" value={form.bh_auto_dagen_2} onChange={u('bh_auto_dagen_2')} data-testid="bh-auto-dagen-2" /></div>
                <div><label className="form-label">Aanmaning na (dagen te laat)</label><input type="number" className="field-input" value={form.bh_auto_dagen_3} onChange={u('bh_auto_dagen_3')} data-testid="bh-auto-dagen-3" /></div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-gray-500">
                  <Lightning size={13} weight="bold" className="inline mr-1 text-[#D97706]" />
                  Cron draait <strong>dagelijks om 08:15</strong> (Brussel). Vereist SendGrid + klant-e-mailadres.
                </div>
                <button className="btn btn-secondary btn-sm" onClick={async () => {
                  setSyncMsg('Betalingsherinnering-cron bezig...');
                  try {
                    await API.post('/cron/run-bh-auto');
                    setSyncMsg('Betalingsherinnering-cron uitgevoerd');
                  } catch (e) {
                    setSyncMsg(`Mislukt: ${e.response?.data?.detail || 'Fout'}`);
                  }
                  setTimeout(() => setSyncMsg(''), 5000);
                }} data-testid="bh-auto-run-btn">
                  <ArrowsClockwise size={13} weight="bold" /> Nu uitvoeren
                </button>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header"><span className="section-title">Automatische herhaalde facturen</span></div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Wanneer ingeschakeld, verwerkt de server elke ochtend automatisch alle actieve herhaalde-factuur-schemas waarvan de volgende-datum bereikt is. Facturen worden aangemaakt én — indien geactiveerd per schema — ook automatisch per e-mail naar de klant verstuurd.
              </p>
              <label className="flex items-center gap-2 mb-3">
                <IOSToggle checked={!!form.cron_schemas_auto} onChange={v => setForm(p => ({ ...p, cron_schemas_auto: v }))} size="md" showLabel={false} testid="cron-schemas-auto-cb" />
                <span className="text-xs font-medium">Schemas automatisch laten verwerken door de cron</span>
              </label>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-gray-500">
                  <Lightning size={13} weight="bold" className="inline mr-1 text-[#D97706]" />
                  Cron draait <strong>dagelijks om 07:00</strong> (Brussel). Je kan ook nu handmatig je eigen schemas verwerken:
                </div>
                <button className="btn btn-secondary btn-sm" onClick={async () => {
                  setSyncMsg('Schemas verwerken...');
                  try {
                    const r = await API.post('/cron/run-schemas');
                    const d = r.data || {};
                    setSyncMsg(`${d.created || 0} nieuwe facturen aangemaakt (van ${d.due || 0} vervallen schemas${d.errors ? `, ${d.errors} fouten` : ''}).`);
                  } catch (e) {
                    setSyncMsg(`Mislukt: ${e.response?.data?.detail || 'Fout'}`);
                  }
                  setTimeout(() => setSyncMsg(''), 8000);
                }} data-testid="cron-schemas-run-btn">
                  <ArrowsClockwise size={13} weight="bold" /> Nu verwerken
                </button>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header"><span className="section-title">Dagelijkse samenvattingsmail</span></div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Ontvang elke ochtend een overzicht van wat er <strong>automatisch</strong> voor je gedaan werd: opgestelde betalingsherinneringen, verzonden herinneringen en aangemaakte herhaalde facturen. Zelfde stijl als Billit/MyFact — één blik en je weet alles.
              </p>
              <label className="flex items-center gap-2 mb-3">
                <IOSToggle checked={!!form.daily_summary_email} onChange={v => setForm(p => ({ ...p, daily_summary_email: v }))} size="md" showLabel={false} testid="daily-summary-cb" />
                <span className="text-xs font-medium">Dagelijkse samenvattingsmail activeren</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Ontvanger (mijn e-mail)</label>
                  <input className="field-input" value={form.daily_summary_to} onChange={u('daily_summary_to')} placeholder={form.email || 'mijn@bedrijf.be (leeg = bedrijfs-e-mail)'} data-testid="daily-summary-to" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-gray-500">
                  <Lightning size={13} weight="bold" className="inline mr-1 text-[#D97706]" />
                  Mail vertrekt <strong>dagelijks om 08:30</strong> (Brussel), ná herinneringen en schema-verwerking.
                </div>
                <div className="flex gap-2 items-center">
                  <a className="btn btn-secondary btn-sm inline-flex items-center gap-1" href={`${process.env.REACT_APP_BACKEND_URL}/api/cron/preview-summary?demo=1`} target="_blank" rel="noopener noreferrer" data-testid="daily-summary-preview-btn">
                    <Info size={13} weight="bold" /> Preview (voorbeeld)
                  </a>
                  <button className="btn btn-secondary btn-sm" onClick={async () => {
                    setSyncMsg('Samenvatting versturen...');
                    try {
                      const r = await API.post('/cron/run-summary');
                      const d = r.data || {};
                      setSyncMsg(d.ok ? `Samenvatting verzonden (${d.reminders || 0} herinneringen, ${d.schemas || 0} schemas).` : 'Geen wijzigingen vandaag — geen mail verzonden.');
                    } catch (e) {
                      setSyncMsg(`Mislukt: ${e.response?.data?.detail || 'Fout'}`);
                    }
                    setTimeout(() => setSyncMsg(''), 8000);
                  }} data-testid="daily-summary-test-btn">
                    <ArrowsClockwise size={13} weight="bold" /> Verstuur nu
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header"><span className="section-title">Weekrapport (maandagmorgen)</span></div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Start elke week met een <strong>financiële vogelvlucht</strong>: omzet van de voorbije 7 dagen, vervallen facturen, inkomende betalingen, nieuwe klanten en je <strong>Top 5 openstaande facturen</strong>. Perfect voor maandagochtend — direct inzicht zonder in te loggen.
              </p>
              <label className="flex items-center gap-2 mb-3">
                <IOSToggle checked={!!form.weekly_report_email} onChange={v => setForm(p => ({ ...p, weekly_report_email: v }))} size="md" showLabel={false} testid="weekly-report-cb" />
                <span className="text-xs font-medium">Weekrapport activeren</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Ontvanger (mijn e-mail)</label>
                  <input className="field-input" value={form.weekly_report_to} onChange={u('weekly_report_to')} placeholder={form.daily_summary_to || form.email || 'mijn@bedrijf.be (leeg = bedrijfs-e-mail)'} data-testid="weekly-report-to" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-gray-500">
                  <Lightning size={13} weight="bold" className="inline mr-1 text-[#D97706]" />
                  Rapport vertrekt <strong>elke maandag om 07:45</strong> (Brussel).
                </div>
                <div className="flex gap-2 items-center">
                  <a className="btn btn-secondary btn-sm inline-flex items-center gap-1" href={`${process.env.REACT_APP_BACKEND_URL}/api/cron/preview-weekly?demo=1`} target="_blank" rel="noopener noreferrer" data-testid="weekly-report-preview-btn">
                    <Info size={13} weight="bold" /> Preview (voorbeeld)
                  </a>
                  <button className="btn btn-secondary btn-sm" onClick={async () => {
                    setSyncMsg('Weekrapport versturen...');
                    try {
                      const r = await API.post('/cron/run-weekly');
                      const d = r.data || {};
                      setSyncMsg(d.ok ? `Weekrapport verzonden (omzet €${(d.omzet || 0).toFixed(2)}, ${d.aantal_facturen || 0} facturen, ${d.nieuwe_klanten || 0} nieuwe klanten).` : 'Geen SendGrid-key of afzender — kijk na in Integraties.');
                    } catch (e) {
                      setSyncMsg(`Mislukt: ${e.response?.data?.detail || 'Fout'}`);
                    }
                    setTimeout(() => setSyncMsg(''), 10000);
                  }} data-testid="weekly-report-test-btn">
                    <ArrowsClockwise size={13} weight="bold" /> Verstuur nu
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header"><span className="section-title">Automatische kwartaal BTW-aangifte mail</span></div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Elk kwartaal ontvangt u automatisch een e-mail met de klaargezette Intervat XML-bestanden (BTW-aangifte + IC-opgave). Verzonden op de <strong>15e van januari/april/juli/oktober</strong> om 09:00. Controleer de cijfers, log in op MyMinFin en upload.
              </p>
              <label className="flex items-center gap-2 mb-3">
                <IOSToggle checked={!!form.auto_btw_reminder} onChange={v => setForm(p => ({ ...p, auto_btw_reminder: v }))} size="md" showLabel={false} testid="auto-btw-cb" />
                <span className="text-xs font-medium">Automatische kwartaal BTW-mail activeren</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Ontvanger e-mail</label>
                  <input className="field-input" value={form.btw_reminder_email} onChange={u('btw_reminder_email')} placeholder="boekhouder@voorbeeld.be (leeg = bedrijfs-e-mail)" data-testid="btw-reminder-email" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-gray-500">
                  <Info size={13} weight="bold" className="inline mr-1 text-[#002FA7]" />
                  Vereist een werkende <strong>SendGrid API key</strong> (Instellingen → Integraties). Test nu met een specifiek kwartaal:
                </div>
                <div className="flex gap-1 items-center">
                  <select className="field-input text-xs" style={{width: 80}} id="test-kwartaal" data-testid="test-kwartaal">
                    <option value="1">Q1</option><option value="2">Q2</option><option value="3">Q3</option><option value="4">Q4</option>
                  </select>
                  <input type="number" className="field-input text-xs" style={{width: 90}} id="test-jaar" defaultValue={new Date().getFullYear()} data-testid="test-jaar" />
                  <button className="btn btn-secondary btn-sm" onClick={async () => {
                    const kw = document.getElementById('test-kwartaal').value;
                    const jr = document.getElementById('test-jaar').value;
                    setSyncMsg('BTW-mail versturen...');
                    try {
                      const r = await API.post('/cron/send-btw-reminder', { kwartaal: parseInt(kw), jaar: parseInt(jr) });
                      setSyncMsg(r.data?.message || (r.data?.ok ? 'Verzonden!' : 'Niet verzonden'));
                    } catch (e) {
                      setSyncMsg(`Mislukt: ${e.response?.data?.detail || 'Fout'}`);
                    }
                    setTimeout(() => setSyncMsg(''), 8000);
                  }} data-testid="test-btw-mail">
                    <Lightning size={13} weight="bold" /> Verstuur nu
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="section-card">
              <div className="section-header"><span className="section-title">Interesten</span></div>
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <IOSToggle checked={!!form.interest_actief} onChange={v => setForm(p => ({ ...p, interest_actief: v }))} size="md" showLabel={false} />
                  <span className="text-sm font-medium">Interesten aanrekenen</span>
                </div>
                {form.interest_actief && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="form-label">Interestvoet %</label><input type="number" className="field-input" value={form.interest_pct} onChange={u('interest_pct')} data-testid="her-interest-pct" /></div>
                      <div><label className="form-label">Berekening</label><select className="field-input" value={form.interest_berekening} onChange={u('interest_berekening')}><option value="maandelijks">Maandelijks</option><option value="jaarlijks">Jaarlijks</option></select></div>
                    </div>
                    <div><label className="form-label">Minimumbedrag factuur</label><input type="number" className="field-input" value={form.interest_min} onChange={u('interest_min')} /></div>
                  </>
                )}
              </div>
            </div>
            <div className="section-card">
              <div className="section-header"><span className="section-title">Aanmaningskosten</span></div>
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <IOSToggle checked={!!form.aanmaningskosten_actief} onChange={v => setForm(p => ({ ...p, aanmaningskosten_actief: v }))} size="md" showLabel={false} />
                  <span className="text-sm font-medium">Aanmaningskosten aanrekenen</span>
                </div>
                {form.aanmaningskosten_actief && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="form-label">Bedrag</label><input type="number" className="field-input" value={form.aanmaningskost_bedrag} onChange={u('aanmaningskost_bedrag')} /></div>
                    <div><label className="form-label">Vanaf</label><select className="field-input" value={form.aanmaningskosten_vanaf} onChange={u('aanmaningskosten_vanaf')}><option value="herinnering_2">2e herinnering</option><option value="aanmaning">Aanmaning</option></select></div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="section-card">
            <div className="section-header"><span className="section-title">Herinneringsteksten</span></div>
            <div className="p-5 space-y-4">
              {[
                { key: 'herinnering_1_tekst', label: '1e Herinnering' },
                { key: 'herinnering_2_tekst', label: '2e Herinnering' },
                { key: 'aanmaning_tekst', label: 'Aanmaning (laatste waarschuwing)' },
              ].map(h => (
                <div key={h.key}>
                  <label className="form-label">{h.label}</label>
                  <textarea className="field-input text-xs" rows={4} value={form[h.key] || ''} onChange={u(h.key)} data-testid={`her-${h.key}`} />
                </div>
              ))}
              <p className="text-[10px] text-gray-400">Variabelen: {'{document_nr}'} = factuurnummer, {'{outstanding}'} = openstaand bedrag, {'{ogm}'} = mededeling, {'{bedrijfsnaam}'} = uw bedrijfsnaam.</p>
            </div>
          </div>
        </div>
      )}


      {/* ── TAB: Boekjaar & Nummering ── */}
      {tab === 'nummering' && (
        <div className="space-y-5">
          {/* Boekjaar + Format in one row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="section-card">
              <div className="section-header"><span className="section-title">Boekjaar</span></div>
              <div className="p-5">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="form-label">Jaar</label>
                    <div className="field-input bg-gray-50 text-center font-bold text-[#002FA7]" data-testid="inst-boekjaar-jaar">{curYear}</div>
                  </div>
                  <div>
                    <label className="form-label">Startmaand</label>
                    <select className="field-input" value={bjMaand}
                      onChange={e => setForm(p => ({ ...p, boekjaar_start: `${e.target.value}-${bjDag}` }))}
                      data-testid="inst-boekjaar-maand">
                      {MAAND_NAMEN.map((naam, i) => <option key={i} value={String(i + 1).padStart(2, '0')}>{naam}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Startdag</label>
                    <select className="field-input" value={bjDag}
                      onChange={e => setForm(p => ({ ...p, boekjaar_start: `${bjMaand}-${e.target.value}` }))}
                      data-testid="inst-boekjaar-dag">
                      {Array.from({length: 28}, (_, i) => String(i + 1).padStart(2, '0')).map(d =>
                        <option key={d} value={d}>{d}</option>
                      )}
                    </select>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-3">Elk jaar starten documentnummers automatisch opnieuw.</p>
              </div>
            </div>

            <div className="section-card">
              <div className="section-header"><span className="section-title">Nummerformaat</span></div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="form-label">Scheidingsteken</label>
                    <select className="field-input" value={sep} onChange={uFmt('separator')} data-testid="num-separator">
                      {SEPARATORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Nummerbreedte</label>
                    <select className="field-input" value={String(pad)} onChange={uFmt('padding')} data-testid="num-padding">
                      {PADDINGS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="p-3 bg-[#F0F4FF] border border-[#002FA7]/10">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400">Voorbeeld</span>
                  <div className="font-mono font-bold text-[#002FA7] text-lg mt-1" data-testid="num-format-preview">
                    FAC{sep}{curYear}{sep}{'0'.repeat(Math.max(0, pad - 1))}1
                  </div>
                </div>
                <PaddingInconsistencyCheck />
              </div>
            </div>
          </div>

          {/* Nummering tabel */}
          <div className="section-card">
            <div className="section-header"><span className="section-title"><Hash size={16} className="inline mr-1.5" />Document Nummering</span></div>
            <div className="section-body">
              <table className="data-table">
                <thead><tr><th style={{width:130}}>Document</th><th style={{width:90}}>Prefix</th><th className="right" style={{width:70}}>Huidig</th><th>Volgend</th><th style={{width:130}}>Wijzig</th></tr></thead>
                <tbody>
                  {DOC_TYPES.map(dt => {
                    const st = numStatus[dt.code] || {};
                    const curPrefix = form.nummering?.[dt.code]?.prefix || dt.code;
                    return (
                      <tr key={dt.code}>
                        <td><span className="flex items-center gap-2"><span className="w-2 h-2 inline-block" style={{background:dt.color}} /><span className="font-semibold text-sm">{dt.label}</span></span></td>
                        <td><input className="field-input text-xs font-mono" style={{width:70,padding:'3px 6px'}} value={curPrefix} onChange={e => setPrefix(dt.code, e.target.value.toUpperCase())} data-testid={`num-prefix-${dt.code}`} /></td>
                        <td className="right"><span className="font-bold text-[#002FA7]" data-testid={`num-current-${dt.code}`}>{st.huidig || 0}</span></td>
                        <td><span className="font-mono text-xs text-gray-500" data-testid={`num-preview-${dt.code}`}>{st.voorbeeld || (dt.code === 'KL' ? `${curPrefix}${sep}${'0'.repeat(Math.max(0,pad-1))}1` : `${curPrefix}${sep}${curYear}${sep}${'0'.repeat(Math.max(0,pad-1))}1`)}</span></td>
                        <td><div className="flex items-center gap-1">
                          <input type="number" className="field-input text-xs" style={{width:55,padding:'3px 6px'}} value={resetVals[dt.code]||''} onChange={e => setResetVals(p=>({...p,[dt.code]:e.target.value}))} placeholder={st.volgend||1} data-testid={`num-reset-input-${dt.code}`} />
                          <button className="btn btn-ghost btn-sm" onClick={()=>resetCounter(dt.code)} title="Toepassen" data-testid={`num-reset-btn-${dt.code}`}><ArrowCounterClockwise size={13} className="text-[#D97706]" /></button>
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-4 py-3 text-xs text-gray-400 border-t border-gray-100">Wijzig prefix en klik "Opslaan". Reset het volgnummer met het icoon.</div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Boekingsregels ── */}
      {tab === 'boekingsregels' && (
        <div className="space-y-5" data-testid="boekingsregels-tab">
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Automatische boekingsregels</span>
              <button className="btn btn-primary btn-sm" onClick={() => setBrForm({ leverancier_id: '', grootboekrekening: '', categorie: '', btw_type: 'Binnenland' })} data-testid="add-br-btn">
                <Plus size={13} weight="bold" /> Nieuwe regel
              </button>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-4">
                Koppel een leverancier automatisch aan een grootboekrekening, categorie en BTW-type. Bij een inkomende factuur worden deze velden automatisch ingevuld zodra de leverancier herkend wordt.
              </p>

              {brForm !== null && (
                <div className="p-4 mb-4 bg-[#F0F4FF] border border-[#002FA7]/30" data-testid="br-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="form-label">Leverancier *</label>
                      <select className="field-input" value={brForm.leverancier_id || ''} onChange={e => {
                        const k = klanten.find(x => x.id === e.target.value);
                        setBrForm(p => ({ ...p, leverancier_id: e.target.value, leverancier_naam: k?.bedrijf || `${k?.voorn || ''} ${k?.achtern || ''}`.trim() }));
                      }} data-testid="br-leverancier">
                        <option value="">-- Kies leverancier --</option>
                        {klanten.map(k => (
                          <option key={k.id} value={k.id}>{k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim()} {k.btwnr ? `· ${k.btwnr}` : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Grootboekrekening</label>
                      <input className="field-input font-mono" value={brForm.grootboekrekening || ''} onChange={e => setBrForm(p => ({ ...p, grootboekrekening: e.target.value }))} placeholder="bv. 61000" data-testid="br-gb" />
                    </div>
                    <div>
                      <label className="form-label">Categorie</label>
                      <select className="field-input" value={brForm.categorie || ''} onChange={e => setBrForm(p => ({ ...p, categorie: e.target.value }))} data-testid="br-cat">
                        <option value="">-- Geen --</option>
                        {['Overige diensten','Kantoorbenodigdheden','Telecom','Huur','Verzekeringen','Onderhoud','Marketing','Software','Transport','Voeding','Andere'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">BTW-type</label>
                      <select className="field-input" value={brForm.btw_type || 'Binnenland'} onChange={e => setBrForm(p => ({ ...p, btw_type: e.target.value }))} data-testid="br-btw">
                        {['Binnenland','EU','Buiten EU','Verlegd'].map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn btn-primary btn-sm" onClick={saveBr} disabled={brSaving} data-testid="br-save">
                      {brSaving ? 'Opslaan...' : (brForm.id ? 'Regel bijwerken' : 'Regel toevoegen')}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setBrForm(null)}>Annuleren</button>
                  </div>
                </div>
              )}

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Leverancier</th>
                    <th>Grootboekrekening</th>
                    <th>Categorie</th>
                    <th>BTW-type</th>
                    <th style={{width: 100}}></th>
                  </tr>
                </thead>
                <tbody>
                  {boekingsregels.length === 0 && (
                    <tr><td colSpan="5" className="text-center text-gray-400 py-8">Nog geen boekingsregels aangemaakt</td></tr>
                  )}
                  {boekingsregels.map(r => {
                    const k = klanten.find(x => x.id === r.leverancier_id);
                    const naam = r.leverancier_naam || (k ? (k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim()) : '—');
                    return (
                      <tr key={r.id} data-testid={`br-row-${r.id}`}>
                        <td className="font-medium text-sm">{naam}</td>
                        <td className="font-mono text-xs">{r.grootboekrekening || '—'}</td>
                        <td className="text-xs">{r.categorie || '—'}</td>
                        <td className="text-xs">{r.btw_type || 'Binnenland'}</td>
                        <td>
                          <div className="flex gap-1">
                            <button className="btn btn-ghost btn-sm" onClick={() => setBrForm({ ...r })} data-testid={`br-edit-${r.id}`}><PencilSimple size={12} /></button>
                            <button className="btn btn-ghost btn-sm text-red-500" onClick={() => delBr(r.id)} data-testid={`br-del-${r.id}`}><Trash size={12} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Categorieën → Boekhouding ── */}
      {tab === 'categorien' && (
        <div className="space-y-5" data-testid="categorien-tab">
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Categorie → Grootboekrekening mapping</span>
              <button className="btn btn-ghost btn-sm text-red-500" onClick={resetCatMappings} data-testid="reset-cat-btn">
                <ArrowsClockwise size={13} /> Alles resetten
              </button>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Bij AI-uitlezen of handmatige categoriewijziging wordt automatisch de juiste grootboekrekening ingevuld.
                De standaardwaarden volgen het Belgische Minimum Algemeen Rekeningenstelsel (MAR). Afwijkingen per bedrijf zijn mogelijk — pas aan waar nodig.
              </p>
              <div className="mb-3">
                <input className="field-input" placeholder="Zoek categorie of grootboekrekening..." value={catFilter} onChange={e => setCatFilter(e.target.value)} data-testid="cat-search" />
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Categorie</th>
                      <th style={{width: 140}}>Grootboekrekening</th>
                      <th style={{width: 130}}>BTW</th>
                      <th style={{width: 130}}>Aftrekbaarheid</th>
                      <th style={{width: 80}}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catMappings.filter(m => !catFilter || m.categorie.toLowerCase().includes(catFilter.toLowerCase()) || m.grootboekrekening.includes(catFilter)).map(m => (
                      <tr key={m.categorie} data-testid={`cat-row-${m.categorie}`}>
                        <td className="text-xs">{m.categorie}</td>
                        <td>
                          <input className="field-input font-mono text-sm" defaultValue={m.grootboekrekening} key={`gb-${m.categorie}-${m.grootboekrekening}`}
                            onBlur={e => { if (e.target.value !== m.grootboekrekening) saveCatField(m.categorie, 'grootboekrekening', e.target.value); }}
                            disabled={catSaving} data-testid={`cat-gb-${m.categorie}`} />
                        </td>
                        <td>
                          <select className="field-input text-sm" value={m.btw_pct} onChange={e => saveCatField(m.categorie, 'btw_pct', parseInt(e.target.value))} disabled={catSaving} data-testid={`cat-btw-${m.categorie}`}>
                            <option value={-1}>Vrijgesteld</option>
                            <option value={0}>0%</option>
                            <option value={6}>6%</option>
                            <option value={12}>12%</option>
                            <option value={21}>21%</option>
                          </select>
                        </td>
                        <td>
                          <select className="field-input text-sm" value={m.aftrekbaarheid} onChange={e => saveCatField(m.categorie, 'aftrekbaarheid', parseInt(e.target.value))} disabled={catSaving} data-testid={`cat-aft-${m.categorie}`}>
                            <option value={100}>100% (volledig)</option>
                            <option value={69}>69% (restaurant)</option>
                            <option value={50}>50% (personenwagen)</option>
                            <option value={0}>0% (privé/boete)</option>
                          </select>
                        </td>
                        <td>
                          {m.is_default ? (
                            <span className="text-[10px] text-gray-400 font-bold">Standaard</span>
                          ) : (
                            <span className="text-[10px] text-[#D97706] font-bold">Aangepast</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {catMappings.length === 0 && (
                      <tr><td colSpan="5" className="text-center text-gray-400 py-8">Laden...</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400 mt-3">
                💡 Wijzigingen worden automatisch opgeslagen. BTW "Vrijgesteld" voor verzekeringen, lonen, onroerende huur. Aftrekbaarheid 50% voor personenwagens, 69% voor restaurantkosten.
              </p>
            </div>
          </div>

          {/* Eigen categorieën */}
          <div className="section-card" data-testid="custom-cat-card">
            <div className="section-header">
              <span className="section-title">Eigen categorieën</span>
              <button className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={() => setCustomCatModal({ open: true, edit: null })} data-testid="add-custom-cat-btn">
                <Plus size={13} weight="bold" /> Nieuwe toevoegen
              </button>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Voeg je eigen categorieën toe bovenop de 98 voorgedefinieerde. Ze verschijnen automatisch in de dropdown bij inkomende facturen met jouw eigen grootboekrekening, BTW % en aftrekbaarheid.
              </p>
              {customCats.length === 0 ? (
                <div className="p-6 text-center bg-gray-50 border border-dashed border-gray-300 text-xs text-gray-500">
                  Je hebt nog geen eigen categorieën.
                  <button className="block mt-2 mx-auto text-[#002FA7] font-semibold hover:underline" onClick={() => setCustomCatModal({ open: true, edit: null })}>+ Maak je eerste eigen categorie</button>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>Groep</th>
                      <th style={{width: 130}}>Grootboek</th>
                      <th style={{width: 80}}>BTW %</th>
                      <th style={{width: 100}}>Aftrekbaar %</th>
                      <th style={{width: 90}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {customCats.map(c => (
                      <tr key={c.id} data-testid={`custom-cat-row-${c.id}`}>
                        <td className="font-medium text-sm">{c.naam}</td>
                        <td className="text-xs text-gray-500">{c.groep || 'Eigen categorieën'}</td>
                        <td className="font-mono text-xs">{c.grootboekrekening || '—'}</td>
                        <td className="text-xs">{c.btw_pct}%</td>
                        <td className="text-xs">{c.aftrekbaarheid}%</td>
                        <td>
                          <div className="flex gap-1">
                            <button className="btn btn-ghost btn-sm" onClick={() => setCustomCatModal({ open: true, edit: c })} data-testid={`edit-custom-cat-${c.id}`}>
                              <PencilSimple size={12} />
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={async () => {
                              if (!window.confirm(`Categorie "${c.naam}" verwijderen?`)) return;
                              await API.delete(`/categorie-custom/${c.id}`);
                              loadCustomCats();
                            }} data-testid={`del-custom-cat-${c.id}`}>
                              <Trash size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Leverancier-regels (Leer van correcties) */}
          <div className="section-card" data-testid="supplier-rules-card">
            <div className="section-header">
              <span className="section-title flex items-center gap-2">
                <span className="text-lg">🧠</span> Leer van mijn correcties — Leverancier-regels
              </span>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                Emergent leert automatisch je voorkeuren per leverancier. Wanneer je <strong>2 keer</strong> dezelfde waarde instelt voor een veld (categorie, grootboekrekening, BTW% of aftrekbaarheid) wordt dat veld geactiveerd in een persoonlijke regel. Toekomstige facturen van die leverancier krijgen automatisch jouw voorkeuren — velden worden <strong>afzonderlijk</strong> geleerd.
              </p>

              {/* Globale auto-goedkeuren instellingen */}
              <div className="mb-4 p-3 bg-[#FFF7ED] border border-[#FDBA74]">
                <div className="flex items-start gap-3">
                  <div className="text-xl leading-none">⚡</div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-[#9A3412] mb-1">Auto-goedkeuren voor vertrouwde leveranciers</div>
                    <div className="flex items-center gap-2 mb-2">
                      <IOSToggle checked={!!form.auto_goedkeuren_enabled} onChange={v => setForm(p => ({ ...p, auto_goedkeuren_enabled: v }))} size="md" showLabel={false} testid="auto-approve-cb" />
                      <span className="text-xs font-medium text-[#9A3412]">Automatisch goedkeuren na AI-parse als de regel minstens <strong>drempel</strong> keer is toegepast</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <label className="text-[11px] text-[#9A3412] font-semibold">Drempel:</label>
                      <input type="number" min="2" max="50" className="field-input" style={{width: 80}} value={form.auto_goedkeuren_drempel || 5} onChange={e => setForm(p => ({ ...p, auto_goedkeuren_drempel: parseInt(e.target.value) || 5 }))} data-testid="auto-approve-drempel" />
                      <span className="text-[11px] text-[#9A3412]">correcties (standaard: 5)</span>
                    </div>
                    <div className="text-[11px] text-[#9A3412] mt-2 leading-relaxed">
                      💡 <strong>Voorbeeld</strong>: na 5 correcties wordt elke nieuwe Proximus-factuur direct op "Goedgekeurd" gezet na AI-parse, met categorie, grootboek en aftrekbaarheid al ingevuld. Je hoeft enkel nog te betalen.
                    </div>
                  </div>
                </div>
              </div>
              {supplierRules.length === 0 ? (
                <div className="p-6 text-center bg-gray-50 border border-dashed border-gray-300 text-xs text-gray-500">
                  Nog geen regels geleerd. Pas gewoon categorieën aan tijdens het verwerken — Emergent herinnert het voor de toekomst.
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Leverancier</th>
                      <th>Categorie</th>
                      <th style={{width: 90}}>Grootboek</th>
                      <th style={{width: 60}}>BTW</th>
                      <th style={{width: 70}}>Aftrek</th>
                      <th style={{width: 80}}>Correcties</th>
                      <th style={{width: 90}}>Status</th>
                      <th style={{width: 80}} title="Auto-goedkeuren na AI-parse">Auto-OK</th>
                      <th style={{width: 110}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierRules.map(r => {
                      const drempel = form.auto_goedkeuren_drempel || 5;
                      const qualifies = form.auto_goedkeuren_enabled && r.correctie_count >= drempel && r.categorie && (r.auto_goedkeuren !== false);
                      return (
                      <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                        <td className="font-medium text-sm">{r.leverancier_naam}</td>
                        <td className="text-xs">{r.categorie || <span className="text-gray-300">—</span>}</td>
                        <td className="text-xs font-mono">{r.grootboekrekening || <span className="text-gray-300">—</span>}</td>
                        <td className="text-xs">{r.btw_pct !== undefined && r.btw_pct !== null ? `${r.btw_pct}%` : <span className="text-gray-300">—</span>}</td>
                        <td className="text-xs">{r.aftrekbaarheid !== undefined && r.aftrekbaarheid !== null ? `${r.aftrekbaarheid}%` : <span className="text-gray-300">—</span>}</td>
                        <td className="text-xs text-gray-600">{r.correctie_count}×</td>
                        <td>
                          {r.actief ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>Actief
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500">
                              Leerfase
                            </span>
                          )}
                        </td>
                        <td>
                          {qualifies ? (
                            <div className="flex items-center gap-1" title="Schakel auto-goedkeuren uit voor deze leverancier">
                              <IOSToggle checked={r.auto_goedkeuren !== false} onChange={async v => {
                                await API.put(`/leverancier-regels/${r.id}`, { auto_goedkeuren: v });
                                loadSupplierRules();
                              }} size="sm" showLabel={false} testid={`auto-ok-${r.id}`} />
                            </div>
                          ) : (
                            <span className="text-[10px] text-gray-300" title={`Nog ${Math.max(0, drempel - r.correctie_count)} correcties tot auto-goedkeuren`}>
                              {r.correctie_count >= drempel ? '—' : `${r.correctie_count}/${drempel}`}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <button className="btn btn-ghost btn-sm text-xs" onClick={async () => { await API.put(`/leverancier-regels/${r.id}`, { actief: !r.actief }); loadSupplierRules(); }} data-testid={`toggle-rule-${r.id}`} title={r.actief ? 'Uitschakelen' : 'Activeren'}>
                              {r.actief ? 'Stop' : 'Start'}
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={async () => {
                              if (!window.confirm(`Regel voor "${r.leverancier_naam}" verwijderen?`)) return;
                              await API.delete(`/leverancier-regels/${r.id}`);
                              loadSupplierRules();
                            }} data-testid={`del-rule-${r.id}`}>
                              <Trash size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 text-[11px] text-purple-800 leading-relaxed">
                <strong>💡 Hoe het werkt:</strong><br />
                • <strong>Per veld onafhankelijk</strong>: categorie, grootboek, BTW% en aftrekbaarheid worden elk apart geleerd<br />
                • <strong>Keer 1</strong>: je past een veld aan → waarde opgeslagen in leerfase<br />
                • <strong>Keer 2</strong>: zelfde waarde opnieuw → dat veld wordt automatisch <strong>actief</strong><br />
                • <strong>Voorbeeld</strong>: bij Proximus altijd aftrekbaarheid 75% instellen → na 2 keer wordt 75% standaard voor alle Proximus-facturen (zonder AI-tussenkomst)
              </div>
            </div>
          </div>

          {/* Modal: Eigen categorie toevoegen/bewerken */}          {customCatModal.open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCustomCatModal({ open: false, edit: null })} data-testid="custom-cat-modal">
              <div className="bg-white shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-3 border-b font-bold text-gray-800">{customCatModal.edit ? 'Categorie bewerken' : 'Nieuwe categorie'}</div>
                <div className="p-5 space-y-3">
                  <div>
                    <label className="form-label">Naam *</label>
                    <input className="field-input w-full" value={customCatForm.naam} onChange={e => setCustomCatForm(p => ({ ...p, naam: e.target.value }))} placeholder="Bv. Abonnement koffiemachine" data-testid="custom-cat-naam" autoFocus />
                  </div>
                  <div>
                    <label className="form-label">Groep</label>
                    <input className="field-input w-full" value={customCatForm.groep} onChange={e => setCustomCatForm(p => ({ ...p, groep: e.target.value }))} placeholder="Eigen categorieën" data-testid="custom-cat-groep" list="existing-groups" />
                    <datalist id="existing-groups">
                      <option value="Eigen categorieën" />
                      <option value="Gebouwen / lokalen / kantoren / terreinen" />
                      <option value="Algemene kosten" />
                      <option value="Investeringen" />
                      <option value="Personeel / uitzendkrachten" />
                      <option value="Administratieve formaliteiten" />
                      <option value="Onderaannemingen" />
                      <option value="(Handels)goederen" />
                      <option value="Huur" />
                      <option value="Erelonen / commissies" />
                      <option value="Verzekeringen" />
                      <option value="Sociale bijdragen" />
                    </datalist>
                  </div>
                  <div>
                    <label className="form-label">Grootboekrekening</label>
                    <input className="field-input w-full font-mono" value={customCatForm.grootboekrekening} onChange={e => setCustomCatForm(p => ({ ...p, grootboekrekening: e.target.value }))} placeholder="Bv. 613000" data-testid="custom-cat-gb" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="form-label">BTW %</label>
                      <select className="field-input w-full" value={customCatForm.btw_pct} onChange={e => setCustomCatForm(p => ({ ...p, btw_pct: e.target.value }))} data-testid="custom-cat-btw">
                        <option value="21">21% (standaard)</option>
                        <option value="12">12%</option>
                        <option value="6">6%</option>
                        <option value="0">0% (vrijgesteld)</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Aftrekbaar %</label>
                      <select className="field-input w-full" value={customCatForm.aftrekbaarheid} onChange={e => setCustomCatForm(p => ({ ...p, aftrekbaarheid: e.target.value }))} data-testid="custom-cat-aft">
                        <option value="100">100% (volledig)</option>
                        <option value="69">69% (restaurant)</option>
                        <option value="50">50% (auto)</option>
                        <option value="35">35%</option>
                        <option value="0">0%</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
                  <button className="btn btn-sm" onClick={() => setCustomCatModal({ open: false, edit: null })}>Annuleren</button>
                  <button className="btn btn-primary btn-sm" onClick={async () => {
                    if (!customCatForm.naam.trim()) { alert('Naam is verplicht'); return; }
                    try {
                      if (customCatModal.edit) {
                        await API.put(`/categorie-custom/${customCatModal.edit.id}`, customCatForm);
                      } else {
                        await API.post('/categorie-custom', customCatForm);
                      }
                      setCustomCatModal({ open: false, edit: null });
                      loadCustomCats();
                    } catch (e) {
                      alert(e.response?.data?.detail || 'Opslaan mislukt');
                    }
                  }} data-testid="save-custom-cat-btn">
                    {customCatModal.edit ? 'Bewaren' : 'Toevoegen'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: E-mail Inbox ── */}
      {tab === 'inbox' && (
        <div className="space-y-5" data-testid="inbox-tab">
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Uw persoonlijke factuur-inbox</span>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">
                Laat leveranciers hun facturen rechtstreeks naar dit e-mailadres sturen, of stel een automatische doorstuurregel in vanuit uw eigen mailbox. Alle bijlagen (PDF, XML, afbeelding) worden automatisch opgenomen als <strong>Inkomende Facturen</strong> en klaargezet voor AI-uitlezing.
              </p>

              {inbox && (
                <div className="bg-gradient-to-r from-[#F0F4FF] to-white p-5 border-l-4 border-[#002FA7]">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-bold">Uw inbox-adres</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <code className="text-lg font-mono font-bold text-[#002FA7] bg-white px-3 py-2 border border-gray-200" data-testid="inbox-address">
                      {inbox.full_address}
                    </code>
                    <button className="btn btn-primary btn-sm" onClick={copyInbox} data-testid="inbox-copy">
                      <Copy size={13} weight="bold" /> {inboxCopied ? 'Gekopieerd!' : 'Kopieer'}
                    </button>
                    <button className="btn btn-ghost btn-sm text-red-500" onClick={regenerateInbox} data-testid="inbox-regen">
                      <ArrowsClockwise size={13} /> Nieuw adres
                    </button>
                  </div>
                  <div className="mt-3 text-xs text-gray-500">
                    <CheckCircle size={12} weight="bold" className="inline mr-1 text-green-600" />
                    <strong>{inbox.emails_ontvangen || 0}</strong> facturen ontvangen via e-mail
                  </div>
                </div>
              )}

              <details className="mt-3">
                <summary className="text-sm font-bold text-[#002FA7] cursor-pointer">Hoe werkt het? (setup-instructies)</summary>
                <div className="mt-3 space-y-3 pl-3 border-l-2 border-[#002FA7]/20 text-xs text-gray-600">
                  <div>
                    <strong className="text-gray-800">Optie 1 — Leverancier rechtstreeks</strong>
                    <p className="mt-1">Geef dit adres door aan uw leveranciers. Elke factuur die zij versturen komt automatisch in uw Inkomende Facturen te staan.</p>
                  </div>
                  <div>
                    <strong className="text-gray-800">Optie 2 — Doorstuurregel in Outlook/Gmail</strong>
                    <p className="mt-1">Maak een regel aan die alle binnenkomende facturen automatisch doorstuurt naar <code className="font-mono bg-gray-100 px-1">{inbox?.full_address}</code>. Meest gangbare criteria: onderwerp bevat "factuur" of "invoice", of bijlage is PDF.</p>
                  </div>
                  <div>
                    <strong className="text-gray-800">Optie 3 — Peppol / E-invoicing</strong>
                    <p className="mt-1">Voor officiële elektronische facturen (UBL/Peppol) kunt u onze E-Invoicing integratie activeren onder Bedrijf → E-Invoicing.</p>
                  </div>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-800">
                    <Warning size={14} className="inline mr-1" weight="bold" />
                    Ontvangt u spam op dit adres? Klik op <em>"Nieuw adres"</em> om een nieuw uniek inbox-adres te genereren. Het oude adres werkt dan niet meer.
                  </div>
                </div>
              </details>
            </div>
          </div>

          {/* ── Sub-sectie: Mail Hub mailboxen + handtekeningen ── */}
          <div className="section-card" data-testid="instellingen-mail-hub-sectie">
            <div className="section-header">
              <span className="section-title">📨 Verzend-mailbox (Mail Hub)</span>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">
                Koppel je eigen e-mailaccount (Outlook, Gmail, Combell, ...) zodat alle mails vanuit BillFac via <strong>jouw eigen mailbox</strong> verstuurd worden — verschijnen in je echte "Verzonden"-folder, met DKIM/SPF van je eigen domein. Inkomende factuur-PDFs worden bovendien automatisch geïmporteerd.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                  <div className="font-bold text-purple-700 mb-1">📥 Mailboxen koppelen</div>
                  <div className="text-purple-600 mb-2">IMAP/SMTP voor Outlook, Gmail, Combell, Hostnet, Telenet, Proximus, ...</div>
                  <a href="/mail" className="btn btn-primary btn-sm inline-flex items-center gap-1">
                    Open Mail Hub →
                  </a>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="font-bold text-blue-700 mb-1">📝 E-mail handtekening</div>
                  <div className="text-blue-600 mb-2">Per mailbox een handtekening die automatisch onderaan elke verzonden mail komt te staan.</div>
                  <a href="/mail" className="text-blue-700 hover:underline text-xs">
                    Beheer via Mail Hub → Mailboxen → ⚙ Bewerken
                  </a>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <div className="font-bold text-green-700 mb-1">🤖 Auto-routing facturen</div>
                  <div className="text-green-600 mb-2">Inkomende factuur-PDFs gaan direct naar /inkomende-facturen + OCR.</div>
                  <a href="/inkomende-documenten" className="text-green-700 hover:underline text-xs">
                    Bekijk inkomende facturen →
                  </a>
                </div>
              </div>
              <div className="text-[11px] text-gray-400 pt-2 border-t border-gray-100">
                💡 Mail Hub is een Pro-feature. Tracking-pixel, click-tracking, AI-classificatie, 7 toon-opties (Casual/Vriendelijk/Professioneel/Formeel/Kordaat/Bestelling/Aanmaning) × 4 talen (NL/FR/EN/DE), versleepbare kolommen, slim contactenboek.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Functies (feature flags + rol-toegang) ── */}
      {tab === 'functies' && <Navigate to="/instellingen/functies" replace />}
      {tab === 'functie-details' && <FunctiesPanel />}

      {/* ── TAB: Kassa → Klant-display ── */}
      {tab === 'kassa_display' && <KassaDisplayPanel />}

      {/* ── TAB: Kassa → Kassaticket ── */}
      {tab === 'kassa_ticket' && <KassaTicketPanel />}

      {/* ── TAB: Kassa → Prijsgroepen ── */}
      {tab === 'kassa_prijsgroepen' && <KassaPrijsgroepenPanel />}

      {/* ── TAB: Boekhouding → Leverbon ── */}
      {tab === 'leverbon' && <LeverbonPanel />}

      {tab === 'chauffeurs' && <ChauffeursPanel />}
      {tab === 'verkopers' && <VerkopersPanel />}
      {tab === 'commissie-matrix' && (hasPerm('commissies_beheren') ? (
        <CommissieMatrix startOpen />
      ) : (
        <div className="p-6 bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="geen-toegang-commissie-matrix">
          🔒 Geen toegang — deze sectie vereist machtiging <strong>"Commissies beheren"</strong>. Vraag een admin om deze machtiging toe te kennen.
        </div>
      ))}
      {tab === 'email_templates' && <EmailTemplatesPanel />}
      {tab === 'whatsapp' && (
        <div className="space-y-4 max-w-3xl" data-testid="tab-whatsapp">
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-base font-bold text-gray-900">📱 WhatsApp meldingen</div>
                <div className="text-xs text-gray-500 mt-0.5">Krijg een directe melding op je telefoon zodra een nieuwe bestelbon binnenkomt via Verkoop onderweg.</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold">{form.whatsapp_actief ? 'AAN' : 'UIT'}</span>
                <IOSToggle checked={form.whatsapp_actief} onChange={(v) => setForm(p => ({ ...p, whatsapp_actief: v }))} testid="inst-whatsapp-actief" />
              </div>
            </div>
            <div className="border-t border-gray-100 pt-3 mt-3">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">1. Provider</div>
              <div className="flex gap-2 mb-3">
                {[
                  { v: 'callmebot', label: 'CallMeBot (gratis)', desc: 'snel + gratis, ideaal voor 1-2 ontvangers' },
                  { v: 'twilio', label: 'Twilio (professional)', desc: 'eigen nummer, productie-grade' },
                ].map(opt => (
                  <button key={opt.v} type="button"
                    className={`flex-1 p-3 border-2 text-left transition ${form.whatsapp_provider === opt.v ? 'border-[#002FA7] bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    onClick={() => setForm(p => ({ ...p, whatsapp_provider: opt.v }))}
                    data-testid={`inst-whatsapp-provider-${opt.v}`}>
                    <div className="text-sm font-bold">{opt.label}</div>
                    <div className="text-[11px] text-gray-500">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {form.whatsapp_provider === 'callmebot' && (
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">2. CallMeBot setup</div>
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 mb-3 leading-relaxed">
                  <strong>Eerste keer setup (5 min, eenmalig per ontvanger):</strong><br />
                  1. Voeg <strong>+34 644 51 95 23</strong> toe als contact in WhatsApp op de telefoon waar je meldingen wil ontvangen<br />
                  2. Stuur deze tekst (exact): <span className="font-mono bg-white px-1.5 py-0.5">I allow callmebot to send me messages</span><br />
                  3. Je krijgt binnen 2 minuten een bericht terug met een <strong>API key</strong> (~7 cijfers)<br />
                  4. Plak die hieronder + het ontvanger-nummer in E.164 formaat (bv. <span className="font-mono">+32479123456</span>)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group">
                    <label className="form-label">CallMeBot API Key</label>
                    <input className="field-input" value={form.whatsapp_callmebot_apikey} onChange={u('whatsapp_callmebot_apikey')} placeholder="bv. 1234567" data-testid="inst-whatsapp-callmebot-key" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ontvanger nummer(s) <span className="text-gray-400 text-[10px]">— meerdere met komma</span></label>
                    <input className="field-input" value={form.whatsapp_recipients} onChange={u('whatsapp_recipients')} placeholder="+32479123456" data-testid="inst-whatsapp-recipients" />
                  </div>
                </div>
              </div>
            )}

            {form.whatsapp_provider === 'twilio' && (
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">2. Twilio setup</div>
                <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 mb-3 leading-relaxed">
                  <strong>Setup:</strong> Maak gratis account op <a href="https://www.twilio.com" target="_blank" rel="noreferrer" className="underline font-semibold">twilio.com</a>. Voor sandbox-testen: ga naar Console → Messaging → Try it out → WhatsApp en volg de instructies. Voor productie: vraag goedkeuring voor je eigen Belgisch nummer (1-3 dagen via Meta).
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group col-span-2">
                    <label className="form-label">Account SID</label>
                    <input className="field-input font-mono" value={form.whatsapp_twilio_sid} onChange={u('whatsapp_twilio_sid')} placeholder="AC..." data-testid="inst-whatsapp-twilio-sid" />
                  </div>
                  <div className="form-group col-span-2">
                    <label className="form-label">Auth Token</label>
                    <input type="password" className="field-input font-mono" value={form.whatsapp_twilio_token} onChange={u('whatsapp_twilio_token')} placeholder="••••••••" data-testid="inst-whatsapp-twilio-token" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sender (WhatsApp from)</label>
                    <input className="field-input" value={form.whatsapp_twilio_sender} onChange={u('whatsapp_twilio_sender')} placeholder="whatsapp:+14155238886" data-testid="inst-whatsapp-twilio-sender" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ontvanger nummer(s)</label>
                    <input className="field-input" value={form.whatsapp_recipients} onChange={u('whatsapp_recipients')} placeholder="+32479123456" data-testid="inst-whatsapp-recipients" />
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-gray-100 pt-3 mt-3 flex gap-2 items-center">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={!form.whatsapp_actief || (!form.whatsapp_callmebot_apikey && form.whatsapp_provider === 'callmebot') || (!form.whatsapp_twilio_sid && form.whatsapp_provider === 'twilio') || !form.whatsapp_recipients}
                onClick={async () => {
                  // Eerst opslaan zodat backend de huidige config heeft
                  await save();
                  try {
                    const r = await API.post('/whatsapp/test', {});
                    toast.success('WhatsApp test verstuurd', { description: `Verstuurd naar: ${(r.data?.sent || []).join(', ')}` });
                  } catch (e) {
                    toast.error('WhatsApp test mislukt', { description: e?.response?.data?.detail || e.message });
                  }
                }}
                data-testid="inst-whatsapp-test"
              >
                📤 Verstuur test-bericht
              </button>
              <span className="text-[11px] text-gray-500">Slaat je instellingen op en verstuurt direct een testbericht naar je geconfigureerde nummers.</span>
            </div>
          </div>

          {/* TELEGRAM SECTIE */}
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-base font-bold text-gray-900">✈️ Telegram meldingen</div>
                <div className="text-xs text-gray-500 mt-0.5">Snel & gratis alternatief — werkt direct, zonder goedkeuring. Loopt parallel naast WhatsApp.</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold">{form.telegram_actief ? 'AAN' : 'UIT'}</span>
                <IOSToggle checked={form.telegram_actief} onChange={(v) => setForm(p => ({ ...p, telegram_actief: v }))} testid="inst-telegram-actief" />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3 mt-3">
              {/* WAAROM TELEGRAM */}
              <details className="mb-3 group" open>
                <summary className="cursor-pointer text-sm font-bold text-gray-900 flex items-center gap-1.5 hover:text-[#002FA7]">
                  <span className="text-base">💡</span>
                  <span>Waarvoor dient deze instelling?</span>
                  <span className="ml-auto text-gray-400 text-xs group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 ml-6 text-xs text-gray-700 leading-relaxed space-y-1.5">
                  <p>Telegram stuurt je <strong>real-time meldingen</strong> op je telefoon zodra er iets gebeurt in BillFac. Je hoeft niet meer in je dashboard te kijken — je krijgt het direct in je pocket.</p>
                  <p className="text-gray-500"><strong>Voorbeeld-bericht dat je gaat ontvangen:</strong></p>
                  <div className="bg-[#e7f3ff] border border-[#c5deff] rounded-lg p-3 font-mono text-[11px] text-gray-800 whitespace-pre-line">
{`🛒 Nieuwe Bestelbon BB-2026-0042
━━━━━━━━━━━━━━
👤 Janssens NV
📦 5 lijnen
💶 € 1.245,80
👨‍💼 Tom (verkoper)
━━━━━━━━━━━━━━
Bakkerij Jan BV

[📄 Open PDF]  ← inline knop`}
                  </div>
                </div>
              </details>

              {/* WANNEER */}
              <details className="mb-3 group">
                <summary className="cursor-pointer text-sm font-bold text-gray-900 flex items-center gap-1.5 hover:text-[#002FA7]">
                  <span className="text-base">⚡</span>
                  <span>Wanneer krijg je een Telegram-bericht?</span>
                  <span className="ml-auto text-gray-400 text-xs group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 ml-6 text-xs text-gray-700 leading-relaxed space-y-1">
                  <p>Je krijgt automatisch een melding bij:</p>
                  <ul className="list-disc ml-4 space-y-0.5">
                    <li>📋 <strong>Nieuwe offerte</strong> aangemaakt door een verkoper of via de shop</li>
                    <li>🛒 <strong>Nieuwe bestelbon</strong> via je B2B shop of manueel</li>
                    <li>🚚 <strong>Levering klaar / onderweg</strong> (deelleveringen)</li>
                    <li>🧾 <strong>Nieuwe factuur</strong> verzonden naar klant</li>
                    <li>💰 <strong>Betaling ontvangen</strong> (automatische match via bankafschrift)</li>
                  </ul>
                  <p className="mt-2 text-gray-500">📌 Welke types je wil ontvangen kan je filteren onderaan deze sectie ("Types").</p>
                </div>
              </details>

              {/* SETUP 5 STAPPEN */}
              <details className="mb-3 group">
                <summary className="cursor-pointer text-sm font-bold text-gray-900 flex items-center gap-1.5 hover:text-[#002FA7]">
                  <span className="text-base">🛠️</span>
                  <span>Setup — 5 minuten éénmalig (klik om uit te klappen)</span>
                  <span className="ml-auto text-gray-400 text-xs group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 ml-6 space-y-2">
                  <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 leading-relaxed rounded-lg">
                    <div className="font-bold mb-1.5">Stap 1 — Maak een Telegram-bot</div>
                    Open <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="underline font-semibold">@BotFather</a> in Telegram (web of app) → klik <strong>"Start"</strong>.
                  </div>
                  <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 leading-relaxed rounded-lg">
                    <div className="font-bold mb-1.5">Stap 2 — Stuur het commando</div>
                    Typ <span className="font-mono bg-white px-1.5 py-0.5">/newbot</span> → BotFather vraagt om een naam (bv. <em>"Bakkerij Jan Bot"</em>) en een username eindigend op <span className="font-mono">_bot</span> (bv. <span className="font-mono">bakkerij_jan_bot</span>).
                  </div>
                  <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 leading-relaxed rounded-lg">
                    <div className="font-bold mb-1.5">Stap 3 — Kopieer je Bot Token</div>
                    BotFather geeft je een <strong>Bot Token</strong> (~46 tekens, formaat <span className="font-mono">123456789:ABC-DEF...</span>) → <strong>plak dit in het veld hieronder</strong>.
                    <div className="mt-1.5 text-[10px] text-blue-700 italic">⚠️ Deel deze token met NIEMAND — wie 'm heeft kan berichten in jouw naam sturen.</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 leading-relaxed rounded-lg">
                    <div className="font-bold mb-1.5">Stap 4 — Open je bot en klik Start</div>
                    BotFather geeft een link <span className="font-mono">https://t.me/jouw_bot</span> → klik erop → klik <strong>"Start"</strong> in de bot. Dat geeft de bot toestemming om jou berichten te sturen.
                  </div>
                  <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 leading-relaxed rounded-lg">
                    <div className="font-bold mb-1.5">Stap 5 — Klik "Ontdek mijn chat-ID"</div>
                    Klik op de knop <strong>"🔍 Ontdek mijn chat-ID"</strong> hieronder → BillFac haalt automatisch je chat-ID op uit Telegram.
                  </div>
                  <div className="bg-green-50 border border-green-200 text-green-900 text-xs p-3 leading-relaxed rounded-lg">
                    <div className="font-bold mb-1.5">✅ Stap 6 — Test</div>
                    Klik <strong>"📤 Verstuur test-bericht"</strong> — als je een berichtje krijgt op Telegram, ben je klaar!
                  </div>
                </div>
              </details>

              {/* TIPS */}
              <details className="mb-3 group">
                <summary className="cursor-pointer text-sm font-bold text-gray-900 flex items-center gap-1.5 hover:text-[#002FA7]">
                  <span className="text-base">💎</span>
                  <span>Tips & FAQs</span>
                  <span className="ml-auto text-gray-400 text-xs group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 ml-6 text-xs text-gray-700 space-y-2 leading-relaxed">
                  <div>
                    <strong>👥 Meerdere mensen tegelijk bereiken (verkoopteam)?</strong>
                    <div className="text-gray-600">Maak een Telegram-<strong>groepschat</strong>, voeg je bot toe, en stuur in die groep "/start". Gebruik dan de <em>group chat-ID</em> (begint met <span className="font-mono">-100</span>). Of typ meerdere chat-IDs gescheiden door een komma.</div>
                  </div>
                  <div>
                    <strong>📵 Berichten tijdelijk uitzetten?</strong>
                    <div className="text-gray-600">Zet de schakelaar rechtsboven op <strong>UIT</strong> — je instellingen blijven bewaard.</div>
                  </div>
                  <div>
                    <strong>💰 Kost het iets?</strong>
                    <div className="text-gray-600">Volledig <strong>gratis</strong>, ook bij grote volumes. Telegram limiteert wel tot ~30 berichten/seconde — meer dan genoeg.</div>
                  </div>
                  <div>
                    <strong>📲 Werkt het ook op iPhone / Android / Desktop?</strong>
                    <div className="text-gray-600">Ja — Telegram werkt op alle platforms. Je kan op meerdere toestellen tegelijk berichten ontvangen.</div>
                  </div>
                  <div>
                    <strong>🔄 Verschil met WhatsApp?</strong>
                    <div className="text-gray-600">Telegram is meteen actief (geen goedkeuringsperiode), bots zijn gratis en programmable. WhatsApp Business heeft meer professionele uitstraling maar vereist provider-account (Twilio of CallMeBot).</div>
                  </div>
                </div>
              </details>

              <div className="grid grid-cols-2 gap-3">
                <div className="form-group col-span-2">
                  <label className="form-label">Bot Token <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    className="field-input font-mono"
                    value={form.telegram_bot_token}
                    onChange={u('telegram_bot_token')}
                    placeholder="123456789:ABC-DEF..."
                    data-testid="inst-telegram-token"
                  />
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Chat-ID(s) <span className="text-gray-400 text-[10px]">— meerdere met komma; negatieve ID = group-chat</span></label>
                  <input
                    className="field-input font-mono"
                    value={form.telegram_chat_ids}
                    onChange={u('telegram_chat_ids')}
                    placeholder="123456789"
                    data-testid="inst-telegram-chatids"
                  />
                </div>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-3 mt-3 flex gap-2 items-center flex-wrap">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={!form.telegram_bot_token}
                onClick={async () => {
                  await save();
                  try {
                    const r = await API.post('/telegram/discover-chats');
                    const chats = r.data?.chats || [];
                    if (chats.length === 0) {
                      toast.warning('Geen chats ontdekt', { description: 'Open je bot in Telegram en stuur eerst /start' });
                      return;
                    }
                    const ids = chats.map(c => c.chat_id).join(', ');
                    setForm(p => ({ ...p, telegram_chat_ids: ids }));
                    toast.success(`${chats.length} chat(s) gevonden`, {
                      description: chats.map(c => `${c.naam} (${c.chat_id})`).join(' · '),
                      duration: 8000,
                    });
                  } catch (e) {
                    toast.error('Ontdekken mislukt', { description: e?.response?.data?.detail || e.message });
                  }
                }}
                data-testid="inst-telegram-discover"
              >
                🔍 Ontdek mijn chat-ID
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={!form.telegram_actief || !form.telegram_bot_token || !form.telegram_chat_ids}
                onClick={async () => {
                  await save();
                  try {
                    const r = await API.post('/telegram/test', {});
                    toast.success('Telegram test verstuurd', { description: `Naar chat(s): ${(r.data?.sent || []).join(', ')}` });
                  } catch (e) {
                    toast.error('Telegram test mislukt', { description: e?.response?.data?.detail || e.message });
                  }
                }}
                data-testid="inst-telegram-test"
              >
                📤 Verstuur test-bericht
              </button>
            </div>
          </div>
        </div>
      )}
      {tab === 'gebruikers' && <GebruikersPage />}
      {tab === 'eenheden' && <EenhedenPanel />}
      {tab === 'prullenbak' && <PrullenbakPanel />}
      {tab === 'packing' && <PackingConfigPanel />}
      {tab === 'pdf_layouts' && (
        <div className="space-y-4" data-testid="pdf-layouts-tab-wrapper">
          {/* Globale PDF-opties (verhuisd vanuit Bijlagen-tab) */}
          <div className="section-card" data-testid="pdf-epc-qr-card">
            <div className="section-header"><span className="section-title inline-flex items-center gap-2">📱 QR-code voor betaling (EPC-QR)</span></div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500" data-testid="pdf-epc-qr-compatibility">SEPA-betaal-QR voor bankapps met een overschrijvingsscanner. Ondersteuning verschilt per bank en scanner; dit is geen Bancontact-QR.</p>
              <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={!!form.epc_qr_actief}
                  onChange={e => setForm(p => ({ ...p, epc_qr_actief: e.target.checked }))}
                  data-testid="pdf-epc-qr-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold" data-testid="pdf-epc-qr-status">{form.epc_qr_actief ? 'QR-code voor betaling is ingeschakeld' : 'QR-code voor betaling activeren'}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5" data-testid="pdf-epc-qr-availability">
                    {form.epc_qr_actief ? 'Alleen bij geldige betaalgegevens en een positief te betalen bedrag.' : 'QR-code voor overschrijvingen in EUR.'}
                  </div>
                </div>
              </label>
              <EpcQrValidation form={form} testId="pdf-epc-validation" />
              {form.epc_qr_actief && (
                <div className="border-t border-gray-100 pt-3 mt-3" data-testid="epc-qr-per-type">
                  <div className="text-xs font-semibold text-gray-700 mb-2">QR-code per documenttype</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { k: 'factuur',   l: 'Factuur',     icon: '📄' },
                      { k: 'creditnota', l: 'Creditnota', icon: '↩️' },
                      { k: 'proforma',  l: 'Proforma',    icon: '📑' },
                      { k: 'offerte',   l: 'Offerte',     icon: '✉️' },
                    ].map(t => {
                      const checked = form.epc_qr_per_type?.[t.k] !== false;
                      return (
                        <label
                          key={t.k}
                          className={`flex items-center gap-2 cursor-pointer p-2 border rounded transition-colors ${checked ? 'border-[#002FA7] bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                          data-testid={`epc-qr-type-${t.k}`}
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            data-testid={`epc-qr-type-toggle-${t.k}`}
                            checked={checked}
                            onChange={e => setForm(p => ({
                              ...p,
                              epc_qr_per_type: { ...(p.epc_qr_per_type || {}), [t.k]: e.target.checked },
                            }))}
                          />
                          <span className="text-xs font-medium">{t.icon} {t.l}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-2">
                    Vink uit per type om de QR-code daar weg te laten. Werkt enkel als de globale schakelaar hierboven actief is.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Artikel-foto's op PDF */}
          <div className="section-card" data-testid="pdf-fotos-card">
            <div className="section-header"><span className="section-title inline-flex items-center gap-2">🖼️ Artikel-foto's op document-PDF's</span></div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">Voegt een thumbnail-foto (40×40 px) toe links van elke artikel-omschrijving. Handig bij CMR's en leveringen ("is dit het juiste profiel?"). Werkt enkel voor artikels die een foto hebben; lijnen zonder foto laten de kolom leeg.</p>
              <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={!!form.pdf_toon_artikel_fotos}
                  onChange={e => setForm(p => ({ ...p, pdf_toon_artikel_fotos: e.target.checked }))}
                  data-testid="pdf-fotos-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold">{form.pdf_toon_artikel_fotos ? '✅ Foto\'s actief — kies hieronder per documenttype' : 'Artikel-foto\'s op documenten activeren'}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    Wanneer uitgeschakeld: nooit foto's en nooit bevestigingsvraag bij afdruk. Wanneer actief: foto's verschijnen op de hieronder gekozen document-types.
                  </div>
                </div>
              </label>
              {form.pdf_toon_artikel_fotos && (
                <div className="mt-3 pl-3 pr-3 py-3 bg-blue-50/40 border-l-4 border-[#002FA7]" data-testid="pdf-fotos-types-block">
                  <div className="text-[11px] uppercase font-bold tracking-wider text-[#002FA7] mb-2">Foto's tonen op deze document-types:</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { key: 'factuur', label: 'Factuur' },
                      { key: 'creditnota', label: 'Creditnota' },
                      { key: 'offerte', label: 'Offerte' },
                      { key: 'bestelbon', label: 'Bestelbon' },
                      { key: 'leverbon', label: 'Leverbon' },
                      { key: 'proforma', label: 'Proforma' },
                    ].map(t => {
                      const lijst = Array.isArray(form.pdf_fotos_types)
                        ? form.pdf_fotos_types
                        : ['factuur', 'creditnota', 'offerte', 'bestelbon', 'leverbon', 'proforma'];
                      const actief = lijst.includes(t.key);
                      return (
                        <label key={t.key} className="flex items-center gap-2 cursor-pointer p-2 bg-white border border-gray-200 hover:border-[#002FA7] transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={actief}
                            onChange={e => {
                              const huidig = Array.isArray(form.pdf_fotos_types)
                                ? form.pdf_fotos_types
                                : ['factuur', 'creditnota', 'offerte', 'bestelbon', 'leverbon', 'proforma'];
                              const nieuw = e.target.checked
                                ? [...new Set([...huidig, t.key])]
                                : huidig.filter(k => k !== t.key);
                              setForm(p => ({ ...p, pdf_fotos_types: nieuw }));
                            }}
                            data-testid={`pdf-fotos-type-${t.key}`}
                          />
                          <span className="text-xs font-bold">{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-2 italic">
                    Tip: voor leverbonnen en CMR's is foto-weergave heel handig (chauffeur herkent direct het product). Op facturen blijft het vaak strakker zonder foto's.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bestaande per-type layout-editor (kleuren, kolommen, ...) */}
          <PdfLayoutEditor />
        </div>
      )}

      {tab === 'klanten-vies' && <KlantenViesPanel />}

      {/* ── TAB: Weergave (lettertype + tekstgrootte) ── */}
      {tab === 'weergave' && <WeergavePanel />}

      {/* ── TAB: Integraties ── */}
      {tab === 'integraties' && (
        <>
          <div className="mb-4 p-3 border-2 border-[#2563EB]/30 bg-[#EFF6FF] text-xs text-[#1E40AF]" data-testid="integraties-info-banner">
            💡 <strong>Tip:</strong> u kan hier de API-sleutels invoeren. Om een hele integratie (bv. Mollie, CodaBox, WooCommerce) of document-type (leverbon, creditnota, CMR, ...) voor uw bedrijf <strong>aan of uit te zetten</strong> en zo de sidebar te vereenvoudigen, ga naar <a href="/instellingen?tab=functies" className="font-bold underline hover:text-[#002FA7]">Functies (On/Off)</a>.
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="section-card lg:col-span-2" data-testid="billit-card">
            <div className="section-header">
              <span className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-[#F59E0B] text-white flex items-center justify-center text-xl font-bold">🇧🇪</div>
                <span className="section-title">Billit — Belgisch boekhoud-SaaS + Peppol e-invoicing</span>
              </span>
            </div>
            <div className="p-5">
              <BillitPanel />
            </div>
          </div>

          <div className="section-card lg:col-span-2" data-testid="printnode-card">
            <div className="section-header">
              <span className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-[#10B981] text-white flex items-center justify-center text-xl font-bold">☁️</div>
                <span className="section-title">PrintNode — Cloud remote printing (aanbevolen)</span>
              </span>
            </div>
            <div className="p-5">
              <PrintNodePanel />
            </div>
          </div>

          <div className="section-card lg:col-span-2" data-testid="print-agent-card">
            <div className="section-header">
              <span className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-[#002FA7] text-white flex items-center justify-center text-xl font-bold">🖨</div>
                <span className="section-title">BillFac Print Agent — Remote printing vanaf elke locatie</span>
              </span>
            </div>
            <div className="p-5">
              <PrintAgentPanel />
            </div>
          </div>

          <IntegrationCard Logo={() => <div className="w-10 h-10 rounded bg-[#FF6B0D] text-white flex items-center justify-center text-xl font-bold">📦</div>} title="Sendcloud — Verzendlabels (DPD/bpost/PostNL/UPS/DHL)" status={!!form.sendcloud_public_key && !!form.sendcloud_secret_key}>
            <p className="text-xs text-gray-400">Maak in 1 klik een verzendlabel aan vanaf elke leverbon of telefonische bestelling. Druk het label-PDF direct af op je thermische printer.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren — API-keys ophalen bij Sendcloud</summary>
              <div className="px-3 py-2 space-y-1.5 text-gray-600">
                <div>1. Log in op <a href="https://panel.sendcloud.sc" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">panel.sendcloud.sc</a> (of registreer gratis op <a href="https://www.sendcloud.com" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">sendcloud.com</a>)</div>
                <div>2. Ga naar <strong>Instellingen → Integraties → Sendcloud API</strong></div>
                <div>3. Klik op <strong>"Maak API-key aan"</strong> → kopieer de <strong>Public Key</strong> en <strong>Secret Key</strong></div>
                <div>4. Plak ze hieronder en klik op <strong>"Test verbinding"</strong></div>
                <div className="pt-1 text-amber-700 bg-amber-50 px-2 py-1 rounded">💡 Sendcloud onderhandelt op jouw volume. Voor &lt;25 pakketten/maand kan je beter direct met bpost/DPD werken.</div>
              </div>
            </details>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <label className="form-label">Public Key</label>
                <input
                  className="field-input font-mono text-xs"
                  value={form.sendcloud_public_key || ''}
                  onChange={u('sendcloud_public_key')}
                  placeholder="abcde12345..."
                  data-testid="inst-sendcloud-pub"
                />
              </div>
              <div>
                <label className="form-label">Secret Key</label>
                <input
                  type="password"
                  className="field-input font-mono text-xs"
                  value={form.sendcloud_secret_key || ''}
                  onChange={u('sendcloud_secret_key')}
                  placeholder="••••••••••••"
                  data-testid="inst-sendcloud-sec"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-[#FF6B0D] text-white text-xs font-semibold hover:bg-[#E55A00] disabled:opacity-50"
                disabled={!form.sendcloud_public_key || !form.sendcloud_secret_key}
                onClick={async () => {
                  try {
                    await save();
                    const r = await API.get('/sendcloud/test');
                    alert(`✅ Sendcloud verbonden!\n\nGebruiker: ${r.data.username || '-'}\nE-mail: ${r.data.email || '-'}\nLand: ${r.data.country || '-'}`);
                  } catch (e) {
                    alert('Test mislukt: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="inst-sendcloud-test"
              >
                🔌 Test verbinding
              </button>
              <span className="text-[10px] text-gray-500">Slaat eerst je instellingen op en pingt /api/sendcloud/test.</span>
            </div>
          </IntegrationCard>

          <IntegrationCard Logo={() => <div className="w-10 h-10 rounded bg-[#FFCB05] text-[#0E1116] flex items-center justify-center text-xl font-bold">📮</div>} title="MyParcel — Verzendlabels (PostNL/bpost/DPD/DHL)" status={!!form.myparcel_api_key}>
            <p className="text-xs text-gray-400">Alternatieve verzendprovider, vooral sterk in NL/BE. Klant kan kiezen tussen Sendcloud en MyParcel per pakket.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren — API-key ophalen bij MyParcel</summary>
              <div className="px-3 py-2 space-y-1.5 text-gray-600">
                <div>1. Log in op <a href="https://backoffice.myparcel.nl" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">backoffice.myparcel.nl</a> (NL) of <a href="https://backoffice.myparcel.be" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">backoffice.myparcel.be</a> (BE)</div>
                <div>2. Ga naar <strong>Instellingen → Account → Algemeen</strong></div>
                <div>3. Onderaan staat je <strong>API-sleutel</strong> — kopieer hem (klik op het oogje om hem zichtbaar te maken)</div>
                <div>4. Plak hier en kies het juiste platform → klik <strong>"Test verbinding"</strong></div>
              </div>
            </details>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="form-label">API-sleutel</label>
                <input
                  type="password"
                  className="field-input font-mono text-xs"
                  value={form.myparcel_api_key || ''}
                  onChange={u('myparcel_api_key')}
                  placeholder="••••••••••••"
                  data-testid="inst-myparcel-key"
                />
              </div>
              <div>
                <label className="form-label">Platform</label>
                <select
                  className="field-input"
                  value={form.myparcel_platform || 'be'}
                  onChange={u('myparcel_platform')}
                  data-testid="inst-myparcel-platform"
                >
                  <option value="be">🇧🇪 België</option>
                  <option value="nl">🇳🇱 Nederland</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-[#FFCB05] text-[#0E1116] text-xs font-semibold hover:bg-[#E8B800] disabled:opacity-50"
                disabled={!form.myparcel_api_key}
                onClick={async () => {
                  try {
                    await save();
                    const r = await API.get('/myparcel/test');
                    alert(`✅ MyParcel verbonden!\n\nPlatform: ${r.data.platform === 'nl' ? 'Nederland' : 'België'}\nShop: ${r.data.shop_naam || '-'}\nAantal shops: ${r.data.aantal_shops}`);
                  } catch (e) {
                    alert('Test mislukt: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="inst-myparcel-test"
              >
                🔌 Test verbinding
              </button>
              <span className="text-[10px] text-gray-500">Slaat eerst je instellingen op en pingt /api/myparcel/test.</span>
            </div>
          </IntegrationCard>

          <IntegrationCard Logo={LogoSMTP} title="SMTP — Eigen e-mail-server (aanbevolen)" status={!!form.smtp_host && !!form.smtp_user && !!form.smtp_password}>
            <p className="text-xs text-gray-400">Verstuur facturen via uw eigen e-mailadres (Gmail / Outlook365 / Telenet / Combell / eigen server). Klanten zien u als afzender, niet een externe service.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren — voorbeelden voor populaire providers</summary>
              <div className="px-3 py-2 space-y-2 text-gray-600">
                <div><strong>Gmail / Google Workspace:</strong> host <code className="bg-gray-200 px-1">smtp.gmail.com</code> · poort <code className="bg-gray-200 px-1">587</code> · TLS aan · username = uw Gmail-adres · wachtwoord = <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">App-wachtwoord aanmaken</a> (2FA verplicht)</div>
                <div><strong>Outlook 365 / Office 365:</strong> host <code className="bg-gray-200 px-1">smtp.office365.com</code> · poort <code className="bg-gray-200 px-1">587</code> · TLS aan · username = uw e-mailadres · wachtwoord = <a href="https://account.microsoft.com/security" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">App-wachtwoord</a></div>
                <div><strong>Telenet:</strong> <code className="bg-gray-200 px-1">smtp.telenet.be</code> · 587 · TLS · username = uw Telenet-adres</div>
                <div><strong>Combell / Eigen hosting:</strong> raadpleeg uw provider voor SMTP-host en credentials</div>
              </div>
            </details>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><label className="form-label">SMTP-host</label><input className="field-input" value={form.smtp_host} onChange={u('smtp_host')} placeholder="smtp.gmail.com" data-testid="inst-smtp-host" /></div>
              <div><label className="form-label">Poort</label><input type="number" className="field-input" value={form.smtp_port} onChange={u('smtp_port')} placeholder="587" data-testid="inst-smtp-port" /></div>
              <div className="flex items-end gap-2 text-[11px]">
                <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={form.smtp_tls !== false} onChange={e => setForm(p => ({ ...p, smtp_tls: e.target.checked }))} data-testid="inst-smtp-tls" /><span>STARTTLS (587)</span></label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={!!form.smtp_ssl} onChange={e => setForm(p => ({ ...p, smtp_ssl: e.target.checked }))} data-testid="inst-smtp-ssl" /><span>SSL (465)</span></label>
              </div>
              <div className="col-span-2"><label className="form-label">Gebruikersnaam</label><input className="field-input" value={form.smtp_user} onChange={u('smtp_user')} placeholder="u@bedrijf.be" data-testid="inst-smtp-user" /></div>
              <div className="col-span-2"><label className="form-label">Wachtwoord (app-wachtwoord aanbevolen)</label><input type="password" className="field-input font-mono text-xs" value={form.smtp_password} onChange={u('smtp_password')} placeholder="••••••••••••" data-testid="inst-smtp-password" /></div>
              <div><label className="form-label">From-naam</label><input className="field-input" value={form.smtp_from_name} onChange={u('smtp_from_name')} placeholder={form.bedrijf || 'Uw bedrijf'} data-testid="inst-smtp-from-name" /></div>
              <div><label className="form-label">From-adres</label><input type="email" className="field-input" value={form.smtp_from} onChange={u('smtp_from')} placeholder="facturen@bedrijf.be" data-testid="inst-smtp-from" /></div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-[#059669] text-white text-xs font-semibold hover:bg-[#047857] disabled:opacity-50"
                disabled={!form.smtp_host || !form.smtp_user || !form.smtp_password}
                onClick={async () => {
                  try {
                    // Eerst opslaan zodat de backend de SMTP-config kent
                    await save();
                    const r = await API.post('/email/test', { to: form.email || form.smtp_from });
                    alert(`✅ Testmail verstuurd naar ${form.email || form.smtp_from} via ${(r.data.provider || '').toUpperCase()}`);
                  } catch (e) {
                    alert('Testmail mislukt: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="inst-smtp-test"
              >
                ✉️ Stuur testmail naar mezelf
              </button>
              <span className="text-[10px] text-gray-500">Slaat eerst je instellingen op en stuurt dan een testmail naar {form.email || form.smtp_from || 'jouw profiel-e-mail'}.</span>
            </div>
            <div className="border-t border-gray-100 pt-3 mt-2">
              <div className="text-xs font-semibold text-gray-700 mb-1">🔗 Publieke URLs (vereist voor open-tracking en portaal-link)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="form-label">App-URL (frontend)</label>
                  <input className="field-input font-mono text-xs" value={form.public_app_url} onChange={u('public_app_url')} placeholder="https://app.uwbedrijf.be" data-testid="inst-public-app-url" />
                </div>
                <div>
                  <label className="form-label">Backend-URL (voor tracking-pixel)</label>
                  <input className="field-input font-mono text-xs" value={form.public_backend_url} onChange={u('public_backend_url')} placeholder="https://app.uwbedrijf.be" data-testid="inst-public-backend-url" />
                </div>
              </div>
              <div className="text-[10px] text-gray-500 mt-1">Tip: meestal zijn beide hetzelfde. Wordt gebruikt om <code className="bg-gray-100 px-1">{'{{portaal.link}}'}</code> in mails te genereren en de tracking-pixel-URL te bepalen.</div>
            </div>
          </IntegrationCard>

          <IntegrationCard Logo={LogoSendGrid} title="SendGrid E-mail (alternatief)" status={!!form.sendgrid_key}>
            <p className="text-xs text-gray-400">Verstuur facturen rechtstreeks via e-mail.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li><a href="https://signup.sendgrid.com/" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-medium">Registreer gratis SendGrid account</a> (100 e-mails/dag gratis)</li>
                <li>Verifieer afzender-domein via <a href="https://app.sendgrid.com/settings/sender_auth" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Sender Authentication</a></li>
                <li>Maak een <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">API Key</a> met 'Mail Send' permissie</li>
              </ol>
            </details>
            <div><label className="form-label">API Key</label><input type="password" className="field-input font-mono text-xs" value={form.sendgrid_key} onChange={u('sendgrid_key')} placeholder="SG.xxxxxxxxxxxx" data-testid="inst-sendgrid-key" /></div>
            <div><label className="form-label">Afzender e-mail</label><input type="email" className="field-input" value={form.sender_email} onChange={u('sender_email')} placeholder="facturen@uwbedrijf.be" data-testid="inst-sender-email" /></div>
          </IntegrationCard>

          <IntegrationCard Logo={LogoStripe} title="Stripe Betalingen" status={!!form.stripe_key}>
            <p className="text-xs text-gray-400">Online betaallinks per factuur via creditcard of Bancontact.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li><a href="https://dashboard.stripe.com/register" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-medium">Registreer bij Stripe</a> — doe KYC voor Live modus</li>
                <li>Activeer Bancontact + iDEAL in <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Payment methods</a></li>
                <li>Kopieer <strong>Secret Key</strong> uit <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Developers → API keys</a></li>
              </ol>
              <div className="px-4 pb-2 text-[10px] text-gray-500">Tarieven: 1.4% + €0.25 voor Europese kaarten · <a href="https://stripe.com/be/pricing" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">stripe.com/be/pricing</a></div>
            </details>
            <div><label className="form-label">Secret Key</label><input type="password" className="field-input font-mono text-xs" value={form.stripe_key} onChange={u('stripe_key')} placeholder="sk_live_xxxxxxxxxxxx" data-testid="inst-stripe-key" /></div>
            <p className="text-[10px] text-gray-300">Laat leeg voor test-modus.</p>
          </IntegrationCard>

          <IntegrationCard Logo={LogoMollie} title="Mollie Betalingen" status={!!form.mollie_key}>
            <p className="text-xs text-gray-400">Belgisch alternatief voor online betalingen (iDEAL, Bancontact, KBC).</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li><a href="https://my.mollie.com/dashboard/signup" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-medium">Registreer bij Mollie</a> (KYC vereist voor Live modus)</li>
                <li>Activeer betaalmethodes in <a href="https://my.mollie.com/dashboard/settings/profiles" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Dashboard → Profielen</a></li>
                <li>Kopieer <strong>Live API Key</strong> uit <a href="https://my.mollie.com/dashboard/developers/api-keys" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Developers → API keys</a></li>
              </ol>
              <div className="px-4 pb-2 text-[10px] text-gray-500">Tarieven: <a href="https://www.mollie.com/be/pricing" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">mollie.com/be/pricing</a></div>
            </details>
            <div><label className="form-label">API Key</label><input type="password" className="field-input font-mono text-xs" value={form.mollie_key} onChange={u('mollie_key')} placeholder="live_xxxxxxxxxxxx" data-testid="inst-mollie-key" /></div>
          </IntegrationCard>

          <IntegrationCard Logo={LogoCodaBox} title="CodaBox" status={!!form.codabox_key}>
            <p className="text-xs text-gray-400">Automatisch bankafschriften importeren en koppelen aan facturen.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li>Meld je aan via je <strong>accountant/boekhouder</strong> of direct op <a href="https://www.codabox.com/nl/contact" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-medium">codabox.com/nl/contact</a></li>
                <li>Configureer automatische CODA-levering per bankrekening in jouw CodaBox-dashboard</li>
                <li>Vraag een <strong>API Token</strong> aan via <a href="https://developer.codabox.com/" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">developer.codabox.com</a> en kopieer hieronder</li>
              </ol>
              <div className="px-4 pb-2 text-[10px] text-gray-500">Meer info: <a href="https://www.codabox.com/nl/" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">codabox.com</a></div>
            </details>
            <div><label className="form-label">API Key</label><input type="password" className="field-input font-mono text-xs" value={form.codabox_key} onChange={u('codabox_key')} placeholder="coda_xxxxxxxxxxxx" data-testid="inst-codabox-key" /></div>
            <button className="btn btn-secondary btn-sm w-full justify-center mt-1" onClick={() => doSync('/codabox/import', 'CodaBox')} data-testid="sync-codabox">
              <ArrowsClockwise size={13} /> Import bankafschriften
            </button>
          </IntegrationCard>

          <IntegrationCard Logo={LogoPonto} title="Ponto (Isabel)" status={!!form.ponto_key}>
            <p className="text-xs text-gray-400">Bankconnectie via Ponto/Isabel voor realtime transactie-import.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li><a href="https://myponto.com/en/register" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-medium">Registreer bij Ponto (Isabel Group)</a></li>
                <li>Connecteer je bank(en) via <a href="https://myponto.com/en/applications" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">myponto.com/en/applications</a></li>
                <li>Maak een <strong>Application</strong> aan en kopieer Client ID + Secret hieronder</li>
              </ol>
              <div className="px-4 pb-2 text-[10px] text-gray-500">Documentatie: <a href="https://documentation.myponto.com/" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">documentation.myponto.com</a></div>
            </details>
            <div><label className="form-label">Client ID / Secret</label><input type="password" className="field-input font-mono text-xs" value={form.ponto_key} onChange={u('ponto_key')} placeholder="ponto_xxxxxxxxxxxx" data-testid="inst-ponto-key" /></div>
            <button className="btn btn-secondary btn-sm w-full justify-center mt-1" onClick={() => doSync('/ponto/sync', 'Ponto')} data-testid="sync-ponto">
              <ArrowsClockwise size={13} /> Sync transacties
            </button>
          </IntegrationCard>

          <IntegrationCard Logo={LogoWoo} title="WooCommerce" status={!!form.woocommerce_key}>
            <p className="text-xs text-gray-400">Synchroniseer bestellingen en klanten vanuit je webshop.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li>In WooCommerce Admin: <strong>Instellingen → Geavanceerd → REST API</strong></li>
                <li>Klik "Sleutel toevoegen" — geef rechten <em>Lezen/Schrijven</em></li>
                <li>Kopieer <strong>Consumer Key</strong> + <strong>Consumer Secret</strong> + vul je shop-URL hieronder in</li>
              </ol>
              <div className="px-4 pb-2 text-[10px] text-gray-500">Documentatie: <a href="https://woocommerce.github.io/woocommerce-rest-api-docs/" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">WooCommerce REST API docs</a></div>
            </details>
            <div><label className="form-label">Shop URL</label><input className="field-input text-xs" value={form.woocommerce_url} onChange={u('woocommerce_url')} placeholder="https://uwshop.be" data-testid="inst-woo-url" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="form-label">Consumer Key</label><input type="password" className="field-input font-mono text-xs" value={form.woocommerce_key} onChange={u('woocommerce_key')} placeholder="ck_xxx" data-testid="inst-woo-key" /></div>
              <div><label className="form-label">Consumer Secret</label><input type="password" className="field-input font-mono text-xs" value={form.woocommerce_secret} onChange={u('woocommerce_secret')} placeholder="cs_xxx" data-testid="inst-woo-secret" /></div>
            </div>
            <button className="btn btn-secondary btn-sm w-full justify-center mt-1" onClick={() => doSync('/woocommerce/sync', 'WooCommerce')} data-testid="sync-woo">
              <ArrowsClockwise size={13} /> Sync bestellingen
            </button>
          </IntegrationCard>

          {/* SMS verbruik & budget widget */}
          {smsStats && (smsStats.totaal.verzonden > 0 || smsStats.totaal.mislukt > 0 || (smsStats.budget?.max_eur || 0) > 0) && (
            <div className="lg:col-span-2 bg-white border border-purple-200" data-testid="sms-stats-widget">
              <div className="px-4 py-2.5 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-purple-700 text-lg">📱</span>
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-900">SMS verbruik — {smsStats.maand.label}</span>
                </div>
                <button onClick={loadSmsStats} className="text-[10px] text-purple-700 hover:underline">↻ Vernieuwen</button>
              </div>

              {/* Budget waarschuwing */}
              {smsStats.budget?.status === 'overschreden' && (
                <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-[11px] font-semibold text-red-800 flex items-center gap-2" data-testid="budget-overschreden">
                  <span>🚫</span>
                  <span>Maandbudget overschreden (€{smsStats.budget.max_eur.toFixed(2)}) — nieuwe SMS worden automatisch geblokkeerd tot volgende maand of totdat u het budget verhoogt.</span>
                </div>
              )}
              {smsStats.budget?.status === 'waarschuwing' && (
                <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-[11px] font-semibold text-yellow-800 flex items-center gap-2" data-testid="budget-waarschuwing">
                  <span>⚠️</span>
                  <span>U nadert uw maandbudget ({smsStats.budget.gebruikt_pct}% van €{smsStats.budget.max_eur.toFixed(2)}). Nog €{smsStats.budget.resterend_eur.toFixed(2)} beschikbaar.</span>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-purple-100">
                <div className="p-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Deze maand</div>
                  <div className="text-2xl font-bold text-purple-700 mt-0.5" data-testid="sms-maand-aantal">{smsStats.maand.aantal}</div>
                  <div className="text-[10px] text-gray-500">SMS verzonden</div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kost deze maand</div>
                  <div className="text-2xl font-bold text-[#002FA7] mt-0.5" data-testid="sms-maand-kost">± €{smsStats.maand.kost_eur.toFixed(2)}</div>
                  <div className="text-[10px] text-gray-500">Geschat (BE-tarief)</div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Totaal verzonden</div>
                  <div className="text-2xl font-bold text-[#008A27] mt-0.5">{smsStats.totaal.verzonden}</div>
                  <div className="text-[10px] text-gray-500">± €{smsStats.totaal.kost_eur.toFixed(2)} totaal</div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Mislukte pogingen</div>
                  <div className={`text-2xl font-bold mt-0.5 ${smsStats.totaal.mislukt > 0 ? 'text-red-600' : 'text-gray-400'}`}>{smsStats.totaal.mislukt}</div>
                  <div className="text-[10px] text-gray-500">{smsStats.totaal.mislukt > 0 ? 'Check credentials' : 'Alles OK'}</div>
                </div>
              </div>

              {/* Budget configuratie + progress */}
              <div className="px-4 py-3 border-t border-purple-100 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Maandbudget</span>
                    {smsStats.budget?.status === 'uit' ? (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-gray-200 text-gray-600">UIT</span>
                    ) : smsStats.budget?.status === 'ok' ? (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-green-100 text-green-800">OK</span>
                    ) : smsStats.budget?.status === 'waarschuwing' ? (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-yellow-100 text-yellow-800">WAARSCHUWING</span>
                    ) : (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-red-100 text-red-800">OVERSCHREDEN</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-gray-500">Max €</span>
                    <input
                      type="number"
                      step="0.50"
                      min="0"
                      className="field-input w-24 font-mono text-xs text-right"
                      value={form.sms_budget_max ?? ''}
                      onChange={u('sms_budget_max')}
                      onBlur={loadSmsStats}
                      placeholder="0 = uit"
                      data-testid="inst-sms-budget-max"
                    />
                    <span className="text-gray-500">/maand</span>
                  </div>
                </div>
                {(smsStats.budget?.max_eur || 0) > 0 && (
                  <>
                    <div className="w-full h-2 bg-gray-100 overflow-hidden" data-testid="budget-bar">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${Math.min(smsStats.budget.gebruikt_pct, 100)}%`,
                          background: smsStats.budget.status === 'overschreden' ? '#DC2626' : smsStats.budget.status === 'waarschuwing' ? '#D97706' : '#059669',
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500">
                      <span>€{smsStats.maand.kost_eur.toFixed(2)} gebruikt</span>
                      <span className="font-bold">{smsStats.budget.gebruikt_pct}%</span>
                      <span>€{smsStats.budget.resterend_eur.toFixed(2)} resterend</span>
                    </div>
                  </>
                )}
                {(!smsStats.budget?.max_eur) && (
                  <p className="text-[10px] text-gray-400 italic">Geen limiet ingesteld — alle SMS worden verzonden. Vul een bedrag in om te voorkomen dat u te veel uitgeeft in drukke maanden.</p>
                )}
              </div>

              {Object.keys(smsStats.maand.per_type || {}).length > 0 && (
                <div className="px-4 py-2 border-t border-purple-100 bg-purple-50/40 text-[11px] text-gray-700 flex items-center gap-3 flex-wrap">
                  <span className="font-bold text-purple-900">Per type:</span>
                  {Object.entries(smsStats.maand.per_type).map(([t, n]) => (
                    <span key={t} className="px-2 py-0.5 bg-white border border-purple-200 font-mono">
                      {t.replace('herinnering_stap1', '1e herinnering').replace('herinnering_stap2', '2e herinnering').replace('herinnering_stap3', 'aanmaning').replace('betaling_ontvangen', 'betaalbevestiging')}: <strong className="text-purple-700">{n}</strong>
                    </span>
                  ))}
                </div>
              )}
              {smsStats.laatste && smsStats.laatste.length > 0 && (
                <details className="border-t border-purple-100 text-[11px]">
                  <summary className="cursor-pointer px-4 py-1.5 text-gray-600 hover:bg-purple-50 select-none">📋 Laatste 10 berichten</summary>
                  <div className="max-h-48 overflow-y-auto divide-y divide-purple-100">
                    {smsStats.laatste.map((m, i) => (
                      <div key={i} className="px-4 py-1.5 flex items-center gap-2 text-[10px]">
                        <span className="font-mono text-gray-500 min-w-[110px]">{(m.tijdstip || '').slice(0, 16).replace('T', ' ')}</span>
                        <span className={`px-1.5 py-0.5 font-bold ${m.status === 'verzonden' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{m.status}</span>
                        <span className="text-gray-700 font-mono">{m.to}</span>
                        <span className="text-gray-500">{m.provider}</span>
                        <span className="text-gray-400 truncate flex-1 italic">{m.body}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Twilio SMS */}
          <IntegrationCard Logo={LogoTwilio} title="Twilio SMS" status={!!form.twilio_sid}>
            <p className="text-xs text-gray-500">Wereldwijd SMS-netwerk met hoogste betrouwbaarheid. ±€0.07/SMS naar BE. Vereist ook verificatie van je afzender-nummer.</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren (3 stappen)</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li><a href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-medium">Registreer een Twilio account</a> (gratis proefversie met ~$15 credits)</li>
                <li>Koop in het <a href="https://console.twilio.com/us1/develop/phone-numbers/manage/search" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Console → Phone Numbers</a> een Belgisch nummer (~€1/maand) of activeer Alphanumeric Sender ID voor BE</li>
                <li>Kopieer <strong>Account SID</strong> + <strong>Auth Token</strong> uit de <a href="https://console.twilio.com/" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Twilio Console</a> en plak hieronder</li>
              </ol>
              <div className="px-4 pb-2 text-[10px] text-gray-500">
                Tarieven: <a href="https://www.twilio.com/en-us/sms/pricing/be" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Twilio SMS pricing BE</a> · Documentatie: <a href="https://www.twilio.com/docs/sms" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">docs.twilio.com</a>
              </div>
            </details>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="form-label">Account SID</label>
                <input type="password" className="field-input font-mono text-xs" value={form.twilio_sid || ''} onChange={u('twilio_sid')} placeholder="AC..." data-testid="inst-twilio-sid" />
              </div>
              <div>
                <label className="form-label">Auth Token</label>
                <input type="password" className="field-input font-mono text-xs" value={form.twilio_token || ''} onChange={u('twilio_token')} placeholder="xxxxxxxxxxxx" data-testid="inst-twilio-token" />
              </div>
            </div>
            <div>
              <label className="form-label">Afzender (nummer of alphanumeric)</label>
              <input className="field-input font-mono text-xs" value={form.twilio_from || ''} onChange={u('twilio_from')} placeholder="+32XXXXXXXXX of EmergentBV" data-testid="inst-twilio-from" />
              <p className="text-[10px] text-gray-400 mt-0.5">Numeriek nummer moet bij Twilio gekocht zijn. Alphanumeric ID (max 11 tekens) werkt alleen eenrichting.</p>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <input type="checkbox" checked={!!form.twilio_actief} onChange={uBool('twilio_actief')} data-testid="inst-twilio-actief" />
                <span className={form.twilio_actief ? 'text-[#008A27]' : 'text-gray-500'}>{form.twilio_actief ? 'Actief' : 'Inactief'}</span>
              </label>
              <a href="https://www.twilio.com/console" target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline">Open Twilio console →</a>
            </div>

            {/* WhatsApp Business uitbreiding (gebruikt zelfde Twilio SID+Token) */}
            <div className="border border-green-200 bg-green-50/50 p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-base">💬</span>
                <span className="text-xs font-bold text-green-900">Twilio WhatsApp Business</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 bg-green-600 text-white">±€0,04/bericht</span>
              </div>
              <p className="text-[11px] text-gray-600">Hergebruikt dezelfde Twilio SID + Token. Klanten lezen WhatsApp 3× sneller dan SMS. Werkt met sandbox-nummer of geapproved Business-nummer.</p>
              <details className="text-[10px] border border-green-200 bg-white">
                <summary className="cursor-pointer px-2 py-1 font-bold text-green-900 hover:bg-green-50 select-none">📖 Hoe activeren (sandbox = direct, production = 1-5 dagen)</summary>
                <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                  <li><strong>Sandbox (test direct)</strong>: Ga naar <a href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Twilio Console → Messaging → Try WhatsApp</a> voor uw join-code. Elke klant moet eerst één keer "join &lt;code&gt;" sturen naar <strong>+1 415 523 8886</strong>.</li>
                  <li><strong>Production</strong>: Registreer een <a href="https://console.twilio.com/us1/develop/sms/whatsapp/senders" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">WhatsApp Sender</a> (vereist Facebook Business verificatie, 1-5 werkdagen). Hierna kunt u klanten zonder opt-in contacteren via goedgekeurde templates.</li>
                  <li>Vul hieronder uw WhatsApp-afzender in (+14155238886 voor sandbox) en activeer.</li>
                </ol>
                <div className="px-4 pb-2 text-[10px] text-gray-500">Pricing: <a href="https://www.twilio.com/en-us/whatsapp/pricing/be" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Twilio WhatsApp pricing BE</a></div>
              </details>
              <div>
                <label className="form-label">WhatsApp afzender</label>
                <input className="field-input font-mono text-xs" value={form.twilio_whatsapp_from || ''} onChange={u('twilio_whatsapp_from')} placeholder="+14155238886 (sandbox) of +32XXXXXXXXX" data-testid="inst-wa-from" />
                <p className="text-[10px] text-gray-400 mt-0.5">Sandbox standaard: +14155238886. Eigen geapproved nummer mogelijk in production.</p>
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-green-200">
                <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                  <input type="checkbox" checked={!!form.twilio_whatsapp_actief} onChange={uBool('twilio_whatsapp_actief')} data-testid="inst-wa-actief" />
                  <span className={form.twilio_whatsapp_actief ? 'text-[#008A27]' : 'text-gray-500'}>{form.twilio_whatsapp_actief ? 'WhatsApp actief' : 'WhatsApp inactief'}</span>
                </label>
                <label className="flex items-center gap-1.5 text-[10px] cursor-pointer">
                  <input type="checkbox" checked={!!form.whatsapp_voorkeur} onChange={uBool('whatsapp_voorkeur')} data-testid="inst-wa-voorkeur" disabled={!form.twilio_whatsapp_actief} />
                  <span className={form.whatsapp_voorkeur ? 'font-bold text-green-800' : 'text-gray-500'}>WhatsApp i.p.v. SMS (met fallback)</span>
                </label>
              </div>
              <button className="btn btn-secondary btn-sm w-full justify-center" onClick={() => doSync('/whatsapp/test', 'WhatsApp test', { askTestNumber: true })} data-testid="sync-wa" disabled={!form.twilio_whatsapp_actief}>
                <ArrowsClockwise size={13} /> Verstuur test-WhatsApp
              </button>
            </div>

            <div className="bg-gray-50 border border-gray-200 p-2.5 text-[11px] text-gray-600 space-y-0.5">
              <div className="font-bold text-gray-700">Wat wordt verzonden? <span className="text-[9px] font-normal text-gray-500">(via {form.whatsapp_voorkeur && form.twilio_whatsapp_actief ? 'WhatsApp → SMS fallback' : 'SMS'})</span></div>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.sms_vervallen_factuur} onChange={uBool('sms_vervallen_factuur')} data-testid="inst-sms-vervallen" /> Bericht bij <strong>vervallen factuur</strong> (cron: elke dag 09:00)</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.sms_herinnering} onChange={uBool('sms_herinnering')} /> Bericht bij <strong>2e herinnering</strong> (handmatig vanaf knop)</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.sms_aanmaning} onChange={uBool('sms_aanmaning')} /> Bericht bij <strong>aanmaning</strong> (handmatig)</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.sms_betaling_ontvangen} onChange={uBool('sms_betaling_ontvangen')} /> Bericht <strong>bevestiging betaling ontvangen</strong></label>
            </div>
            <button className="btn btn-secondary btn-sm w-full justify-center mt-1" onClick={() => doSync('/sms/test', 'Twilio test', { askTestNumber: true })} data-testid="sync-twilio">
              <ArrowsClockwise size={13} /> Verstuur test-SMS
            </button>
          </IntegrationCard>

          {/* MessageBird SMS */}
          <IntegrationCard Logo={LogoMessageBird} title="MessageBird (Bird)" status={!!form.msgbird_key}>
            <p className="text-xs text-gray-500">Europees SMS-netwerk, betrouwbaar in BE/NL. ±€0.05/SMS. Werkt zonder gekocht afzender-nummer (Alphanumeric ID toegestaan in BE).</p>
            <details className="text-[11px] border border-gray-200 bg-gray-50/50">
              <summary className="cursor-pointer px-2 py-1.5 font-bold text-gray-700 hover:bg-gray-100 select-none">📖 Hoe activeren (3 stappen)</summary>
              <ol className="px-4 py-2 list-decimal space-y-1 text-gray-600">
                <li><a href="https://bird.com/try" target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-medium">Registreer bij Bird (voorheen MessageBird)</a> — gratis account met test-credits</li>
                <li>Maak in het <a href="https://dashboard.bird.com/developers/access-keys" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Dashboard → Developers → API Access</a> een <strong>Live Access Key</strong> aan</li>
                <li>Kies als afzender een <strong>Alphanumeric Sender ID</strong> (max 11 karakters, bv. jouw bedrijfsnaam) — geen nummer nodig voor BE</li>
              </ol>
              <div className="px-4 pb-2 text-[10px] text-gray-500">
                Tarieven: <a href="https://bird.com/pricing/messaging" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Bird SMS pricing</a> · Documentatie: <a href="https://developers.bird.com/api/sms" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">developers.bird.com</a>
              </div>
            </details>
            <div>
              <label className="form-label">Access Key (Live)</label>
              <input type="password" className="field-input font-mono text-xs" value={form.msgbird_key || ''} onChange={u('msgbird_key')} placeholder="live_xxxxxxxxxxxx" data-testid="inst-msgbird-key" />
            </div>
            <div>
              <label className="form-label">Afzender (originator)</label>
              <input className="field-input font-mono text-xs" value={form.msgbird_from || ''} onChange={u('msgbird_from')} placeholder="+32XXXXXXXXX of EmergentBV" data-testid="inst-msgbird-from" />
              <p className="text-[10px] text-gray-400 mt-0.5">In BE kan een alphanumeric ID (max 11 karakters) gebruikt worden zonder nummer te kopen.</p>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <input type="checkbox" checked={!!form.msgbird_actief} onChange={uBool('msgbird_actief')} data-testid="inst-msgbird-actief" />
                <span className={form.msgbird_actief ? 'text-[#008A27]' : 'text-gray-500'}>{form.msgbird_actief ? 'Actief' : 'Inactief'}</span>
              </label>
              <a href="https://dashboard.bird.com/" target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline">Open Bird dashboard →</a>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-2.5 text-[11px] text-blue-900">
              <div className="font-bold mb-0.5">💡 Aanbevolen flow: <span className="text-[#002FA7]">Hybride e-mail → SMS</span></div>
              <div>Schakel Twilio OF MessageBird in (niet beide tegelijk). SMS wordt pas verstuurd als e-mail onbeantwoord blijft (≥48u) en factuur vervallen is. Dit houdt kosten laag.</div>
            </div>
          </IntegrationCard>

          {syncMsg && <div className="lg:col-span-2 p-3 bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium" data-testid="sync-msg">{syncMsg}</div>}
        </div>
        </>
      )}

      {/* ── TAB: Databeheer ── */}
      {tab === 'databeheer' && (
        <div>
          {!dbVerified ? (
            <div className="section-card" style={{ maxWidth: 400 }}>
              <div className="section-header"><span className="section-title">Admin verificatie</span></div>
              <div className="p-5">
                <p className="text-xs text-gray-500 mb-3">Deze acties zijn onomkeerbaar. Voer je admin wachtwoord in om door te gaan.</p>
                <div className="flex gap-2">
                  <input type="password" className="field-input flex-1" value={dbPw} onChange={e => setDbPw(e.target.value)}
                    placeholder="Admin wachtwoord" onKeyDown={e => e.key === 'Enter' && verifyPw()} data-testid="databeheer-pw" />
                  <button className="btn btn-primary" onClick={verifyPw} data-testid="databeheer-verify">Verifieer</button>
                </div>
                {dbMsg && <div className="mt-2 text-xs text-red-600">{dbMsg}</div>}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {dbMsg && <div className="p-3 bg-green-50 border border-green-200 text-green-700 text-sm font-medium" data-testid="databeheer-msg">{dbMsg}</div>}

              {/* ⚠️ TEST RESET — wist in één klik alle transactionele data (behoudt klanten, verkopers, instellingen) */}
              <div className="section-card border-2 border-red-200 bg-red-50/30" data-testid="test-reset-card">
                <div className="section-header bg-red-50 border-b border-red-200">
                  <span className="section-title text-red-700"><Warning size={16} weight="fill" className="inline mr-1.5" />Test reset — volledige transactionele wipe</span>
                </div>
                <div className="p-4 space-y-2">
                  <p className="text-xs text-red-900">
                    Wist in één klik <strong>alle documenten, artikels, banktransacties, kassaverkopen en herinneringen</strong>.
                    Klanten, verkopers, instellingen en bedrijfsgegevens blijven <strong>bewaard</strong>.
                    Ideaal om de app leeg te maken vóór een test-ronde.
                  </p>
                  <button
                    className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                    disabled={dbLoading === 'reset_test_data'}
                    onClick={() => {
                      if (!window.confirm(
                        'TEST RESET — ALLES WISSEN\n\n' +
                        'Dit wist ONHERSTELBAAR alle:\n' +
                        `  • ${dbStats?.documenten || 0} documenten (facturen, offertes, bestelbonnen, ...)\n` +
                        `  • ${dbStats?.artikels || 0} artikels\n` +
                        `  • ${dbStats?.bank || 0} banktransacties\n` +
                        `  • ${dbStats?.kassa || 0} kassaverkopen\n` +
                        '  • alle herinneringen en betalingsgeschiedenis\n\n' +
                        'Uw klanten, verkopers, instellingen en bedrijfsgegevens BLIJVEN BEWAARD.\n\n' +
                        'Zeker verdergaan?'
                      )) return;
                      doActie('reset_test_data', 'Volledige test-reset');
                    }}
                    data-testid="db-test-reset"
                  >
                    <Warning size={16} weight="fill" />
                    {dbLoading === 'reset_test_data' ? 'Bezig met wissen...' : 'TEST RESET — Wis alle transactionele data'}
                  </button>
                </div>
              </div>

              {/* Nummering controleren & harmoniseren */}
              <NummeringPanel />

              {/* Producten / Artikels */}
              <div className="section-card">
                <div className="section-header"><span className="section-title"><Article size={16} className="inline mr-1.5" />Producten / Artikels</span>
                  {dbStats && <span className="text-xs text-gray-400">{dbStats.artikels} artikels{dbStats.artikel_fotos > 0 ? ` · ${dbStats.artikel_fotos} foto's` : ''}</span>}
                </div>
                <div className="p-4 space-y-2">
                  <button className="flex items-center gap-2 text-sm text-[#D97706] hover:text-[#B45309] font-medium w-full py-1.5"
                    disabled={dbLoading === 'wis_alle_fotos' || !(dbStats?.artikel_fotos > 0)}
                    onClick={() => {
                      if (!window.confirm(
                        `Wis ALLE ${dbStats?.artikel_fotos || 0} foto's van je artikels?\n\n` +
                        `De artikels zelf blijven bestaan (alleen de foto's worden gewist).\n` +
                        `Je kan daarna nieuwe foto's uploaden of via Preview-sync importeren.\n\n` +
                        `Zeker doorgaan?`
                      )) return;
                      doActie('wis_alle_fotos', `Wis alle ${dbStats?.artikel_fotos || 0} artikel-foto's`);
                    }}
                    data-testid="db-wis-fotos">
                    <Trash size={16} /> {dbLoading === 'wis_alle_fotos' ? 'Bezig...' : 'Wis alle foto\'s van artikels'}
                    {dbStats?.artikel_fotos > 0 && <span className="ml-auto text-xs text-gray-400">{dbStats.artikel_fotos} foto's</span>}
                  </button>
                  <button className="flex items-center gap-2 text-sm text-[#D97706] hover:text-[#B45309] font-medium w-full py-1.5"
                    disabled={dbLoading === 'wis_producten'} onClick={() => doActie('wis_producten', 'Wis alle producten')} data-testid="db-wis-producten">
                    <Trash size={16} /> {dbLoading === 'wis_producten' ? 'Bezig...' : 'Wis alle producten'}
                  </button>
                </div>
              </div>

              {/* Klanten */}
              <div className="section-card">
                <div className="section-header"><span className="section-title"><UsersThree size={16} className="inline mr-1.5" />Klanten</span>
                  {dbStats && <span className="text-xs text-gray-400">{dbStats.klanten} klanten</span>}
                </div>
                <div className="p-4 space-y-2">
                  <button className="flex items-center gap-2 text-sm text-[#002FA7] hover:text-[#001A6E] font-medium w-full py-1.5"
                    disabled={dbLoading === 'samenvoegen_klanten_btw'} onClick={() => doActie('samenvoegen_klanten_btw', 'Klanten samenvoegen op BTW-nummer')} data-testid="db-merge-btw">
                    <UsersThree size={16} /> Klanten samenvoegen op basis van hetzelfde BTW-nummer
                    {dbStats?.dupes_btw > 0 && <span className="ml-auto text-xs text-[#D97706] font-bold">{dbStats.dupes_btw} dubbels</span>}
                  </button>
                  <button className="flex items-center gap-2 text-sm text-[#002FA7] hover:text-[#001A6E] font-medium w-full py-1.5"
                    disabled={dbLoading === 'samenvoegen_klanten_email'} onClick={() => doActie('samenvoegen_klanten_email', 'Klanten samenvoegen op email+naam')} data-testid="db-merge-email">
                    <UsersThree size={16} /> Klanten samenvoegen op basis van hetzelfde emailadres en naam
                    {dbStats?.dupes_email > 0 && <span className="ml-auto text-xs text-[#D97706] font-bold">{dbStats.dupes_email} dubbels</span>}
                  </button>
                  <button className="flex items-center gap-2 text-sm text-[#D97706] hover:text-[#B45309] font-medium w-full py-1.5"
                    disabled={dbLoading === 'wis_klanten_zonder_factuur'} onClick={() => doActie('wis_klanten_zonder_factuur', 'Wis klanten zonder factuur')} data-testid="db-wis-klanten">
                    <Trash size={16} /> Wis alle klanten waar geen factuur voor bestaat
                    {dbStats?.klanten_zonder_factuur > 0 && <span className="ml-auto text-xs text-gray-400">{dbStats.klanten_zonder_factuur} klanten</span>}
                  </button>
                </div>
              </div>

              {/* Documenten */}
              <div className="section-card">
                <div className="section-header"><span className="section-title"><FileText size={16} className="inline mr-1.5" />Documenten</span>
                  {dbStats && <span className="text-xs text-gray-400">{dbStats.documenten} documenten</span>}
                </div>
                <div className="p-4 space-y-2">
                  <button className="flex items-center gap-2 text-sm text-[#D97706] hover:text-[#B45309] font-medium w-full py-1.5"
                    disabled={dbLoading === 'wis_documenten_concept'} onClick={() => doActie('wis_documenten_concept', 'Wis alle concepten')} data-testid="db-wis-concept">
                    <Trash size={16} /> Wis alle documenten in concept/snelle invoer
                    {dbStats?.documenten_concept > 0 && <span className="ml-auto text-xs text-gray-400">{dbStats.documenten_concept} concepten</span>}
                  </button>
                  <button className="flex items-center gap-2 text-sm text-red-600 hover:text-red-800 font-medium w-full py-1.5"
                    disabled={dbLoading === 'wis_alle_documenten'} onClick={() => doActie('wis_alle_documenten', 'Verwijder ALLE documenten')} data-testid="db-wis-docs">
                    <Warning size={16} /> Verwijder alle documenten in de documentenmodule
                  </button>
                </div>
              </div>

              {/* Bankrekeningen */}
              <div className="section-card">
                <div className="section-header"><span className="section-title"><BankIcon size={16} className="inline mr-1.5" />Bankrekeningen & Kassa</span></div>
                <div className="p-4 space-y-2">
                  <button className="flex items-center gap-2 text-sm text-[#D97706] hover:text-[#B45309] font-medium w-full py-1.5"
                    disabled={dbLoading === 'wis_bank_transacties'} onClick={() => doActie('wis_bank_transacties', 'Wis alle banktransacties')} data-testid="db-wis-bank">
                    <Trash size={16} /> Wis alle banktransacties
                    {dbStats?.bank > 0 && <span className="ml-auto text-xs text-gray-400">{dbStats.bank} transacties</span>}
                  </button>
                  <button className="flex items-center gap-2 text-sm text-[#D97706] hover:text-[#B45309] font-medium w-full py-1.5"
                    disabled={dbLoading === 'wis_kassaverkopen'} onClick={() => doActie('wis_kassaverkopen', 'Wis alle kassaverkopen')} data-testid="db-wis-kassa">
                    <Trash size={16} /> Wis alle kassaverkopen
                    {dbStats?.kassa > 0 && <span className="ml-auto text-xs text-gray-400">{dbStats.kassa} verkopen</span>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// ─── Inline padding-inconsistency check + harmonize knop ─────────
import API2 from '@/lib/api';
import { Warning as WarningIcon, ArrowsClockwise as ArrowsClockwiseIcon } from '@phosphor-icons/react';

function PaddingInconsistencyCheck() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const check = async () => {
    try {
      const r = await API2.get('/admin/controleer-nummering');
      setStatus(r.data);
    } catch { /* ignore voor niet-admins */ }
  };
  useEffect(() => { check(); }, []);

  if (!status || status.inconsistent_count === 0) return null;

  const harmoniseer = async () => {
    if (!window.confirm(
      `${status.inconsistent_count} document-nummer(s) wijken af van het huidige padding-formaat.\n\n` +
      'Wilt u deze nu harmoniseren? Alle bestaande IDs worden bijgewerkt en cross-references (bron_id, betalingen, herinneringen) mee gemigreerd.'
    )) return;
    setBusy(true); setMsg('');
    try {
      const { data } = await API2.post('/admin/normaliseer-nummering');
      setMsg(`✓ ${data.hernoemd} document(en) geharmoniseerd.`);
      check();
    } catch (e) {
      setMsg('Fout: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  return (
    <div className="p-3 rounded border-2 border-amber-400 bg-amber-50" data-testid="padding-warning">
      <div className="flex items-start gap-2">
        <WarningIcon size={20} className="text-amber-700 shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1">
          <div className="text-xs font-bold text-amber-800 mb-1">Inconsistente nummering gedetecteerd</div>
          <div className="text-[11px] text-amber-700 mb-2">
            {status.inconsistent_count} document(en) hebben een andere padding dan het huidige formaat ({status.padding} cijfers). Dit kan leiden tot verwarrende sortering in lijsten.
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={harmoniseer}
            disabled={busy}
            data-testid="padding-harmoniseer"
          >
            <ArrowsClockwiseIcon size={12} /> {busy ? 'Bezig…' : `Harmoniseer nu (${status.inconsistent_count})`}
          </button>
          {msg && <div className="text-[10px] text-green-700 mt-2 font-medium">{msg}</div>}
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
// WeergavePanel — lettertype + tekstgrootte voor de hele app
// ════════════════════════════════════════════════════════════════
function WeergavePanel() {
  const [prefs, setPrefs] = useState(() => loadUiPrefs());
  const update = (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    saveUiPrefs(next);
  };
  const reset = () => {
    const def = { fontFamily: 'sans', fontSize: 'md' };
    setPrefs(def);
    saveUiPrefs(def);
  };
  return (
    <div className="space-y-4" data-testid="weergave-panel">
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-5 py-3 border-b bg-gradient-to-r from-purple-50 to-pink-50">
          <h2 className="text-lg font-bold text-gray-900 inline-flex items-center gap-2">
            <Palette size={20} weight="duotone" className="text-purple-600" />
            Weergave-instellingen
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Pas lettertype en tekstgrootte aan voor de hele app. Wijzigingen worden onmiddellijk toegepast en bewaard in je browser.
          </p>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600 mb-2 inline-flex items-center gap-1">
              <TextAa size={14} /> Lettertype
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FONT_FAMILY_PRESETS.map(f => {
                const isActive = prefs.fontFamily === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => update({ fontFamily: f.key })}
                    style={{ fontFamily: f.stack }}
                    className={`p-3 border rounded text-left transition-colors ${isActive ? 'border-[#8B5CF6] bg-purple-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`font-family-${f.key}`}
                  >
                    <div className="text-sm font-semibold mb-0.5">{f.label}</div>
                    <div className="text-xs text-gray-500">De snelle bruine vos springt over de luie hond.</div>
                    {isActive && <div className="text-[10px] text-purple-700 mt-1 font-bold">✓ Actief</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600 mb-2 inline-flex items-center gap-1">
              <TextAa size={14} /> Tekstgrootte
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {FONT_SIZE_PRESETS.map(s => {
                const isActive = prefs.fontSize === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => update({ fontSize: s.key })}
                    className={`p-3 border rounded text-center transition-colors ${isActive ? 'border-[#8B5CF6] bg-purple-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`font-size-${s.key}`}
                  >
                    <div style={{ fontSize: s.scale }} className="font-semibold leading-tight">Aa</div>
                    <div className="text-[10px] text-gray-600 mt-1">{s.label}</div>
                    <div className="text-[10px] text-gray-400">{s.scale}px</div>
                    {isActive && <div className="text-[10px] text-purple-700 mt-1 font-bold">✓</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-4 bg-gray-50 border border-gray-200 rounded">
            <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">Voorbeeld</div>
            <div className="space-y-1">
              <h3 className="text-xl font-bold">BillFac — uw boekhoudsysteem</h3>
              <p className="text-base text-gray-800">
                Dit is een voorbeeld van hoe gewone tekst eruitziet met de gekozen instellingen.
                Knoppen, formulieren en tabellen passen zich automatisch aan.
              </p>
              <p className="text-sm text-gray-600 italic">
                Tip: kies "Extra groot" als je vaak aan de PC werkt — bespaart je ogen.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t">
            <div className="text-xs text-gray-500">
              💾 Voorkeuren worden opgeslagen in deze browser.
            </div>
            <button onClick={reset} className="btn btn-secondary btn-sm">↺ Reset naar standaard</button>
          </div>
        </div>
      </div>

      {/* ── APARTE MAIL-WEERGAVE ── */}
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-5 py-3 border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <h2 className="text-lg font-bold text-gray-900 inline-flex items-center gap-2">
            📧 Mail-weergave (apart instelbaar)
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Een aparte instelling alleen voor de <strong>Mail Hub, factuur-mails-tab en klant-mails-tab</strong>.
            Handig als je mailtjes liefst in groter formaat of een ander lettertype leest.
          </p>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600 mb-2 inline-flex items-center gap-1">
              <TextAa size={14} /> Lettertype voor mails
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FONT_FAMILY_PRESETS.map(f => {
                const isActive = prefs.mailFontFamily === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => update({ mailFontFamily: f.key })}
                    style={{ fontFamily: f.stack }}
                    className={`p-3 border rounded text-left transition-colors ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`mail-font-family-${f.key}`}
                  >
                    <div className="text-sm font-semibold mb-0.5">{f.label}</div>
                    <div className="text-xs text-gray-500">Beste klant, hierbij ontvangt u factuur 2026-001.</div>
                    {isActive && <div className="text-[10px] text-blue-700 mt-1 font-bold">✓ Actief voor mails</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600 mb-2 inline-flex items-center gap-1">
              <TextAa size={14} /> Tekstgrootte voor mails
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {FONT_SIZE_PRESETS.map(s => {
                const isActive = prefs.mailFontSize === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => update({ mailFontSize: s.key })}
                    className={`p-3 border rounded text-center transition-colors ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`mail-font-size-${s.key}`}
                  >
                    <div style={{ fontSize: s.scale }} className="font-semibold leading-tight">Aa</div>
                    <div className="text-[10px] text-gray-600 mt-1">{s.label}</div>
                    <div className="text-[10px] text-gray-400">{s.scale}px</div>
                    {isActive && <div className="text-[10px] text-blue-700 mt-1 font-bold">✓</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mail-content p-4 bg-gray-50 border border-gray-200 rounded">
            <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">Voorbeeld van mail-inhoud</div>
            <div className="space-y-1">
              <div className="text-sm font-semibold">📧 Geachte heer/mevrouw,</div>
              <p className="text-sm text-gray-800">
                Bij deze e-mail vindt u factuur F-2026-00018 als bijlage. Gelieve het openstaand bedrag van € 638,88
                te voldoen binnen 14 dagen op IBAN BE68 5390 0754 7034.
              </p>
              <p className="text-xs text-gray-600 italic">Met vriendelijke groet, Alphaprofiles BV</p>
            </div>
          </div>
          <div className="flex items-center justify-end pt-3 border-t">
            <button
              onClick={() => { update({ mailFontFamily: prefs.fontFamily, mailFontSize: prefs.fontSize }); }}
              className="btn btn-secondary btn-sm"
              title="Synchroniseer met algemene app-instellingen"
            >
              🔄 Volg algemene app-instellingen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

